import { z } from 'zod';

// src/tenant.types.ts
var planSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  monthlyPrice: z.number().nonnegative(),
  annualPrice: z.number().nonnegative(),
  smsCreditsPerMonth: z.number().int().nonnegative(),
  emailCreditsPerMonth: z.number().int().nonnegative(),
  features: z.record(z.boolean()),
  isActive: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var tenantSettingsSchema = z.object({
  timezone: z.string().default("America/New_York"),
  dateFormat: z.string().default("MM/DD/YYYY"),
  timeFormat: z.enum(["12h", "24h"]).default("12h"),
  defaultLanguage: z.enum(["en", "es", "fr"]).default("en"),
  branding: z.object({
    logoUrl: z.string().url().optional(),
    primaryColor: z.string().optional(),
    companyName: z.string().optional()
  }).optional()
});
var ORG_CUSTOM_FIELD_RESERVED = /* @__PURE__ */ new Set([
  "companyName",
  "logo",
  "timezone",
  "dateFormat",
  "bookingLink",
  "firstName",
  "lastName",
  "fullName",
  "email",
  "phone",
  "address",
  "city",
  "state",
  "zipCode",
  "country",
  "fullAddress",
  "name"
]);
var OrgCustomFieldSchema = z.object({
  fieldKey: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, "Must start with a letter; letters/numbers/underscore only").max(64).refine((k) => !ORG_CUSTOM_FIELD_RESERVED.has(k), {
    message: "Reserved field name \u2014 already an organisation field"
  }),
  label: z.string().min(1).max(100),
  value: z.string().max(500).default("")
});
var OrgCustomFieldsSchema = z.array(OrgCustomFieldSchema).max(50).superRefine((arr, ctx) => {
  const seen = /* @__PURE__ */ new Set();
  arr.forEach((f, i) => {
    if (seen.has(f.fieldKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [i, "fieldKey"],
        message: "Duplicate field key"
      });
    }
    seen.add(f.fieldKey);
  });
});
var tenantSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  slug: z.string().min(1).regex(/^[a-z0-9-]+$/),
  planId: z.string().uuid(),
  stripeCustomerId: z.string().optional(),
  settings: tenantSettingsSchema,
  isActive: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
  // OOBE
  setupCompletedAt: z.date().nullable().optional(),
  setupSkippedAt: z.date().nullable().optional(),
  setupStepsCompleted: z.string().nullable().optional(),
  subscriptionPromptTiming: z.enum(["early", "late"]).default("late"),
  // Business identity
  businessId: z.string().nullable().optional(),
  businessType: z.enum(["individual", "business"]).nullable().optional(),
  businessWebsite: z.string().nullable().optional(),
  customFields: OrgCustomFieldsSchema.optional()
});
var createTenantSchema = tenantSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
var updateTenantSchema = createTenantSchema.partial();
var userRoleSchema = z.enum(["SuperAdmin", "UserTenant", "TeamUser"]);
var userSettingsSchema = z.object({
  notifications: z.object({
    email: z.boolean().default(true),
    sms: z.boolean().default(false),
    push: z.boolean().default(true)
  }).optional(),
  preferences: z.object({
    theme: z.enum(["light", "dark", "system"]).default("system"),
    language: z.enum(["en", "es", "fr"]).default("en"),
    defaultView: z.enum(["grid", "list"]).default("list")
  }).optional()
});
var userSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  azureAdUserId: z.string(),
  email: z.string().email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phone: z.string().optional(),
  role: userRoleSchema,
  avatar: z.string().url().optional(),
  settings: userSettingsSchema,
  isActive: z.boolean(),
  lastLoginAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var userWithFullNameSchema = userSchema.extend({
  fullName: z.string()
});
var createUserSchema = userSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  lastLoginAt: true
});
var updateUserSchema = createUserSchema.partial().omit({
  tenantId: true,
  azureAdUserId: true
});
var authUserSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  email: z.string().email(),
  role: userRoleSchema
});
var consentLevelSchema = z.enum(["transactional", "informational", "marketing"]);
var optInSourceSchema = z.enum(["import", "form", "manual", "api", "voice"]);
var tagSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1).max(50),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  createdAt: z.date()
});
var createTagSchema = tagSchema.omit({
  id: true,
  tenantId: true,
  createdAt: true
});
var contactSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  firstName: z.string().min(1),
  lastName: z.string().optional(),
  company: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  country: z.string().optional(),
  timezone: z.string().optional(),
  customFields: z.record(z.unknown()).default({}),
  // Consent Management
  smsOptIn: z.boolean().default(false),
  smsOptInAt: z.date().optional(),
  smsOptInSource: optInSourceSchema.optional(),
  smsOptOutAt: z.date().optional(),
  emailOptIn: z.boolean().default(false),
  emailOptInAt: z.date().optional(),
  emailOptInSource: optInSourceSchema.optional(),
  emailOptOutAt: z.date().optional(),
  voiceOptIn: z.boolean().default(false),
  voiceOptInAt: z.date().optional(),
  consentLevel: consentLevelSchema.default("transactional"),
  tcpaProofUrl: z.string().url().optional(),
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date(),
  // Relations (optional for API responses)
  tags: z.array(tagSchema).optional()
});
var createContactSchema = contactSchema.omit({
  id: true,
  tenantId: true,
  createdAt: true,
  updatedAt: true,
  tags: true
});
var updateContactSchema = createContactSchema.partial();
var contactImportRowSchema = z.object({
  email: z.string().email().optional(),
  phone: z.string().optional(),
  firstName: z.string().min(1),
  lastName: z.string().optional(),
  company: z.string().optional(),
  tags: z.array(z.string()).optional()
});
var contactFilterSchema = z.object({
  search: z.string().optional(),
  tagIds: z.array(z.string().uuid()).optional(),
  smsOptIn: z.boolean().optional(),
  emailOptIn: z.boolean().optional(),
  consentLevel: consentLevelSchema.optional(),
  isActive: z.boolean().optional()
});
var channelConsentSchema = z.object({
  informational: z.boolean().default(false),
  marketing: z.boolean().default(false)
});
var consentFormValuesSchema = z.object({
  sms: channelConsentSchema,
  email: channelConsentSchema,
  voice: channelConsentSchema
});
var createContactWithConsentSchema = createContactSchema.extend({
  consent: consentFormValuesSchema.optional(),
  tags: z.array(z.string().uuid()).optional()
});
var updateContactWithConsentSchema = createContactWithConsentSchema.partial();
var formFieldTypeSchema = z.enum([
  "text",
  "textarea",
  "email",
  "phone",
  "number",
  "date",
  "time",
  "dropdown",
  "radio",
  "checkbox",
  "file",
  "signature",
  "section_header",
  "divider",
  "spacer"
]);
var fieldValidationSchema = z.object({
  required: z.boolean().default(false),
  minLength: z.number().int().optional(),
  maxLength: z.number().int().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  pattern: z.string().optional(),
  customMessage: z.string().optional()
});
var formFieldSchema = z.object({
  id: z.string(),
  type: formFieldTypeSchema,
  label: z.string().min(1),
  placeholder: z.string().optional(),
  helpText: z.string().optional(),
  defaultValue: z.unknown().optional(),
  options: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
  validation: fieldValidationSchema.optional(),
  conditionalLogic: z.object({
    fieldId: z.string(),
    operator: z.enum(["equals", "notEquals", "contains", "notContains"]),
    value: z.unknown()
  }).optional()
});
var formSchemaDefinition = z.object({
  fields: z.array(formFieldSchema),
  layout: z.enum(["single", "two-column"]).default("single")
});
var formSettingsSchema = z.object({
  submitButtonText: z.string().default("Submit"),
  successMessage: z.string().default("Thank you for your submission!"),
  redirectUrl: z.string().url().optional(),
  // Email notifications
  notifyOnSubmission: z.boolean().default(true),
  notificationEmails: z.array(z.string().email()).optional(),
  webhookUrl: z.string().url().optional(),
  // Consent capture
  captureEmailConsent: z.boolean().default(false),
  captureSmsConsent: z.boolean().default(false),
  consentText: z.string().optional(),
  // Submission control
  allowMultipleSubmissions: z.boolean().default(true),
  maxSubmissions: z.number().int().positive().optional(),
  requireAuth: z.boolean().default(false)
});
var formBrandingSchema = z.object({
  logoUrl: z.string().url().nullable().optional(),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default("#3b82f6"),
  logoPosition: z.enum(["left", "center", "right"]).default("center"),
  inputBorderRadius: z.number().int().min(0).max(20).default(8),
  inputBorderColor: z.string().nullable().optional(),
  inputBackgroundColor: z.string().nullable().optional(),
  inputTextColor: z.string().nullable().optional()
});
var formSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().optional(),
  schema: formSchemaDefinition,
  settings: formSettingsSchema,
  branding: formBrandingSchema.optional(),
  qrCodeUrl: z.string().url().optional(),
  publicUrl: z.string(),
  isPublished: z.boolean().default(false),
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date()
});
var formSubmissionSchema = z.object({
  id: z.string().uuid(),
  formId: z.string().uuid(),
  contactId: z.string().uuid().optional(),
  data: z.record(z.unknown()),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
  submittedAt: z.date()
});
var createFormSchema = formSchema.omit({
  id: true,
  tenantId: true,
  publicUrl: true,
  qrCodeUrl: true,
  createdAt: true,
  updatedAt: true
});
var updateFormSchema = createFormSchema.partial();
var documentTypeSchema = z.enum(["template", "generated"]);
var documentStatusSchema = z.enum(["draft", "sent", "signed", "expired", "cancelled"]);
var signatureFieldPositionSchema = z.object({
  page: z.number().int().positive(),
  x: z.number().min(0).max(1),
  // Normalized position 0-1
  y: z.number().min(0).max(1),
  // Normalized position 0-1
  width: z.number().min(0).max(1),
  // Normalized width 0-1
  height: z.number().min(0).max(1)
  // Normalized height 0-1
});
var templateSignerSchema = z.object({
  id: z.string(),
  name: z.string(),
  order: z.number().int().positive()
});
var textFieldSchema = z.object({
  id: z.string(),
  variableName: z.string(),
  position: signatureFieldPositionSchema,
  signerId: z.string().optional()
  // Associate with specific signer for multi-signer documents
});
var templateContentSchema = z.object({
  html: z.string().optional(),
  // For rich text templates
  variables: z.array(z.string()),
  signatureFields: z.array(
    z.object({
      id: z.string(),
      signerId: z.string().optional(),
      // Associate with specific signer for multi-signer documents
      label: z.string(),
      required: z.boolean(),
      position: signatureFieldPositionSchema.optional()
    })
  ),
  // PDF-specific fields
  pdfPageCount: z.number().int().positive().optional(),
  // Number of pages in PDF
  textFields: z.array(textFieldSchema).optional(),
  // Variable placeholders on PDF
  signers: z.array(templateSignerSchema).optional()
  // Signers for multi-signer documents
});
var documentSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1),
  type: documentTypeSchema,
  templateContent: templateContentSchema.optional(),
  fileUrl: z.string().url().optional(),
  fileSize: z.number().int().optional(),
  originalName: z.string().optional(),
  pageCount: z.number().int().positive().optional(),
  status: documentStatusSchema.default("draft"),
  expiresAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var createDocumentSchema = documentSchema.omit({
  id: true,
  tenantId: true,
  createdAt: true,
  updatedAt: true
});
var updateDocumentSchema = createDocumentSchema.partial();
var signatureStatusSchema = z.enum([
  "pending",
  "sent",
  "viewed",
  "signed",
  "declined",
  "expired"
]);
var signatureDataSchema = z.object({
  type: z.enum(["draw", "type", "upload"]),
  data: z.string(),
  // Base64 image or typed name
  fontFamily: z.string().optional()
  // For typed signatures
});
var signatureSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  documentId: z.string().uuid(),
  contactId: z.string().uuid(),
  status: signatureStatusSchema.default("pending"),
  signedAt: z.date().optional(),
  signatureData: signatureDataSchema.optional(),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
  // Audit trail
  sentAt: z.date().optional(),
  viewedAt: z.date().optional(),
  remindersSent: z.number().int().default(0),
  lastReminderAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var createSignatureRequestSchema = z.object({
  documentId: z.string().uuid(),
  contactIds: z.array(z.string().uuid()).min(1),
  deliveryMethod: z.enum(["sms", "email", "both"]),
  expiresAt: z.date().optional(),
  message: z.string().optional()
});
var signDocumentSchema = z.object({
  signatureData: signatureDataSchema,
  ipAddress: z.string().optional(),
  userAgent: z.string().optional()
});
var signatureWithRelationsSchema = signatureSchema.extend({
  document: z.object({
    id: z.string().uuid(),
    name: z.string()
  }),
  contact: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string().optional(),
    email: z.string().email().optional()
  })
});
var signerMappingSchema = z.object({
  signerId: z.string(),
  // Template signer ID (e.g., "signer_1")
  signerRole: z.string(),
  // Human-readable role name
  contactId: z.string().uuid(),
  signingOrder: z.number().int().positive()
});
var signatureInstanceStatusSchema = z.enum([
  "pending",
  "in_progress",
  "paused",
  "completed",
  "expired",
  "cancelled"
]);
var signatureInstanceSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  documentId: z.string().uuid(),
  campaignId: z.string().uuid().optional(),
  signerMap: z.record(z.string(), z.string().uuid()),
  // signerId -> contactId
  currentSignerId: z.string().optional(),
  status: signatureInstanceStatusSchema,
  signedPdfUrl: z.string().url().optional(),
  completedAt: z.date().optional(),
  expiresAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var signerMappingInstanceSchema = z.object({
  instanceId: z.string(),
  // Client-side temporary ID
  mappings: z.array(signerMappingSchema)
});
var multiSignerStatusSchema = signatureStatusSchema.or(z.enum(["waiting"]));
var signerContextSchema = z.object({
  role: z.string(),
  order: z.number().int().positive(),
  totalSigners: z.number().int().positive()
});
var messageTypeSchema = z.enum(["transactional", "informational", "marketing"]);
var sendTypeSchema = z.enum(["message", "document", "form", "voice"]);
var deliveryMethodSchema = z.enum(["sms", "email", "sms_email", "voice"]);
var sendIntentSchema = z.enum(["esign", "form", "message"]);
var deliveryChannelSchema = z.enum(["sms", "email", "voice"]);
var deliveryConfigSchema = z.object({
  sms: z.boolean().default(false),
  email: z.boolean().default(true),
  voice: z.boolean().default(false)
});
var campaignTypeSchema = deliveryMethodSchema;
var campaignStatusSchema = z.enum([
  "draft",
  "scheduled",
  "sending",
  "paused",
  "completed",
  "cancelled",
  "failed"
]);
var recipientStatusSchema = z.enum([
  "pending",
  "sent",
  "delivered",
  "failed",
  "opened",
  "clicked",
  "bounced",
  "unsubscribed"
]);
var conversationStatusSchema = z.enum(["active", "waiting", "resolved", "archived"]);
var prioritySchema = z.enum(["low", "normal", "high", "urgent"]);
var channelSchema = z.enum(["sms", "email"]);
var messageDirectionSchema = z.enum(["inbound", "outbound"]);
var messageStatusSchema = z.enum(["pending", "sent", "delivered", "failed", "read"]);
var complianceCheckSchema = z.object({
  score: z.number().int().min(0).max(100),
  passed: z.boolean(),
  risks: z.array(
    z.object({
      type: z.string(),
      severity: z.enum(["low", "medium", "high"]),
      description: z.string(),
      suggestion: z.string().optional()
    })
  ),
  checkedAt: z.date()
});
var recipientSelectionSchema = z.object({
  type: z.enum(["manual", "tags", "all"]),
  contactIds: z.array(z.string().uuid()).default([]),
  tagIds: z.array(z.string().uuid()).default([]),
  excludeContactIds: z.array(z.string().uuid()).default([])
});
var recipientScheduleOverrideSchema = z.object({
  contactId: z.string().uuid(),
  channel: z.enum(["sms", "email", "voice"]),
  scheduledAt: z.coerce.date()
});
var scheduleOptionsSchema = z.object({
  type: z.enum(["immediate", "scheduled", "recurring"]),
  scheduledAt: z.coerce.date().optional(),
  timezone: z.string().default("America/New_York"),
  recurringRule: z.string().optional()
  // iCal RRULE format
});
var costEstimateSchema = z.object({
  recipientCount: z.number().int().min(0),
  smsCount: z.number().int().min(0),
  emailCount: z.number().int().min(0),
  voiceCount: z.number().int().min(0),
  estimatedCost: z.object({
    sms: z.number().min(0),
    email: z.number().min(0),
    voice: z.number().min(0),
    total: z.number().min(0)
  }),
  creditsAvailable: z.object({
    sms: z.number().int().min(0),
    email: z.number().int().min(0),
    voice: z.number().int().min(0)
  }),
  isAffordable: z.boolean()
});
var complianceCheckRequestSchema = z.object({
  content: z.string().min(1),
  messageType: messageTypeSchema,
  recipientCount: z.number().int().min(1),
  deliveryMethod: deliveryMethodSchema
});
var campaignSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  createdById: z.string().uuid(),
  name: z.string().min(1),
  // Send configuration
  sendType: sendTypeSchema,
  deliveryMethod: deliveryMethodSchema,
  messageType: messageTypeSchema,
  status: campaignStatusSchema.default("draft"),
  // Content - text content
  messageContent: z.string().optional(),
  smsContent: z.string().optional(),
  emailSubject: z.string().optional(),
  emailContent: z.string().optional(),
  // Content - references
  documentId: z.string().uuid().optional(),
  formId: z.string().uuid().optional(),
  voiceScriptId: z.string().uuid().optional(),
  // Scheduling
  scheduledAt: z.coerce.date().optional(),
  timezone: z.string().default("America/New_York"),
  isRecurring: z.boolean().default(false),
  recurringRule: z.string().optional(),
  // iCal RRULE format
  // AI Compliance
  complianceScore: z.number().int().optional(),
  complianceNotes: z.string().optional(),
  // JSON string
  complianceCheckedAt: z.date().optional(),
  // Stats
  totalRecipients: z.number().int().default(0),
  sentCount: z.number().int().default(0),
  deliveredCount: z.number().int().default(0),
  failedCount: z.number().int().default(0),
  openedCount: z.number().int().default(0),
  clickedCount: z.number().int().default(0),
  startedAt: z.date().optional(),
  completedAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date()
});
var conversationSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  contactId: z.string().uuid(),
  assignedToId: z.string().uuid().optional(),
  channel: channelSchema,
  status: conversationStatusSchema.default("active"),
  priority: prioritySchema.default("normal"),
  lastMessageAt: z.date().optional(),
  unreadCount: z.number().int().default(0),
  createdAt: z.date(),
  updatedAt: z.date()
});
var messageSchema = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  direction: messageDirectionSchema,
  channel: channelSchema,
  content: z.string(),
  // Email specific
  subject: z.string().optional(),
  htmlContent: z.string().optional(),
  attachments: z.array(z.object({ name: z.string(), url: z.string().url() })).optional(),
  // Status
  status: messageStatusSchema.default("pending"),
  externalId: z.string().optional(),
  failureReason: z.string().optional(),
  sentAt: z.date().optional(),
  deliveredAt: z.date().optional(),
  readAt: z.date().optional(),
  createdAt: z.date()
});
var createCampaignSchema = z.object({
  name: z.string().min(1).max(200),
  sendType: sendTypeSchema,
  deliveryMethod: deliveryMethodSchema,
  messageType: messageTypeSchema,
  // Content fields (varies by sendType)
  messageContent: z.string().optional(),
  documentId: z.string().uuid().optional(),
  formId: z.string().uuid().optional(),
  voiceScriptId: z.string().uuid().optional(),
  smsContent: z.string().max(1600).optional(),
  emailSubject: z.string().max(200).optional(),
  emailContent: z.string().optional(),
  // Recipients
  recipientSelection: recipientSelectionSchema,
  // Schedule
  schedule: scheduleOptionsSchema,
  recipientScheduleOverrides: z.array(recipientScheduleOverrideSchema).optional(),
  // Tags
  tagIds: z.array(z.string().uuid()).optional(),
  // Multi-signer support (for document templates with 2+ signers)
  signerMappings: z.array(signerMappingInstanceSchema).optional()
});
var updateCampaignSchema = createCampaignSchema.partial().extend({
  id: z.string().uuid()
});
var sendMessageSchema = z.object({
  contactIds: z.array(z.string().uuid()).min(1),
  channel: channelSchema,
  messageType: messageTypeSchema,
  content: z.string().min(1),
  subject: z.string().optional(),
  // For email
  htmlContent: z.string().optional(),
  // For email
  scheduledAt: z.coerce.date().optional()
});
var conversationListItemSchema = z.object({
  id: z.string().uuid(),
  channel: channelSchema,
  status: conversationStatusSchema,
  priority: prioritySchema,
  unreadCount: z.number().int().min(0),
  lastMessageAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Contact info (denormalized for list display)
  contact: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    avatar: z.string().nullable().optional()
  }),
  // Assigned user info
  assignedTo: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string(),
    avatar: z.string().nullable().optional()
  }).nullable(),
  // Last message preview
  lastMessage: z.object({
    id: z.string().uuid(),
    content: z.string(),
    direction: messageDirectionSchema,
    createdAt: z.string().datetime()
  }).nullable()
});
var conversationMessageSchema = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  direction: messageDirectionSchema,
  channel: channelSchema,
  content: z.string(),
  // Email specific
  subject: z.string().nullable(),
  htmlContent: z.string().nullable(),
  attachments: z.array(
    z.object({
      name: z.string(),
      url: z.string(),
      size: z.number().optional(),
      type: z.string().optional()
    })
  ).nullable(),
  messageId: z.string().nullable().optional(),
  inReplyTo: z.string().nullable().optional(),
  references: z.string().nullable().optional(),
  providerThreadId: z.string().nullable().optional(),
  // Status
  status: messageStatusSchema,
  externalId: z.string().nullable(),
  failureReason: z.string().nullable(),
  // Timestamps
  sentAt: z.string().datetime().nullable(),
  deliveredAt: z.string().datetime().nullable(),
  readAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime()
});
var conversationDetailSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  channel: channelSchema,
  status: conversationStatusSchema,
  priority: prioritySchema,
  unreadCount: z.number().int().min(0),
  lastMessageAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Full contact info
  contact: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    company: z.string().nullable(),
    smsOptIn: z.boolean(),
    emailOptIn: z.boolean(),
    tags: z.array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        color: z.string()
      })
    )
  }),
  // Assigned user info
  assignedTo: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    avatar: z.string().nullable().optional()
  }).nullable(),
  // Messages in this conversation
  messages: z.array(conversationMessageSchema)
});
var listConversationsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  channel: channelSchema.optional(),
  status: conversationStatusSchema.optional(),
  assignedToId: z.string().uuid().optional(),
  search: z.string().optional(),
  unreadOnly: z.coerce.boolean().optional()
});
var conversationsListResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    conversations: z.array(conversationListItemSchema),
    pagination: z.object({
      page: z.number().int(),
      limit: z.number().int(),
      totalItems: z.number().int(),
      totalPages: z.number().int(),
      hasMore: z.boolean()
    })
  })
});
var conversationDetailResponseSchema = z.object({
  success: z.boolean(),
  data: conversationDetailSchema
});
var sendReplyRequestSchema = z.object({
  content: z.string().min(1).max(1e4),
  // For email replies
  subject: z.string().max(200).optional(),
  htmlContent: z.string().optional(),
  attachments: z.array(
    z.object({
      name: z.string(),
      url: z.string().optional(),
      size: z.number().optional(),
      type: z.string().optional(),
      contentBase64: z.string().optional()
    })
  ).optional(),
  inReplyTo: z.string().optional(),
  references: z.string().optional(),
  providerThreadId: z.string().optional()
});
var sendReplyResponseSchema = z.object({
  success: z.boolean(),
  data: conversationMessageSchema
});
var updateConversationRequestSchema = z.object({
  status: conversationStatusSchema.optional(),
  priority: prioritySchema.optional(),
  assignedToId: z.string().uuid().nullable().optional()
});
var updateConversationResponseSchema = z.object({
  success: z.boolean(),
  data: conversationListItemSchema
});
var conversationActionResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    id: z.string().uuid(),
    status: conversationStatusSchema,
    message: z.string()
  })
});
var unreadCountResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    totalUnread: z.number().int().min(0),
    byChannel: z.object({
      sms: z.number().int().min(0),
      email: z.number().int().min(0)
    })
  })
});
var voiceCallTypeSchema = z.enum(["outbound", "inbound", "three_way"]);
var voiceCallStatusSchema = z.enum(["pending", "in_progress", "completed", "failed"]);
var voiceConsentTypeSchema = z.enum(["sms", "email", "voice", "all"]);
var voiceCallSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  contactId: z.string().uuid(),
  type: voiceCallTypeSchema,
  status: voiceCallStatusSchema.default("pending"),
  // UltraVox integration
  ultravoxCallId: z.string().optional(),
  scriptContent: z.string(),
  // Recording
  recordingUrl: z.string().url().optional(),
  transcription: z.string().optional(),
  duration: z.number().int().optional(),
  // seconds
  // Consent outcome
  consentCaptured: z.boolean().default(false),
  consentType: voiceConsentTypeSchema.optional(),
  startedAt: z.date().optional(),
  endedAt: z.date().optional(),
  createdAt: z.date()
});
var voiceScriptCallTypeSchema = z.enum(["outbound_ai", "inbound_hotline", "three_way_merge"]);
var voiceScriptSettingsSchema = z.object({
  maxDuration: z.number().min(1).max(60).default(10),
  // minutes
  retryAttempts: z.number().min(0).max(5).default(2),
  retryInterval: z.number().min(1).max(72).default(24),
  // hours
  preferredCallTime: z.enum(["morning", "afternoon", "evening"]).optional(),
  timezone: z.string().default("America/New_York"),
  successWebhookUrl: z.string().url().optional(),
  failureWebhookUrl: z.string().url().optional()
});
var voiceScriptSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(3).max(100),
  description: z.string().max(500).optional(),
  callType: voiceScriptCallTypeSchema,
  content: z.string().min(50).max(5e3),
  settings: voiceScriptSettingsSchema,
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date()
});
var voiceScriptWithTagsSchema = voiceScriptSchema.extend({
  tags: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    color: z.string()
  }))
});
var voiceScriptUsageStatsSchema = z.object({
  totalCalls: z.number().int().min(0),
  successfulCalls: z.number().int().min(0),
  failedCalls: z.number().int().min(0),
  averageDuration: z.number().min(0),
  // seconds
  successRate: z.number().min(0).max(100)
  // percentage
});
var createVoiceScriptSchema = z.object({
  name: z.string().min(3).max(100),
  description: z.string().max(500).optional(),
  callType: voiceScriptCallTypeSchema,
  content: z.string().min(50).max(5e3),
  settings: voiceScriptSettingsSchema.optional(),
  tags: z.array(z.string().uuid()).optional()
});
var updateVoiceScriptSchema = createVoiceScriptSchema.partial();
var previewVoiceScriptSchema = z.object({
  sampleData: z.record(z.string(), z.string())
  // Variable name -> sample value
});
var initiateVoiceCallSchema = z.object({
  contactId: z.string().uuid(),
  type: voiceCallTypeSchema,
  scriptContent: z.string().min(1)
});
var voiceCallResultSchema = z.object({
  callId: z.string(),
  status: voiceCallStatusSchema,
  duration: z.number().int().optional(),
  recordingUrl: z.string().url().optional(),
  transcription: z.string().optional(),
  consentCaptured: z.boolean(),
  consentType: voiceConsentTypeSchema.optional()
});
var ultravoxStatusSchema = z.enum([
  "queued",
  "ringing",
  "in_progress",
  "completed",
  "failed",
  "no_answer"
]);
var ultravoxWebhookPayloadSchema = z.object({
  callId: z.string(),
  status: ultravoxStatusSchema,
  duration: z.number().int().optional(),
  recordingUrl: z.string().url().optional(),
  transcription: z.string().optional(),
  consentCaptured: z.boolean().optional(),
  consentResponse: z.enum(["yes", "no"]).nullable().optional(),
  endedAt: z.string().optional(),
  // ISO timestamp
  failureReason: z.string().optional()
});
function mapUltravoxStatus(ultravoxStatus) {
  switch (ultravoxStatus) {
    case "queued":
      return "pending";
    case "ringing":
    case "in_progress":
      return "in_progress";
    case "completed":
      return "completed";
    case "failed":
    case "no_answer":
      return "failed";
    default:
      return "failed";
  }
}
var dateRangeSchema = z.object({
  start: z.date(),
  end: z.date()
});
var aggregationPeriodSchema = z.enum(["hour", "day", "week", "month", "year"]);
var dashboardStatsSchema = z.object({
  totalContacts: z.number().int(),
  activeContacts: z.number().int(),
  totalSends: z.number().int(),
  deliveryRate: z.number(),
  // percentage
  responseRate: z.number(),
  // percentage
  // By type
  messagesSent: z.number().int(),
  documentsSent: z.number().int(),
  formsSent: z.number().int(),
  voiceCallsMade: z.number().int(),
  // Signatures
  pendingSignatures: z.number().int(),
  completedSignatures: z.number().int(),
  signatureCompletionRate: z.number(),
  // percentage
  // Forms
  formSubmissions: z.number().int(),
  // Period comparison
  periodChange: z.object({
    contacts: z.number(),
    // percentage change
    sends: z.number(),
    deliveryRate: z.number()
  })
});
var timeSeriesPointSchema = z.object({
  timestamp: z.date(),
  value: z.number()
});
var chartDataSchema = z.object({
  labels: z.array(z.string()),
  datasets: z.array(
    z.object({
      label: z.string(),
      data: z.array(z.number()),
      color: z.string().optional()
    })
  )
});
var activitySummarySchema = z.object({
  date: z.date(),
  sends: z.object({
    messages: z.number().int(),
    documents: z.number().int(),
    forms: z.number().int(),
    voice: z.number().int()
  }),
  deliveries: z.object({
    sms: z.number().int(),
    email: z.number().int()
  }),
  responses: z.object({
    signed: z.number().int(),
    submitted: z.number().int(),
    replied: z.number().int()
  })
});
var analyticsQuerySchema = z.object({
  dateRange: dateRangeSchema,
  aggregation: aggregationPeriodSchema.optional(),
  sendTypes: z.array(sendTypeSchema).optional(),
  tagIds: z.array(z.string().uuid()).optional()
});
var exportFormatSchema = z.enum(["csv", "pdf", "xlsx"]);
var reportRequestSchema = z.object({
  type: z.enum(["activity", "delivery", "signatures", "forms", "voice"]),
  dateRange: dateRangeSchema,
  format: exportFormatSchema,
  includeDetails: z.boolean().default(false)
});
var activityStatusSchema = z.enum([
  "draft",
  "scheduled",
  "sending",
  "sent",
  "delivered",
  "completed",
  "paused",
  "cancelled",
  "failed",
  // Document-specific statuses
  "awaiting_signature",
  "signed",
  // Form-specific statuses
  "awaiting_submission",
  "submitted",
  // Voice-specific statuses
  "calling",
  "answered",
  "no_answer"
]);
var activityTabSchema = z.enum(["activity", "inbox", "drafts"]);
var activityStatsSchema = z.object({
  sent: z.number().int().min(0).default(0),
  delivered: z.number().int().min(0).default(0),
  opened: z.number().int().min(0).default(0),
  failed: z.number().int().min(0).default(0),
  pending: z.number().int().min(0).default(0)
});
var activityTagSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  color: z.string()
});
var activityItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  sendType: sendTypeSchema,
  deliveryMethod: deliveryMethodSchema,
  status: activityStatusSchema,
  // Recipients
  recipientCount: z.number().int().min(0),
  // Stats (computed)
  stats: activityStatsSchema,
  // Progress (for bulk sends)
  progress: z.number().min(0).max(100).optional(),
  // Dates
  createdAt: z.string(),
  scheduledAt: z.string().optional(),
  sentAt: z.string().optional(),
  completedAt: z.string().optional(),
  // Tags
  tags: z.array(activityTagSchema),
  // Content preview
  preview: z.string().optional(),
  // Created by
  createdBy: z.object({
    id: z.string().uuid(),
    name: z.string()
  }).optional()
});
var activityQueryParamsSchema = z.object({
  // Pagination
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  // Search
  search: z.string().optional(),
  // Filters
  sendType: z.string().optional(),
  // Comma-separated: message,document,form,voice
  deliveryMethod: z.string().optional(),
  // Comma-separated: sms,email,voice
  status: z.string().optional(),
  // Comma-separated status values
  tagIds: z.string().optional(),
  // Comma-separated tag IDs
  // Date filters
  startDate: z.string().optional(),
  // ISO date string
  endDate: z.string().optional(),
  // ISO date string
  dateField: z.enum(["created", "scheduled", "sent"]).default("created"),
  // Sorting
  sortBy: z.enum(["name", "createdAt", "scheduledAt", "status", "recipientCount"]).default("createdAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc"),
  // Tab filter
  tab: activityTabSchema.default("activity")
});
var activityPaginationSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
  hasMore: z.boolean()
});
var activityListResponseSchema = z.object({
  data: z.array(activityItemSchema),
  pagination: activityPaginationSchema,
  filters: z.object({
    appliedFilters: z.number().int()
  })
});
var STATUS_BADGE_CONFIG = {
  draft: { label: "Draft", color: "gray", icon: "circle" },
  scheduled: { label: "Scheduled", color: "blue", icon: "clock" },
  sending: { label: "Sending", color: "yellow", icon: "loader", pulse: true },
  sent: { label: "Sent", color: "green", icon: "check" },
  delivered: { label: "Delivered", color: "green", icon: "check-double" },
  completed: { label: "Completed", color: "green", icon: "check" },
  paused: { label: "Paused", color: "orange", icon: "pause" },
  cancelled: { label: "Cancelled", color: "red", icon: "x" },
  failed: { label: "Failed", color: "red", icon: "alert" },
  awaiting_signature: { label: "Awaiting Signature", color: "yellow", icon: "pen" },
  signed: { label: "Signed", color: "green", icon: "pen" },
  awaiting_submission: { label: "Awaiting Submission", color: "yellow", icon: "clipboard" },
  submitted: { label: "Submitted", color: "green", icon: "clipboard" },
  calling: { label: "Calling", color: "yellow", icon: "loader", pulse: true },
  answered: { label: "Answered", color: "green", icon: "check" },
  no_answer: { label: "No Answer", color: "orange", icon: "x" }
};
var SEND_TYPE_CONFIG = {
  message: { label: "Message", color: "text-blue-600", bgColor: "bg-blue-100" },
  document: { label: "Document", color: "text-purple-600", bgColor: "bg-purple-100" },
  form: { label: "Form", color: "text-orange-600", bgColor: "bg-orange-100" },
  voice: { label: "Voice", color: "text-green-600", bgColor: "bg-green-100" }
};
var DELIVERY_METHOD_CONFIG = {
  sms: { label: "SMS", shortLabel: "SMS", color: "bg-blue-100 text-blue-700" },
  email: { label: "Email", shortLabel: "Email", color: "bg-indigo-100 text-indigo-700" },
  sms_email: { label: "SMS + Email", shortLabel: "SMS+Email", color: "bg-violet-100 text-violet-700" },
  voice: { label: "Voice", shortLabel: "Voice", color: "bg-green-100 text-green-700" }
};
var timelineEventTypeSchema = z.enum([
  "created",
  "scheduled",
  "started",
  "sent",
  "delivered",
  "opened",
  "clicked",
  "failed",
  "completed",
  "paused",
  "resumed",
  "cancelled",
  "reminder_sent",
  "signed",
  "submitted"
]);
var timelineEventSchema = z.object({
  id: z.string().uuid(),
  type: timelineEventTypeSchema,
  timestamp: z.string(),
  // ISO date string
  description: z.string(),
  metadata: z.record(z.unknown()).optional(),
  // Optional recipient info for recipient-specific events
  recipientId: z.string().uuid().optional(),
  recipientName: z.string().optional()
});
var timelineResponseSchema = z.object({
  events: z.array(timelineEventSchema),
  pagination: activityPaginationSchema
});
var recipientWithContactSchema = z.object({
  id: z.string().uuid(),
  campaignId: z.string().uuid(),
  contactId: z.string().uuid(),
  status: z.enum(["pending", "sent", "delivered", "failed", "opened", "clicked", "bounced", "unsubscribed"]),
  channel: z.enum(["sms", "email", "voice"]),
  // Delivery timestamps
  sentAt: z.string().optional(),
  deliveredAt: z.string().optional(),
  openedAt: z.string().optional(),
  clickedAt: z.string().optional(),
  failedAt: z.string().optional(),
  failureReason: z.string().optional(),
  // External provider reference
  externalId: z.string().optional(),
  // Contact details (joined from Contact table)
  contact: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string().optional(),
    email: z.string().email().optional(),
    phone: z.string().optional(),
    company: z.string().optional(),
    // Consent status
    smsOptIn: z.boolean().optional(),
    emailOptIn: z.boolean().optional(),
    voiceOptIn: z.boolean().optional(),
    consentLevel: z.string().optional()
  }),
  // PRE routing decision (null when no decision was logged — e.g. draft campaigns)
  preDecision: z.enum(["ALLOW", "ALLOW_WITH_CONDITIONS", "BLOCK"]).nullable().optional(),
  preEligibility: z.enum(["ELIGIBLE", "NOT_ELIGIBLE"]).nullable().optional(),
  preConditionCount: z.number().int().nullable().optional(),
  preRiskFlagCount: z.number().int().nullable().optional(),
  // Actual reason codes parsed from the PRE output JSON. Used by the recipient
  // table to render human-readable reason chips next to the Decision badge
  // (labels live in PRE_*_LABELS in pre.types). All optional / capped at the
  // most informative codes server-side — the UI just renders what it gets.
  preRemediationCodes: z.array(z.string()).optional(),
  preConditionCodes: z.array(z.string()).optional(),
  preRiskFlagCodes: z.array(z.string()).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional()
});
var recipientsListResponseSchema = z.object({
  data: z.array(recipientWithContactSchema),
  pagination: activityPaginationSchema
});
var campaignDetailSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  // Send configuration
  sendType: sendTypeSchema,
  deliveryMethod: deliveryMethodSchema,
  messageType: z.enum(["transactional", "informational", "marketing"]),
  status: z.enum(["draft", "scheduled", "sending", "paused", "completed", "cancelled", "failed"]),
  // Content - text content
  messageContent: z.string().optional(),
  smsContent: z.string().optional(),
  emailSubject: z.string().optional(),
  emailContent: z.string().optional(),
  // Content - references (IDs)
  documentId: z.string().uuid().optional(),
  formId: z.string().uuid().optional(),
  voiceScriptId: z.string().uuid().optional(),
  // Related entities (populated)
  document: z.object({
    id: z.string().uuid(),
    name: z.string(),
    fileName: z.string(),
    fileUrl: z.string(),
    signedFileUrl: z.string().optional(),
    pageCount: z.number().int().optional()
  }).optional(),
  form: z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().optional()
  }).optional(),
  voiceScript: z.object({
    id: z.string().uuid(),
    name: z.string(),
    scriptContent: z.string(),
    voiceType: z.string().optional()
  }).optional(),
  // Scheduling
  scheduledAt: z.string().optional(),
  timezone: z.string(),
  isRecurring: z.boolean(),
  recurringRule: z.string().optional(),
  // AI Compliance
  complianceScore: z.number().int().optional(),
  complianceNotes: z.string().optional(),
  complianceCheckedAt: z.string().optional(),
  // Stats
  totalRecipients: z.number().int(),
  sentCount: z.number().int(),
  deliveredCount: z.number().int(),
  failedCount: z.number().int(),
  openedCount: z.number().int(),
  clickedCount: z.number().int(),
  // Progress percentage (calculated)
  progress: z.number().min(0).max(100).optional(),
  // Timestamps
  startedAt: z.string().optional(),
  completedAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Created by user
  createdBy: z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string().optional(),
    email: z.string().email()
  }),
  // Tags
  tags: z.array(activityTagSchema)
});
var campaignDetailResponseSchema = z.object({
  success: z.boolean(),
  data: campaignDetailSchema
});
var sendReminderRequestSchema = z.object({
  recipientIds: z.array(z.string().uuid()).optional()
  // If empty, send to all pending
});
var sendReminderResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    remindersSent: z.number().int(),
    recipientIds: z.array(z.string().uuid())
  })
});
var paginationSchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
  sortBy: z.string().optional(),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var paginatedResponseSchema = (itemSchema) => z.object({
  items: z.array(itemSchema),
  meta: z.object({
    page: z.number().int(),
    limit: z.number().int(),
    total: z.number().int(),
    totalPages: z.number().int(),
    hasNextPage: z.boolean(),
    hasPrevPage: z.boolean()
  })
});
var apiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
  timestamp: z.string().datetime(),
  path: z.string().optional(),
  requestId: z.string().optional()
});
var apiSuccessSchema = (dataSchema) => z.object({
  success: z.literal(true),
  data: dataSchema,
  meta: z.record(z.unknown()).optional()
});
var apiErrorResponseSchema = z.object({
  success: z.literal(false),
  error: apiErrorSchema
});
var webhookEventTypeSchema = z.enum([
  "send.completed",
  "document.signed",
  "document.viewed",
  "document.expired",
  "form.submitted",
  "voice.completed",
  "voice.consent_captured",
  "contact.created",
  "contact.updated",
  "consent.captured",
  "consent.revoked",
  "message.received",
  "message.delivered",
  "message.failed"
]);
var webhookEventSchema = z.object({
  id: z.string().uuid(),
  type: webhookEventTypeSchema,
  tenantId: z.string().uuid(),
  timestamp: z.string().datetime(),
  data: z.record(z.unknown())
});
var webhookSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  url: z.string().url(),
  events: z.array(webhookEventTypeSchema),
  secret: z.string(),
  isActive: z.boolean().default(true),
  lastTriggeredAt: z.date().optional(),
  failureCount: z.number().int().default(0),
  createdAt: z.date(),
  updatedAt: z.date()
});
var createWebhookSchema = z.object({
  url: z.string().url(),
  events: z.array(webhookEventTypeSchema).min(1)
});
var auditActionSchema = z.enum([
  "create",
  "update",
  "delete",
  "send",
  "sign",
  "view",
  "login",
  "logout",
  "export",
  "import"
]);
var auditLogSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  userId: z.string().uuid().optional(),
  action: auditActionSchema,
  entityType: z.string(),
  entityId: z.string(),
  changes: z.record(z.unknown()).optional(),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
  createdAt: z.date()
});
var optOutChannelSchema = z.enum(["sms", "email", "voice"]);
var optOutActionSchema = z.enum(["opt_out", "opt_in"]);
var optOutSourceSchema = z.enum([
  "keyword",
  "link",
  "manual",
  "form",
  "import",
  "api",
  "voice",
  "confirmation_sent",
  // double opt-in: confirmation message dispatched
  "confirmation_expired",
  // double opt-in: token expired without action
  "confirmation_link",
  // double opt-in: contact clicked confirmation link
  "auto_jurisdiction_rule"
  // informational consent auto-applied by PRE rule
]);
var SMS_OPT_OUT_KEYWORDS = ["STOP", "UNSUBSCRIBE", "CANCEL", "END", "QUIT"];
var SMS_OPT_IN_KEYWORDS = ["START", "SUBSCRIBE", "YES"];
var optOutEventSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  contactId: z.string().uuid(),
  channel: optOutChannelSchema,
  action: optOutActionSchema,
  source: optOutSourceSchema,
  metadata: z.object({
    keyword: z.string().optional(),
    ipAddress: z.string().optional(),
    userAgent: z.string().optional(),
    userId: z.string().uuid().optional(),
    // For manual changes
    reason: z.string().optional(),
    evidenceUrl: z.string().optional(),
    // For manual opt-in with uploaded proof
    evidenceFileName: z.string().optional(),
    writtenNote: z.string().optional(),
    formId: z.string().optional(),
    // For form-sourced events
    messageId: z.string().optional()
    // For link-sourced events
  }).optional(),
  createdAt: z.date()
});
var unsubscribeTokenSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  contactId: z.string().uuid(),
  token: z.string(),
  messageId: z.string().optional(),
  usedAt: z.date().optional(),
  expiresAt: z.date(),
  createdAt: z.date()
});
var sinchInboundSmsSchema = z.object({
  type: z.literal("mo_text").optional(),
  id: z.string().optional(),
  from: z.string(),
  // Sender phone number
  to: z.string(),
  // Recipient phone number (our number)
  body: z.string(),
  // SMS content
  received_at: z.string().optional(),
  operator_id: z.string().optional(),
  client_reference: z.string().optional()
});
var clicksendInboundSmsMessageSchema = z.object({
  body: z.string(),
  from: z.string(),
  to: z.string(),
  message_id: z.string().optional(),
  date: z.number().optional(),
  list_id: z.number().optional()
});
var clicksendInboundSmsSchema = z.object({
  messages: z.array(clicksendInboundSmsMessageSchema)
});
var manualOptChangeRequestSchema = z.object({
  channel: optOutChannelSchema,
  optIn: z.union([z.boolean(), z.string().transform((v) => v === "true")]),
  // supports JSON and form-data strings
  reason: z.string().optional(),
  writtenNote: z.string().optional(),
  evidenceFileName: z.string().optional()
});
var optOutHistoryQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  channel: optOutChannelSchema.optional(),
  action: optOutActionSchema.optional()
});
var optOutHistoryResponseSchema = z.object({
  events: z.array(optOutEventSchema),
  pagination: z.object({
    page: z.number(),
    limit: z.number(),
    totalItems: z.number(),
    totalPages: z.number(),
    hasMore: z.boolean()
  })
});
var adminOptOutEventsQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(50),
  channel: optOutChannelSchema.optional(),
  action: optOutActionSchema.optional(),
  source: optOutSourceSchema.optional(),
  contactId: z.string().uuid().optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional()
});
var unsubscribeInfoSchema = z.object({
  contactEmail: z.string().optional(),
  contactFirstName: z.string().optional(),
  tenantName: z.string(),
  alreadyUnsubscribed: z.boolean()
});
var OPT_OUT_CONFIRMATION_SMS = "You have been unsubscribed. Reply START to re-subscribe.";
var OPT_IN_CONFIRMATION_SMS = (tenantName) => `You have been re-subscribed to messages from ${tenantName}.`;
var tokenStatusSchema = z.enum([
  "valid",
  "expired",
  "already_completed",
  "not_found",
  "invalid"
]);
var tokenValidationResponseSchema = z.object({
  status: tokenStatusSchema,
  message: z.string().optional(),
  expiresAt: z.date().optional()
});
var publicDocumentSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  fileUrl: z.string().url(),
  pageCount: z.number().int().positive().optional(),
  signatureFields: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      required: z.boolean(),
      position: z.object({
        page: z.number().int().positive(),
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
        width: z.number().min(0).max(1),
        height: z.number().min(0).max(1)
      }).optional(),
      fieldType: z.string().optional(),
      signerId: z.string().optional(),
      placeholder: z.string().optional(),
      helpText: z.string().optional(),
      defaultValue: z.union([z.string(), z.boolean()]).optional(),
      options: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
      validation: z.object({
        minLength: z.number().int().nonnegative().optional(),
        maxLength: z.number().int().positive().optional(),
        pattern: z.string().optional(),
        customMessage: z.string().optional()
      }).optional()
    })
  ),
  senderName: z.string().optional(),
  message: z.string().optional(),
  expiresAt: z.date().optional()
});
var publicFormSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().optional(),
  schema: formSchemaDefinition,
  settings: formSettingsSchema,
  branding: z.object({
    logoUrl: z.string().url().optional(),
    primaryColor: z.string().optional(),
    companyName: z.string().optional()
  }).optional()
});
var publicSignDocumentSchema = z.object({
  signatureId: z.string().uuid(),
  signatureData: signatureDataSchema,
  signedFields: z.array(
    z.object({
      fieldId: z.string(),
      signatureData: signatureDataSchema
    })
  )
});
var declineDocumentSchema = z.object({
  signatureId: z.string().uuid(),
  reason: z.string().min(1).max(500)
});
var publicFormSubmissionSchema = z.object({
  formId: z.string().uuid(),
  data: z.record(z.unknown()),
  token: z.string().optional()
  // For private form links
});
var formSubmissionResponseSchema = z.object({
  success: z.boolean(),
  submissionId: z.string().uuid().optional(),
  message: z.string(),
  redirectUrl: z.string().url().optional()
});
var signatureCompletionResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  downloadUrl: z.string().url().optional()
});
var publicFileUploadResponseSchema = z.object({
  success: z.boolean(),
  fileUrl: z.string().url(),
  fileName: z.string(),
  fileSize: z.number().int()
});
var viewModeSchema = z.enum(["table", "grid", "kanban"]);
var contactGroupSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).nullable().optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  icon: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createContactGroupSchema = z.object({
  name: z.string().min(1).max(100),
  industry: z.string().max(100).optional(),
  description: z.string().max(500).optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default("#3b82f6"),
  icon: z.string().optional(),
  stages: z.array(z.object({
    name: z.string().min(1).max(100),
    description: z.string().max(255).optional(),
    exitCriteria: z.array(z.string().min(1).max(255)).max(20).optional(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    isDefault: z.boolean().optional(),
    isFinal: z.boolean().optional()
  })).optional()
});
var updateContactGroupSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).nullable().optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  icon: z.string().nullable().optional()
});
var contactGroupStageSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  name: z.string().min(1).max(100),
  description: z.string().max(255).nullable().optional(),
  exitCriteria: z.array(z.string()).nullable().optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  sortOrder: z.number().int().default(0),
  isDefault: z.boolean().default(false),
  isFinal: z.boolean().default(false),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createContactGroupStageSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(255).optional(),
  exitCriteria: z.array(z.string().min(1).max(255)).max(20).optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default("#64748b"),
  isDefault: z.boolean().optional(),
  isFinal: z.boolean().optional()
});
var updateContactGroupStageSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(255).nullable().optional(),
  exitCriteria: z.array(z.string().min(1).max(255)).max(20).nullable().optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  isDefault: z.boolean().optional(),
  isFinal: z.boolean().optional()
});
var contactGroupMembershipSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  stageId: z.string().uuid(),
  contactId: z.string().uuid(),
  addedAt: z.coerce.date(),
  movedAt: z.coerce.date(),
  notes: z.string().max(1e3).nullable().optional(),
  sortOrder: z.number().int().default(0)
});
var moveContactSchema = z.object({
  toStageId: z.string().uuid(),
  sortOrder: z.number().int().optional(),
  notes: z.string().max(1e3).nullable().optional()
});
var kanbanContactSchema = z.object({
  membershipId: z.string().uuid(),
  contactId: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  company: z.string().nullable().optional(),
  tags: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    color: z.string()
  })).optional(),
  addedAt: z.coerce.date(),
  movedAt: z.coerce.date(),
  daysInStage: z.number().int(),
  notes: z.string().nullable().optional(),
  sortOrder: z.number().int()
});
var kanbanStageSchema = contactGroupStageSchema.extend({
  contactCount: z.number().int(),
  contacts: z.array(kanbanContactSchema)
});
var kanbanGroupSchema = contactGroupSchema.extend({
  stages: z.array(kanbanStageSchema),
  totalContacts: z.number().int()
});
var groupListItemSchema = contactGroupSchema.extend({
  stageCount: z.number().int(),
  contactCount: z.number().int(),
  stages: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable().optional(),
    exitCriteria: z.array(z.string()).nullable().optional(),
    color: z.string(),
    isDefault: z.boolean(),
    isFinal: z.boolean()
  }))
});
var contactGroupInfoSchema = z.object({
  membershipId: z.string().uuid(),
  group: z.object({
    id: z.string().uuid(),
    name: z.string(),
    color: z.string(),
    icon: z.string().nullable().optional()
  }),
  stage: z.object({
    id: z.string().uuid(),
    name: z.string(),
    color: z.string(),
    description: z.string().nullable().optional(),
    isDefault: z.boolean(),
    isFinal: z.boolean()
  }),
  addedAt: z.coerce.date(),
  movedAt: z.coerce.date(),
  daysInStage: z.number().int(),
  notes: z.string().nullable().optional()
});
var customFieldTypeSchema = z.enum([
  "text",
  "textarea",
  "dropdown",
  "radio",
  "checkboxes",
  "date",
  "image",
  "section_header",
  "spacer"
]);
var LAYOUT_FIELD_TYPES = ["section_header", "spacer"];
function isLayoutFieldType(fieldType) {
  return LAYOUT_FIELD_TYPES.includes(fieldType);
}
var customFieldDefinitionSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1).max(100),
  fieldKey: z.string().min(1).max(50).regex(/^[a-z][a-z0-9_]*$/, {
    message: "Field key must start with a letter and contain only lowercase letters, numbers, and underscores"
  }),
  fieldType: customFieldTypeSchema,
  isRequired: z.boolean().default(false),
  isVisible: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  options: z.array(z.string()).nullable(),
  placeholder: z.string().max(255).nullable(),
  helpText: z.string().max(500).nullable(),
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date()
});
var createCustomFieldSchema = z.object({
  name: z.string().min(1, "Name is required").max(100, "Name is too long"),
  fieldKey: z.string().min(1, "Field key is required").max(50).regex(/^[a-z][a-z0-9_]*$/, {
    message: "Field key must start with a letter and contain only lowercase letters, numbers, and underscores"
  }),
  fieldType: customFieldTypeSchema,
  isRequired: z.boolean().default(false),
  isVisible: z.boolean().default(true),
  options: z.array(z.string().min(1)).optional(),
  placeholder: z.string().max(255).optional(),
  helpText: z.string().max(500).optional()
}).refine(
  (data) => {
    if (["dropdown", "radio", "checkboxes"].includes(data.fieldType)) {
      return data.options && data.options.length > 0;
    }
    return true;
  },
  {
    message: "Options are required for dropdown, radio, and checkboxes field types",
    path: ["options"]
  }
);
var updateCustomFieldSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  fieldType: customFieldTypeSchema.optional(),
  isRequired: z.boolean().optional(),
  isVisible: z.boolean().optional(),
  options: z.array(z.string().min(1)).nullable().optional(),
  placeholder: z.string().max(255).nullable().optional(),
  helpText: z.string().max(500).nullable().optional()
}).refine(
  (data) => {
    if (data.fieldType && ["dropdown", "radio", "checkboxes"].includes(data.fieldType)) {
      return data.options && data.options.length > 0;
    }
    return true;
  },
  {
    message: "Options are required for dropdown, radio, and checkboxes field types",
    path: ["options"]
  }
);
var reorderCustomFieldsSchema = z.object({
  fieldIds: z.array(z.string().uuid()).min(1)
});
var customFieldValueSchema = z.union([
  z.string(),
  z.array(z.string()),
  z.null()
]);
var customFieldValuesSchema = z.record(z.string(), customFieldValueSchema);
var customFieldDefinitionListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(customFieldDefinitionSchema.omit({ tenantId: true }))
});
var customFieldFilterSchema = z.object({
  isActive: z.boolean().optional(),
  isVisible: z.boolean().optional(),
  fieldType: customFieldTypeSchema.optional()
});
var unifiedInboxChannelSchema = z.enum(["email", "sms", "voice", "form"]);
var unifiedInboxFilterSchema = z.enum([
  "all",
  "campaigns",
  "documents",
  "forms",
  "sms",
  "email",
  "voice",
  "opted_out",
  "opted_in"
]);
var unifiedInboxItemTypeSchema = z.enum(["campaign", "conversation", "form_submission", "form", "signature_request", "consent"]);
var unifiedInboxContactSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string().optional(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  company: z.string().optional()
});
var formResponseFieldSchema = z.object({
  fieldId: z.string(),
  label: z.string(),
  type: z.string(),
  value: z.union([z.string(), z.array(z.string()), z.boolean(), z.null()])
});
var unifiedInboxMessageSchema = z.object({
  id: z.string().uuid(),
  direction: z.enum(["inbound", "outbound"]),
  channel: channelSchema.optional(),
  content: z.string(),
  subject: z.string().optional(),
  // For email
  htmlContent: z.string().optional(),
  // For email
  status: messageStatusSchema,
  sentAt: z.string().optional(),
  deliveredAt: z.string().optional(),
  readAt: z.string().optional(),
  createdAt: z.string().optional(),
  failureReason: z.string().optional(),
  // System-generated notice (e.g. shared-number ambiguity warning).
  // Renders as a banner, not a contact reply bubble.
  isSystemNotice: z.boolean().optional()
});
var voiceScriptReferenceSchema = z.object({
  id: z.string().uuid(),
  name: z.string()
});
var preSummarySchema = z.object({
  blockedCount: z.number().int().min(0),
  conditionalCount: z.number().int().min(0),
  allowedCount: z.number().int().min(0),
  // Up to 3 REMED_* codes seen most often across blocked recipients.
  topBlockCodes: z.array(z.string()).default([]),
  // Up to 3 COND_* codes seen most often across conditioned recipients.
  topConditionCodes: z.array(z.string()).default([])
});
var unifiedInboxItemSchema = z.object({
  id: z.string().min(1),
  // Composite ID format: type:uuid (e.g. campaign:abc-123)
  type: unifiedInboxItemTypeSchema,
  // Display fields
  name: z.string(),
  // Contact name or campaign name
  subject: z.string(),
  // Subject line or campaign name/preview
  description: z.string().optional(),
  // Message preview or status description
  // Type classification (optional for non-campaign types like consent)
  sendType: sendTypeSchema.optional(),
  deliveryMethod: deliveryMethodSchema.optional(),
  // Status
  status: activityStatusSchema,
  isRead: z.boolean().default(true),
  isArchived: z.boolean().default(false),
  // Timestamps
  createdAt: z.string(),
  updatedAt: z.string(),
  lastActivityAt: z.string(),
  scheduledAt: z.string().optional(),
  timezone: z.string().optional(),
  // Counts (optional based on type)
  unreadCount: z.number().int().min(0).optional(),
  recipientCount: z.number().int().min(0).optional(),
  formSubmissionCount: z.number().int().min(0).optional(),
  // Stats (for campaigns)
  stats: activityStatsSchema.optional(),
  // Policy Rule Engine outcome summary (campaigns only).
  // Drives the inbox row's Blocked / Conditional badge and the detail banner.
  preSummary: preSummarySchema.optional(),
  // Contact info
  contact: unifiedInboxContactSchema.optional(),
  // Original entity references
  campaignId: z.string().uuid().optional(),
  conversationId: z.string().uuid().optional(),
  contactId: z.string().uuid().optional(),
  documentId: z.string().uuid().optional(),
  formId: z.string().uuid().optional(),
  signatureRequestId: z.string().uuid().optional(),
  // Tags
  tags: z.array(activityTagSchema).default([])
});
var unifiedInboxItemDetailSchema = unifiedInboxItemSchema.extend({
  // Form-specific fields
  formName: z.string().optional(),
  submittedAt: z.string().optional(),
  responses: z.array(formResponseFieldSchema).optional(),
  consentCaptured: z.boolean().optional(),
  signature: z.string().optional(),
  // Base64 or URL
  signedAt: z.string().optional(),
  // Document-specific fields
  documentName: z.string().optional(),
  documentUrl: z.string().optional(),
  // URL to view original document
  signedDocumentUrl: z.string().optional(),
  // URL to download signed document with signatures
  pageCount: z.number().int().optional(),
  sentAt: z.string().optional(),
  viewedAt: z.string().optional(),
  expiresAt: z.string().optional(),
  reminderCount: z.number().int().optional(),
  signatureData: z.object({
    type: z.enum(["draw", "type", "upload"]),
    image: z.string()
  }).optional(),
  recipientEmail: z.string().optional(),
  recipientPhone: z.string().optional(),
  // Recipients array for multi-recipient signature requests (campaigns)
  signatureRecipients: z.array(z.object({
    id: z.string().uuid(),
    contactId: z.string().uuid(),
    contactName: z.string(),
    contactEmail: z.string().optional(),
    contactPhone: z.string().optional(),
    signatureStatus: z.enum(["pending", "sent", "viewed", "signed", "failed", "expired"]),
    sentAt: z.string().optional(),
    viewedAt: z.string().optional(),
    signedAt: z.string().optional(),
    signedPdfUrl: z.string().optional(),
    remindersSent: z.number().optional(),
    lastReminderAt: z.string().optional()
  })).optional(),
  // Message/Conversation-specific fields
  messages: z.array(unifiedInboxMessageSchema).optional(),
  canReply: z.boolean().optional(),
  // True for conversations, false for campaigns
  conversationStatus: conversationStatusSchema.optional(),
  conversationPriority: prioritySchema.optional(),
  isOptedIn: z.boolean().optional(),
  // Contact's opt-in status for the channel
  // Voice-specific fields
  callType: z.enum(["outbound_ai", "inbound_hotline", "three_way_merge"]).optional(),
  duration: z.number().int().optional(),
  // in seconds
  calledAt: z.string().optional(),
  recordingUrl: z.string().optional(),
  transcript: z.string().optional(),
  voiceScript: voiceScriptReferenceSchema.optional(),
  // Timeline events
  timeline: z.array(timelineEventSchema).optional(),
  // Consent event-specific fields
  consentEventId: z.string().uuid().optional(),
  consentChannel: z.enum(["sms", "email", "voice"]).optional(),
  consentAction: z.enum(["opt_in", "opt_out", "confirmation_sent", "confirmation_expired"]).optional(),
  consentSource: z.string().optional(),
  // 'keyword' | 'link' | 'manual' | 'form' | 'import' | 'confirmation_link'
  consentMetadata: z.record(z.unknown()).optional(),
  // Raw metadata JSON
  consentEvidenceUrl: z.string().optional(),
  // URL to evidence file
  consentEvidenceFileName: z.string().optional(),
  consentWrittenNote: z.string().optional(),
  consentPendingExpiry: z.string().optional()
  // ISO date when confirmation token expires
});
var unifiedInboxCountsSchema = z.object({
  all: z.number().int().min(0),
  campaigns: z.number().int().min(0),
  documents: z.number().int().min(0),
  forms: z.number().int().min(0),
  sms: z.number().int().min(0),
  email: z.number().int().min(0),
  voice: z.number().int().min(0),
  optedOut: z.number().int().min(0),
  optedIn: z.number().int().min(0),
  // Legacy support
  recentlyOptedOut: z.number().int().min(0).optional()
});
var unifiedInboxFiltersSchema = z.object({
  channel: unifiedInboxFilterSchema.default("all"),
  search: z.string().default(""),
  dateRange: z.enum(["all", "today", "7days", "30days"]).default("all"),
  status: z.array(z.string()).default([]),
  isRead: z.boolean().nullable().default(null)
});
var unifiedInboxQueryParamsSchema = z.object({
  // Pagination
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  // Search
  search: z.string().optional(),
  // Filters
  channel: unifiedInboxFilterSchema.optional(),
  status: z.string().optional(),
  // Comma-separated
  isRead: z.boolean().optional(),
  // Date filters
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  dateRange: z.enum(["all", "today", "7days", "30days"]).optional(),
  // Sorting
  sortBy: z.enum(["lastActivityAt", "createdAt", "name"]).default("lastActivityAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var unifiedInboxPaginationSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
  hasMore: z.boolean()
});
var unifiedInboxListResponseSchema = z.object({
  data: z.array(unifiedInboxItemSchema),
  pagination: unifiedInboxPaginationSchema,
  counts: unifiedInboxCountsSchema
});
var unifiedInboxDetailResponseSchema = z.object({
  success: z.boolean(),
  data: unifiedInboxItemDetailSchema
});
var bulkActionRequestSchema = z.object({
  ids: z.array(z.string().uuid()).min(1)
});
var bulkActionResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    processedCount: z.number().int(),
    failedCount: z.number().int(),
    failedIds: z.array(z.string().uuid()).optional()
  })
});
var exportRequestSchema = z.object({
  ids: z.array(z.string().uuid()).min(1),
  format: z.enum(["csv", "json", "pdf"]).default("csv")
});
var exportResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    downloadUrl: z.string().url(),
    expiresAt: z.string(),
    recordCount: z.number().int()
  })
});
var auditEntityTypeSchema = z.enum([
  "contact",
  "campaign",
  "document",
  "form",
  "voice_script"
]);
var changeActionSchema = z.enum([
  "create",
  "update",
  "delete",
  "consent_change",
  "opt_in",
  "opt_out",
  "send",
  "sign",
  "view",
  // Emitted by the Policy Rule Engine when a campaign send produces blocked
  // or conditioned recipients — drives the in-app bell notification.
  "policy_review"
]);
var changeDetailSchema = z.object({
  field: z.string(),
  fieldLabel: z.string(),
  from: z.unknown().optional(),
  to: z.unknown().optional()
});
var changeUserSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string()
});
var changeEntrySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  userId: z.string().uuid().optional(),
  user: changeUserSchema.optional(),
  action: changeActionSchema,
  entityType: auditEntityTypeSchema,
  entityId: z.string().uuid(),
  entityName: z.string().optional(),
  changes: z.array(changeDetailSchema),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
  createdAt: z.coerce.date(),
  isRead: z.boolean().default(false)
});
var changeListResponseSchema = z.object({
  changes: z.array(changeEntrySchema),
  pagination: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    totalPages: z.number()
  }),
  unreadCount: z.number()
});
var changesQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  entityType: auditEntityTypeSchema.optional(),
  entityId: z.string().uuid().optional(),
  action: changeActionSchema.optional(),
  unreadOnly: z.coerce.boolean().optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional()
});
var markAsReadRequestSchema = z.object({
  ids: z.union([z.array(z.string().uuid()), z.literal("all")]),
  entityType: auditEntityTypeSchema.optional(),
  entityId: z.string().uuid().optional()
});
var CONTACT_FIELD_LABELS = {
  firstName: "First Name",
  lastName: "Last Name",
  email: "Email Address",
  phone: "Phone Number",
  company: "Company",
  address: "Address",
  city: "City",
  state: "State",
  zipCode: "ZIP Code",
  country: "Country",
  timezone: "Timezone",
  customFields: "Custom Fields",
  smsOptIn: "SMS Consent",
  smsOptInAt: "SMS Consent Date",
  smsOptInSource: "SMS Consent Source",
  smsOptOutAt: "SMS Opt-Out Date",
  emailOptIn: "Email Consent",
  emailOptInAt: "Email Consent Date",
  emailOptInSource: "Email Consent Source",
  emailOptOutAt: "Email Opt-Out Date",
  voiceOptIn: "Voice Consent",
  voiceOptInAt: "Voice Consent Date",
  consentLevel: "Consent Level",
  tcpaProofUrl: "TCPA Proof URL",
  isActive: "Active Status"
};
var CAMPAIGN_FIELD_LABELS = {
  name: "Campaign Name",
  sendType: "Send Type",
  deliveryMethod: "Delivery Method",
  messageType: "Message Type",
  status: "Status",
  messageContent: "Message Content",
  smsContent: "SMS Content",
  emailSubject: "Email Subject",
  emailContent: "Email Content",
  scheduledAt: "Scheduled Date",
  isRecurring: "Recurring"
};
var DOCUMENT_FIELD_LABELS = {
  name: "Document Name",
  type: "Document Type",
  status: "Status",
  templateContent: "Template Content",
  expiresAt: "Expiration Date",
  isActive: "Active Status"
};
var FORM_FIELD_LABELS = {
  name: "Form Name",
  description: "Description",
  schema: "Form Schema",
  settings: "Settings",
  isPublished: "Published",
  isActive: "Active Status"
};
var VOICE_SCRIPT_FIELD_LABELS = {
  name: "Script Name",
  description: "Description",
  callType: "Call Type",
  content: "Script Content",
  settings: "Settings",
  isActive: "Active Status"
};
var FIELD_LABELS = {
  contact: CONTACT_FIELD_LABELS,
  campaign: CAMPAIGN_FIELD_LABELS,
  document: DOCUMENT_FIELD_LABELS,
  form: FORM_FIELD_LABELS,
  voice_script: VOICE_SCRIPT_FIELD_LABELS
};
function getFieldLabel(entityType, field) {
  return FIELD_LABELS[entityType]?.[field] || formatFieldName(field);
}
function formatFieldName(field) {
  return field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()).trim();
}
var ENTITY_ICONS = {
  contact: "User",
  campaign: "Send",
  document: "FileText",
  form: "ClipboardList",
  voice_script: "Phone"
};
var ACTION_COLORS = {
  create: "green",
  update: "blue",
  delete: "red",
  consent_change: "purple",
  opt_in: "green",
  opt_out: "red",
  send: "blue",
  sign: "green",
  view: "gray",
  policy_review: "amber"
};
var ACTION_LABELS = {
  create: "Created",
  update: "Updated",
  delete: "Deleted",
  consent_change: "Consent Changed",
  opt_in: "Opted In",
  opt_out: "Opted Out",
  send: "Sent",
  sign: "Signed",
  view: "Viewed",
  policy_review: "Policy Review Required"
};
var superAdminRoleSchema = z.enum([
  "SuperAdmin",
  "SupportAdmin",
  "BillingAdmin",
  "ComplianceAdmin",
  "ReadOnlyAdmin"
]);
var ROLE_PERMISSIONS = {
  SuperAdmin: ["*"],
  SupportAdmin: ["support:*", "accounts:read", "billing:read"],
  BillingAdmin: ["billing:*", "accounts:read", "support:read", "plans:*"],
  ComplianceAdmin: ["audit:*", "compliance:*", "accounts:read"],
  ReadOnlyAdmin: ["*:read"]
};
var superAdminUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  role: superAdminRoleSchema,
  avatar: z.string().url().optional().nullable(),
  phone: z.string().optional().nullable(),
  isActive: z.boolean(),
  failedLoginAttempts: z.number().int().default(0),
  lockedUntil: z.coerce.date().optional().nullable(),
  lastLoginAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var superAdminAuthUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  role: superAdminRoleSchema
});
var createSuperAdminUserSchema = superAdminUserSchema.omit({
  id: true,
  failedLoginAttempts: true,
  lockedUntil: true,
  lastLoginAt: true,
  createdAt: true,
  updatedAt: true
}).extend({
  password: z.string().min(8)
});
var updateSuperAdminUserSchema = superAdminUserSchema.partial().omit({
  id: true,
  failedLoginAttempts: true,
  lockedUntil: true,
  lastLoginAt: true,
  createdAt: true,
  updatedAt: true
});
var superAdminAuditActionSchema = z.enum([
  "login",
  "logout",
  "create",
  "update",
  "delete",
  "suspend",
  "reactivate",
  "cancel",
  "impersonate",
  "password_reset",
  "toggle_maintenance",
  "clear_cache"
]);
var superAdminEntityTypeSchema = z.enum([
  "tenant",
  "user",
  "plan",
  "template",
  "super_admin_user",
  "feature_flag",
  "platform"
]);
var superAdminAuditLogSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid().optional().nullable(),
  user: superAdminUserSchema.pick({ id: true, email: true, firstName: true, lastName: true }).optional().nullable(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().optional().nullable(),
  entityName: z.string().optional().nullable(),
  details: z.record(z.unknown()).optional().nullable(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  createdAt: z.coerce.date()
});
var platformServiceSchema = z.enum([
  "api-gateway",
  "database",
  "redis",
  "sinch",
  "mailgun",
  "ultravox",
  "stripe"
]);
var healthStatusSchema = z.enum(["healthy", "degraded", "down"]);
var platformHealthCheckSchema = z.object({
  id: z.string().uuid(),
  service: z.string(),
  status: healthStatusSchema,
  latencyMs: z.number().optional().nullable(),
  errorMessage: z.string().optional().nullable(),
  metadata: z.record(z.unknown()).optional().nullable(),
  checkedAt: z.coerce.date()
});
var superAdminLoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1)
});
var superAdminLoginResponseSchema = z.object({
  user: superAdminAuthUserSchema,
  accessToken: z.string(),
  refreshToken: z.string()
});
var superAdminRefreshRequestSchema = z.object({
  refreshToken: z.string()
});
var superAdminRefreshResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string()
});
var tenantStatusSchema = z.enum(["active", "trial", "suspended", "cancelled"]);
var billingStatusSchema = z.enum([
  "current",
  "past_due",
  "grace_period",
  "suspended",
  "pending_card"
]);
var tenantSourceSchema = z.enum(["signup", "referral", "sales", "import"]);
var templateVisibilitySchema = z.enum(["all", "plans", "tenants", "none"]);
var lifecycleEventTypeSchema = z.enum([
  "created",
  "activated",
  "suspended",
  "reactivated",
  "cancelled",
  "plan_changed",
  "billing_status_changed"
]);
var tenantLifecycleEventSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  event: lifecycleEventTypeSchema,
  previousStatus: z.string().optional().nullable(),
  newStatus: z.string().optional().nullable(),
  reason: z.string().optional().nullable(),
  performedById: z.string().uuid().optional().nullable(),
  performedBy: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true, email: true }).optional().nullable(),
  metadata: z.record(z.unknown()).optional().nullable(),
  createdAt: z.coerce.date()
});
var impersonationSessionSchema = z.object({
  id: z.string().uuid(),
  superAdminUserId: z.string().uuid(),
  superAdminUser: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true, email: true }).optional(),
  tenantId: z.string().uuid(),
  tenant: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string()
  }).optional(),
  targetUserId: z.string().uuid(),
  targetUser: z.object({
    id: z.string().uuid(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string()
  }).optional(),
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date().optional().nullable(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable()
});
var accountNoteSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  authorId: z.string().uuid(),
  author: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true, email: true }).optional(),
  content: z.string().min(1),
  isPinned: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createAccountNoteSchema = z.object({
  content: z.string().min(1),
  isPinned: z.boolean().optional()
});
var updateAccountNoteSchema = z.object({
  content: z.string().min(1).optional(),
  isPinned: z.boolean().optional()
});
var accountListFiltersSchema = z.object({
  search: z.string().optional(),
  status: tenantStatusSchema.optional(),
  billingStatus: billingStatusSchema.optional(),
  planId: z.string().uuid().optional(),
  assignedToId: z.string().uuid().optional(),
  source: tenantSourceSchema.optional(),
  dateRange: z.enum(["all", "7days", "30days", "90days"]).optional()
});
var accountSortFieldSchema = z.enum([
  "name",
  "createdAt",
  "status",
  "billingStatus",
  "plan"
]);
var accountListItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  status: tenantStatusSchema,
  billingStatus: billingStatusSchema,
  plan: z.object({
    id: z.string().uuid(),
    name: z.string()
  }),
  userCount: z.number().int(),
  contactCount: z.number().int(),
  assignedTo: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true }).optional().nullable(),
  source: tenantSourceSchema.optional().nullable(),
  industry: z.string().optional().nullable(),
  trialStatus: z.enum(["none", "pending_card", "trialing", "active"]).optional().nullable(),
  trialEndsAt: z.coerce.date().optional().nullable(),
  /** Number of open disputes (status = opened|under_review). Renders as a badge on the AccountsList row when > 0. */
  openDisputeCount: z.number().int().optional().default(0),
  createdAt: z.coerce.date(),
  lastActivityAt: z.coerce.date().optional().nullable(),
  ownerEmail: z.string().email().optional().nullable()
});
var accountDetailSchema = accountListItemSchema.extend({
  helcimCustomerId: z.string().optional().nullable(),
  settings: z.record(z.unknown()),
  suspendedAt: z.coerce.date().optional().nullable(),
  cancelledAt: z.coerce.date().optional().nullable(),
  notes: z.string().optional().nullable(),
  updatedAt: z.coerce.date(),
  // Nested data for tabs
  users: z.array(
    z.object({
      id: z.string().uuid(),
      email: z.string().email(),
      firstName: z.string(),
      lastName: z.string(),
      role: z.string(),
      isActive: z.boolean(),
      lastLoginAt: z.coerce.date().optional().nullable(),
      createdAt: z.coerce.date()
    })
  ).optional(),
  recentActivity: z.array(tenantLifecycleEventSchema).optional(),
  pinnedNotes: z.array(accountNoteSchema).optional()
});
var suspendAccountDtoSchema = z.object({
  reason: z.string().min(1, "Suspension reason is required"),
  notifyUsers: z.boolean().optional()
});
var reactivateAccountDtoSchema = z.object({
  reason: z.string().optional(),
  notifyUsers: z.boolean().optional()
});
var cancelAccountDtoSchema = z.object({
  reason: z.string().min(1, "Cancellation reason is required"),
  effectiveDate: z.coerce.date().optional(),
  feedback: z.string().optional()
});
var createAccountDtoSchema = z.object({
  name: z.string().min(1),
  slug: z.string().min(1).regex(/^[a-z0-9-]+$/, "Slug must be lowercase with hyphens only"),
  planId: z.string().uuid(),
  industry: z.string().optional(),
  source: tenantSourceSchema.optional(),
  notes: z.string().optional(),
  // Initial admin user
  adminEmail: z.string().email(),
  adminFirstName: z.string().min(1),
  adminLastName: z.string().min(1),
  skipWelcomeEmail: z.boolean().optional()
});
var updateAccountDtoSchema = z.object({
  name: z.string().min(1).optional(),
  planId: z.string().uuid().optional(),
  industry: z.string().optional().nullable(),
  assignedToId: z.string().uuid().optional().nullable(),
  notes: z.string().optional().nullable()
});
var startImpersonationDtoSchema = z.object({
  targetUserId: z.string().uuid().optional(),
  reason: z.string().optional()
});
var impersonationResponseSchema = z.object({
  session: impersonationSessionSchema,
  accessToken: z.string(),
  redirectUrl: z.string()
});
var accountUsageStatsSchema = z.object({
  period: z.string(),
  documentsCreated: z.number().int(),
  documentsLimit: z.number().int(),
  smsSent: z.number().int(),
  smsLimit: z.number().int(),
  emailsSent: z.number().int(),
  emailsLimit: z.number().int(),
  formsSubmitted: z.number().int(),
  formsLimit: z.number().int(),
  aiCreditsUsed: z.number().int(),
  aiCreditsLimit: z.number().int()
});
var paginationMetaSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var paginatedAccountListSchema = z.object({
  data: z.array(accountListItemSchema),
  pagination: paginationMetaSchema
});
var supportRequestStatusSchema = z.enum([
  "open",
  "in_progress",
  "resolved",
  "closed"
]);
var supportPrioritySchema = z.enum(["low", "medium", "high", "urgent"]);
var supportCategorySchema = z.enum([
  "billing",
  "technical",
  "compliance",
  "account",
  "feature_request",
  "other"
]);
var authorTypeSchema = z.enum(["tenant_user", "super_admin"]);
var flaggedContentTypeSchema = z.enum(["message", "document", "form"]);
var flagSeveritySchema = z.enum(["low", "medium", "high"]);
var flagStatusSchema = z.enum([
  "pending",
  "reviewed",
  "dismissed",
  "actioned"
]);
var supportRequestSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenant: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string()
  }).optional(),
  subject: z.string().min(1).max(500),
  description: z.string().min(1),
  status: supportRequestStatusSchema,
  priority: supportPrioritySchema,
  category: supportCategorySchema,
  assignedToId: z.string().uuid().optional().nullable(),
  assignedTo: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true, email: true }).optional().nullable(),
  createdById: z.string(),
  resolvedAt: z.coerce.date().optional().nullable(),
  resolvedById: z.string().uuid().optional().nullable(),
  resolvedBy: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true }).optional().nullable(),
  metadata: z.record(z.unknown()).optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var supportMessageSchema = z.object({
  id: z.string().uuid(),
  requestId: z.string().uuid(),
  authorType: authorTypeSchema,
  authorId: z.string(),
  authorName: z.string().optional(),
  // Populated from lookup
  content: z.string().min(1),
  attachments: z.array(
    z.object({
      url: z.string(),
      fileName: z.string(),
      fileSize: z.number().optional(),
      mimeType: z.string().optional()
    })
  ).optional().nullable(),
  isInternal: z.boolean(),
  createdAt: z.coerce.date()
});
var aiFlaggedContentSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenant: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string()
  }).optional(),
  contentType: flaggedContentTypeSchema,
  contentId: z.string(),
  contentPreview: z.string().optional(),
  // Populated preview
  flagReason: z.string(),
  severity: flagSeveritySchema,
  status: flagStatusSchema,
  reviewedById: z.string().uuid().optional().nullable(),
  reviewedBy: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true }).optional().nullable(),
  reviewedAt: z.coerce.date().optional().nullable(),
  notes: z.string().optional().nullable(),
  createdAt: z.coerce.date()
});
var createSupportRequestDtoSchema = z.object({
  tenantId: z.string().uuid(),
  subject: z.string().min(1).max(500),
  description: z.string().min(1),
  priority: supportPrioritySchema.optional(),
  category: supportCategorySchema,
  createdById: z.string()
});
var updateSupportRequestDtoSchema = z.object({
  subject: z.string().min(1).max(500).optional(),
  priority: supportPrioritySchema.optional(),
  category: supportCategorySchema.optional(),
  status: supportRequestStatusSchema.optional()
});
var assignSupportRequestDtoSchema = z.object({
  assignedToId: z.string().uuid().nullable()
});
var resolveSupportRequestDtoSchema = z.object({
  resolution: z.string().min(1).optional()
});
var addSupportMessageDtoSchema = z.object({
  content: z.string().min(1),
  isInternal: z.boolean().optional(),
  attachments: z.array(
    z.object({
      url: z.string(),
      fileName: z.string(),
      fileSize: z.number().optional(),
      mimeType: z.string().optional()
    })
  ).optional()
});
var reviewAiFlagDtoSchema = z.object({
  status: z.enum(["reviewed", "dismissed", "actioned"]),
  notes: z.string().optional()
});
var supportQueueSummarySchema = z.object({
  total: z.number().int(),
  open: z.number().int(),
  inProgress: z.number().int(),
  resolved: z.number().int(),
  closed: z.number().int(),
  urgent: z.number().int(),
  unassigned: z.number().int(),
  avgResolutionTimeHours: z.number().optional(),
  flaggedContentCount: z.number().int()
});
var supportListFiltersSchema = z.object({
  search: z.string().optional(),
  status: supportRequestStatusSchema.optional(),
  priority: supportPrioritySchema.optional(),
  category: supportCategorySchema.optional(),
  assignedToId: z.string().uuid().optional(),
  tenantId: z.string().uuid().optional(),
  dateRange: z.enum(["all", "7days", "30days", "90days"]).optional()
});
var paginatedSupportRequestListSchema = z.object({
  data: z.array(supportRequestSchema),
  pagination: paginationMetaSchema
});
var paginatedAiFlagsListSchema = z.object({
  data: z.array(aiFlaggedContentSchema),
  pagination: paginationMetaSchema
});
var planStatusSchema = z.enum(["active", "inactive", "deprecated"]);
var superAdminPlanSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  monthlyPrice: z.coerce.number(),
  annualPrice: z.coerce.number(),
  smsCreditsPerMonth: z.number().int(),
  emailCreditsPerMonth: z.number().int(),
  aiCreditsPerMonth: z.number().int().default(0),
  // Extended usage limits (from schema.prisma)
  maxContacts: z.number().int().default(100),
  maxUsers: z.number().int().default(1),
  maxSmsPerBroadcast: z.number().int().default(10),
  maxEmailPerBroadcast: z.number().int().default(25),
  maxForms: z.number().int().default(1),
  features: z.string(),
  // JSON string of features
  isActive: z.boolean(),
  /** @deprecated Use helcimPlanId / helcimAnnualPlanId instead */
  stripePriceId: z.string().optional().nullable(),
  helcimPlanId: z.string().optional().nullable(),
  helcimAnnualPlanId: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  displayOrder: z.number().int().default(0),
  tierLevel: z.number().int().default(0),
  highlightLabel: z.string().optional().nullable(),
  badgeColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional().nullable(),
  recommendedForIndividual: z.boolean().default(false),
  recommendedForBusiness: z.boolean().default(false),
  // Trial configuration: 0 = no trial (charge immediately on card capture).
  // Surfaces in OOBE plan picker as "X-day free trial" badge when > 0.
  trialDays: z.number().int().min(0).max(90).default(0),
  // When true, a trial plan still requires card capture before the trial starts.
  // When false (default), trial plans start a no-card trial in the OOBE.
  requireCardForTrial: z.boolean().default(false),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var planWithStatsSchema = superAdminPlanSchema.extend({
  subscriberCount: z.number().int(),
  mrrContribution: z.coerce.number(),
  percentOfTotal: z.number()
});
var createPlanDtoSchema = z.object({
  name: z.string().min(1),
  monthlyPrice: z.coerce.number().min(0),
  annualPrice: z.coerce.number().min(0),
  smsCreditsPerMonth: z.number().int().min(0),
  emailCreditsPerMonth: z.number().int().min(0),
  aiCreditsPerMonth: z.number().int().min(0).optional(),
  maxContacts: z.number().int().min(0).optional(),
  maxUsers: z.number().int().min(0).optional(),
  maxSmsPerBroadcast: z.number().int().min(0).optional(),
  maxEmailPerBroadcast: z.number().int().min(0).optional(),
  maxForms: z.number().int().min(0).optional(),
  features: z.string(),
  isActive: z.boolean().optional(),
  /** @deprecated Use helcimPlanId / helcimAnnualPlanId instead */
  stripePriceId: z.string().optional(),
  helcimPlanId: z.string().optional().nullable(),
  helcimAnnualPlanId: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  displayOrder: z.number().int().optional(),
  tierLevel: z.number().int().min(0).max(3).optional(),
  highlightLabel: z.string().max(50).optional().nullable(),
  badgeColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional().nullable(),
  recommendedForIndividual: z.boolean().optional(),
  recommendedForBusiness: z.boolean().optional(),
  trialDays: z.number().int().min(0).max(90).optional(),
  requireCardForTrial: z.boolean().optional()
});
var updatePlanDtoSchema = createPlanDtoSchema.partial();
var planListFiltersSchema = z.object({
  search: z.string().optional(),
  isActive: z.boolean().optional()
});
var paginatedPlansListSchema = z.object({
  data: z.array(planWithStatsSchema),
  pagination: paginationMetaSchema
});
var revenueMetricsSchema = z.object({
  mrr: z.coerce.number(),
  arr: z.coerce.number(),
  totalRevenue: z.coerce.number(),
  activeSubscriptions: z.number().int(),
  churnRate: z.number(),
  averageRevenuePerAccount: z.coerce.number(),
  growthRate: z.number()
});
var mrrBreakdownSchema = z.object({
  newMrr: z.coerce.number(),
  expansionMrr: z.coerce.number(),
  contractionMrr: z.coerce.number(),
  churnedMrr: z.coerce.number(),
  netNewMrr: z.coerce.number()
});
var revenueTrendDataPointSchema = z.object({
  date: z.string(),
  mrr: z.coerce.number(),
  arr: z.coerce.number(),
  newMrr: z.coerce.number(),
  churnedMrr: z.coerce.number(),
  activeSubscriptions: z.number().int()
});
var invoiceStatusSchema = z.enum(["draft", "paid", "pending", "overdue", "cancelled", "refunded", "disputed"]);
var invoiceLineItemSchema = z.object({
  description: z.string(),
  quantity: z.number(),
  unitPrice: z.coerce.number(),
  total: z.coerce.number(),
  type: z.enum(["subscription", "addon", "overage", "credit"])
});
var invoiceSchema = z.object({
  id: z.string().uuid(),
  helcimInvoiceId: z.string().optional().nullable(),
  invoiceNumber: z.string(),
  tenantId: z.string().uuid(),
  tenantName: z.string().optional(),
  tenant: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string()
  }).optional(),
  subtotal: z.coerce.number(),
  tax: z.coerce.number(),
  total: z.coerce.number(),
  amountPaid: z.coerce.number(),
  amountDue: z.coerce.number(),
  currency: z.string().default("USD"),
  status: invoiceStatusSchema,
  dueDate: z.coerce.date(),
  paidAt: z.coerce.date().optional().nullable(),
  periodStart: z.coerce.date(),
  periodEnd: z.coerce.date(),
  lineItems: z.array(invoiceLineItemSchema).optional(),
  notes: z.string().optional().nullable(),
  createdAt: z.coerce.date()
});
var invoiceListFiltersSchema = z.object({
  search: z.string().optional(),
  status: invoiceStatusSchema.optional(),
  tenantId: z.string().uuid().optional(),
  dateRange: z.enum(["all", "7days", "30days", "90days"]).optional()
});
var paginatedInvoicesListSchema = z.object({
  data: z.array(invoiceSchema),
  pagination: paginationMetaSchema
});
var pastDueAccountSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  tenantSlug: z.string(),
  planName: z.string(),
  amountDue: z.coerce.number(),
  daysOverdue: z.number().int(),
  lastPaymentDate: z.coerce.date().optional().nullable(),
  invoiceCount: z.number().int(),
  contactEmail: z.string().email().optional().nullable()
});
var disputeStatusSchema = z.enum([
  // New (Helcim-sourced) lifecycle values from the Dispute model. The
  // BillingDashboard's existing DisputesList component still understands the
  // legacy `needs_response`/`withdrawn` aliases.
  "opened",
  "under_review",
  "won",
  "lost",
  "accepted",
  // Legacy aliases — kept until the BillingDashboard list is migrated.
  "needs_response",
  "withdrawn"
]);
var paymentDisputeSchema = z.object({
  id: z.string(),
  helcimDisputeId: z.string().optional().nullable(),
  tenantId: z.string().uuid(),
  tenant: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string()
  }).optional(),
  paymentId: z.string().uuid().optional().nullable(),
  payment: z.object({
    id: z.string().uuid(),
    helcimTransactionId: z.string().optional().nullable(),
    amount: z.coerce.number(),
    currency: z.string()
  }).optional().nullable(),
  amount: z.coerce.number(),
  currency: z.string().default("USD"),
  reason: z.string().nullable(),
  status: disputeStatusSchema,
  evidenceDueBy: z.coerce.date().optional().nullable(),
  openedAt: z.coerce.date().optional().nullable(),
  resolvedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var overageTypeSchema = z.enum(["documents", "sms", "email", "storage", "voice"]);
var usageOverageSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  tenantSlug: z.string(),
  type: overageTypeSchema,
  limit: z.number().int(),
  used: z.number().int(),
  overage: z.number().int(),
  percentOver: z.number(),
  period: z.string(),
  estimatedCharge: z.coerce.number(),
  createdAt: z.coerce.date()
});
var paymentSummarySchema = z.object({
  id: z.string().uuid(),
  tenantName: z.string(),
  amount: z.coerce.number(),
  status: z.string(),
  date: z.coerce.date()
});
var planDistributionSchema = z.object({
  planId: z.string().uuid(),
  planName: z.string(),
  tierLevel: z.number().int(),
  subscriberCount: z.number().int(),
  mrrContribution: z.coerce.number(),
  percentOfTotal: z.number()
});
var billingDashboardSchema = z.object({
  metrics: revenueMetricsSchema,
  mrrBreakdown: mrrBreakdownSchema,
  revenueTrend: z.array(revenueTrendDataPointSchema),
  pastDueCount: z.number().int(),
  pastDueAmount: z.coerce.number(),
  recentPayments: z.array(paymentSummarySchema),
  planDistribution: z.array(planDistributionSchema)
});
var freePlanAccountSchema = z.object({
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  tenantSlug: z.string(),
  contactEmail: z.string().email().optional().nullable(),
  daysOnFreePlan: z.number().int(),
  joinedAt: z.coerce.date(),
  billingStatus: z.string(),
  status: z.string(),
  userCount: z.number().int()
});
var billingCycleSchema = z.enum(["monthly", "annual"]);
var subscriptionInfoSchema = z.object({
  subscriptionId: z.string(),
  helcimSubscriptionId: z.number().nullable(),
  planId: z.string().uuid(),
  planName: z.string(),
  status: z.string(),
  billingCycle: billingCycleSchema,
  amount: z.coerce.number(),
  currentPeriodStart: z.coerce.date().nullable(),
  currentPeriodEnd: z.coerce.date().nullable(),
  nextBillingDate: z.coerce.date().nullable()
});
var usageTypeSchema = z.enum([
  "sms",
  "email",
  "whatsapp",
  "voice_minutes",
  "documents",
  "forms",
  "contacts",
  "api_calls"
]);
var usageStatusSchema = z.object({
  type: usageTypeSchema,
  used: z.number().int(),
  limit: z.number().int(),
  remaining: z.number().int(),
  percentUsed: z.number(),
  isAtLimit: z.boolean(),
  canExceedLimit: z.boolean()
});
var tenantUsageSummarySchema = z.object({
  tenantId: z.string().uuid(),
  period: z.string(),
  planName: z.string(),
  tierLevel: z.number().int(),
  usage: z.array(usageStatusSchema),
  totalOverages: z.coerce.number()
});
var addOnTypeSchema = z.enum([
  "sms_pack",
  "email_pack",
  "whatsapp_pack",
  "voice_pack",
  "user_seat",
  "phone_number"
]);
var addOnPurchaseSchema = z.object({
  id: z.string().uuid(),
  type: addOnTypeSchema,
  name: z.string(),
  quantity: z.number().int(),
  unitPrice: z.coerce.number(),
  totalPrice: z.coerce.number(),
  creditsIncluded: z.number().int().nullable(),
  creditsRemaining: z.number().int().nullable(),
  billingType: z.enum(["one_time", "recurring"]),
  status: z.string(),
  activatedAt: z.coerce.date(),
  expiresAt: z.coerce.date().nullable()
});
var availableAddOnSchema = z.object({
  type: addOnTypeSchema,
  name: z.string(),
  description: z.string(),
  unitPrice: z.coerce.number(),
  credits: z.number().int().optional(),
  recurring: z.boolean().optional()
});
var addOnCategorySchema = z.enum(["usage", "seat", "limit-increase", "phone-number", "feature"]);
var limitIncreaseTypeSchema = z.enum(["absolute_increase", "multiplicative", "set_value"]);
var addOnCatalogSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  // e.g., "extended_limit_monthly"
  name: z.string(),
  description: z.string().optional().nullable(),
  category: addOnCategorySchema,
  billingType: z.enum(["one_time", "recurring"]),
  unitPrice: z.coerce.number(),
  currency: z.string().default("USD"),
  // For credit-based add-ons
  creditsValue: z.number().int().optional().nullable(),
  creditType: z.string().optional().nullable(),
  // sms, email, whatsapp, voice_minutes, documents, contacts
  // For limit-increase add-ons
  affectedLimitField: z.string().optional().nullable(),
  // e.g., "maxContacts"
  increaseAmount: z.number().int().optional().nullable(),
  increaseType: limitIncreaseTypeSchema.optional(),
  // Helcim Integration
  helcimAddOnId: z.string().optional().nullable(),
  // Metadata
  isActive: z.boolean(),
  displayOrder: z.number().int(),
  icon: z.string().optional().nullable(),
  metadata: z.record(z.unknown()).optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var planAddOnAssignmentSchema = z.object({
  id: z.string().uuid(),
  planId: z.string().uuid(),
  addOnId: z.string().uuid(),
  addOn: addOnCatalogSchema.optional(),
  overriddenUnitPrice: z.coerce.number().optional().nullable(),
  isEnabled: z.boolean(),
  maxQuantityPerPeriod: z.number().int().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createAddOnCatalogDtoSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional().nullable(),
  category: addOnCategorySchema,
  billingType: z.enum(["one_time", "recurring"]),
  unitPrice: z.coerce.number().positive(),
  currency: z.string().default("USD"),
  creditsValue: z.number().int().optional(),
  creditType: z.string().optional(),
  affectedLimitField: z.string().optional(),
  increaseAmount: z.number().int().optional(),
  increaseType: limitIncreaseTypeSchema.optional(),
  helcimAddOnId: z.string().optional(),
  isActive: z.boolean().default(true),
  displayOrder: z.number().int().default(0),
  icon: z.string().optional(),
  metadata: z.record(z.unknown()).optional()
});
var updateAddOnCatalogDtoSchema = createAddOnCatalogDtoSchema.partial();
var assignAddOnToPlanDtoSchema = z.object({
  planId: z.string().uuid(),
  addOnIds: z.array(z.string().uuid()),
  // Can assign multiple at once
  overriddenUnitPrice: z.coerce.number().optional(),
  isEnabled: z.boolean().default(true),
  maxQuantityPerPeriod: z.number().int().optional()
});
var unassignAddOnFromPlanDtoSchema = z.object({
  planId: z.string().uuid(),
  addOnIds: z.array(z.string().uuid())
});
var addOnCatalogFiltersSchema = z.object({
  search: z.string().optional(),
  category: addOnCategorySchema.optional(),
  billingType: z.enum(["one_time", "recurring"]).optional(),
  isActive: z.boolean().optional()
});
var paginatedAddOnCatalogSchema = z.object({
  data: z.array(addOnCatalogSchema),
  pagination: paginationMetaSchema
});
var templateTypeSchema = z.enum(["email", "sms", "document", "form"]);
var templateCategorySchema = templateTypeSchema;
var globalTemplateSchema = z.object({
  id: z.string().uuid(),
  type: templateTypeSchema,
  // Template type: form, document, sms, email
  name: z.string().min(1),
  description: z.string().optional().nullable(),
  category: z.string().optional().nullable(),
  // Subcategory (e.g., 'popular', 'healthcare' for forms, 'sales', 'hr' for documents)
  content: z.string(),
  // JSON or rich text content
  visibility: templateVisibilitySchema,
  visibleToPlans: z.array(z.string().uuid()).optional().nullable(),
  visibleToTenants: z.array(z.string().uuid()).optional().nullable(),
  usageCount: z.number().int().default(0),
  isActive: z.boolean().default(true),
  createdById: z.string().uuid().optional().nullable(),
  createdBy: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true, email: true }).optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createTemplateDtoSchema = z.object({
  type: templateTypeSchema,
  // Required: form, document, sms, email
  name: z.string().min(1),
  description: z.string().optional().nullable(),
  category: z.string().optional().nullable(),
  // Optional subcategory
  content: z.string(),
  visibility: templateVisibilitySchema.optional(),
  visibleToPlans: z.array(z.string().uuid()).optional(),
  visibleToTenants: z.array(z.string().uuid()).optional(),
  isActive: z.boolean().optional()
});
var updateTemplateDtoSchema = createTemplateDtoSchema.partial();
var updateTemplateVisibilityDtoSchema = z.object({
  visibility: templateVisibilitySchema,
  visibleToPlans: z.array(z.string().uuid()).optional(),
  visibleToTenants: z.array(z.string().uuid()).optional()
});
var templateListFiltersSchema = z.object({
  search: z.string().optional(),
  category: z.string().optional().nullable(),
  // Subcategory filter (e.g., 'popular', 'healthcare', 'sales', 'hr')
  visibility: templateVisibilitySchema.optional(),
  isActive: z.boolean().optional()
});
var paginatedTemplatesListSchema = z.object({
  data: z.array(globalTemplateSchema),
  pagination: paginationMetaSchema
});
var templateCategoryTypeSchema = z.enum(["form", "document"]);
var templateCategoryEntitySchema = z.object({
  id: z.string().uuid(),
  slug: z.string().min(1),
  // e.g., "sales-contracts"
  name: z.string().min(1),
  // e.g., "Sales & Contracts"
  description: z.string().optional().nullable(),
  color: z.string().default("neutral"),
  // Badge color
  icon: z.string().optional().nullable(),
  // Lucide icon name
  type: templateCategoryTypeSchema,
  // "form" or "document"
  sortOrder: z.number().int().default(0),
  isActive: z.boolean().default(true),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createTemplateCategoryDtoSchema = z.object({
  slug: z.string().min(1).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
    message: "Slug must be lowercase alphanumeric with hyphens only"
  }),
  name: z.string().min(1),
  description: z.string().optional().nullable(),
  color: z.string().optional(),
  icon: z.string().optional().nullable(),
  type: templateCategoryTypeSchema
});
var updateTemplateCategoryDtoSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional().nullable(),
  color: z.string().optional(),
  icon: z.string().optional().nullable(),
  isActive: z.boolean().optional()
});
var reorderCategoriesDtoSchema = z.object({
  type: templateCategoryTypeSchema,
  categoryIds: z.array(z.string().uuid())
});
var platformCampaignStatusSchema = z.enum([
  "draft",
  "active",
  "paused",
  "completed",
  "archived"
]);
var campaignTriggerSchema = z.enum([
  "signup",
  "trial_end",
  "plan_change",
  "inactivity",
  "manual"
]);
var campaignStepTypeSchema = z.enum(["email", "sms", "wait"]);
var campaignAudienceTypeSchema = z.enum(["all", "plans", "tags", "custom"]);
var enrollmentStatusSchema = z.enum(["active", "completed", "paused", "exited"]);
var campaignStepSchema = z.object({
  id: z.string(),
  order: z.number().int(),
  type: campaignStepTypeSchema,
  // For wait steps - delay in days
  delay: z.number().int().nullable().optional(),
  // For email steps - inline content (supports {{merge.tags}})
  emailSubject: z.string().nullable().optional(),
  emailContent: z.string().nullable().optional(),
  // HTML body
  // For sms steps - inline content (supports {{merge.tags}})
  smsContent: z.string().nullable().optional()
});
var platformCampaignSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().optional().nullable(),
  status: platformCampaignStatusSchema,
  trigger: campaignTriggerSchema,
  // Audience targeting
  audienceType: campaignAudienceTypeSchema,
  audiencePlans: z.array(z.string().uuid()).optional().nullable(),
  audienceTags: z.array(z.string().uuid()).optional().nullable(),
  audienceFilter: z.string().optional().nullable(),
  // JSON for custom filters
  // Steps
  steps: z.array(campaignStepSchema),
  // Stats
  enrolledCount: z.number().int().default(0),
  completedCount: z.number().int().default(0),
  exitedCount: z.number().int().default(0),
  // Scheduling
  startDate: z.coerce.date().optional().nullable(),
  endDate: z.coerce.date().optional().nullable(),
  // Metadata
  createdById: z.string().uuid().optional().nullable(),
  createdBy: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true, email: true }).optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var campaignEnrollmentSchema = z.object({
  id: z.string().uuid(),
  campaignId: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantName: z.string().optional(),
  tenant: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string()
  }).optional(),
  currentStepIndex: z.number().int(),
  status: enrollmentStatusSchema,
  startedAt: z.coerce.date(),
  completedAt: z.coerce.date().optional().nullable(),
  exitedAt: z.coerce.date().optional().nullable(),
  exitReason: z.string().optional().nullable()
});
var campaignStepStatsSchema = z.object({
  stepId: z.string(),
  reached: z.number().int(),
  completed: z.number().int(),
  exitRate: z.number()
});
var campaignStatsSchema = z.object({
  campaignId: z.string().uuid(),
  totalEnrolled: z.number().int(),
  activeEnrollments: z.number().int(),
  completedEnrollments: z.number().int(),
  exitedEnrollments: z.number().int(),
  averageCompletionDays: z.number().optional(),
  // Funnel data for visualization
  stepMetrics: z.array(campaignStepStatsSchema),
  // Email metrics
  emailMetrics: z.object({
    sent: z.number().int(),
    delivered: z.number().int(),
    opened: z.number().int(),
    clicked: z.number().int(),
    openRate: z.number(),
    clickRate: z.number()
  }).optional()
});
var createCampaignStepDtoSchema = z.object({
  id: z.string(),
  order: z.number().int(),
  type: campaignStepTypeSchema,
  // For wait steps
  delay: z.number().int().nullable().optional(),
  // For email steps
  emailSubject: z.string().nullable().optional(),
  emailContent: z.string().nullable().optional(),
  // For sms steps
  smsContent: z.string().nullable().optional()
});
var createPlatformCampaignDtoSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional().nullable(),
  trigger: campaignTriggerSchema,
  audienceType: campaignAudienceTypeSchema,
  audiencePlans: z.array(z.string().uuid()).optional(),
  audienceTags: z.array(z.string().uuid()).optional(),
  audienceFilter: z.string().optional(),
  steps: z.array(createCampaignStepDtoSchema),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional()
});
var updatePlatformCampaignDtoSchema = createPlatformCampaignDtoSchema.partial();
var platformCampaignListFiltersSchema = z.object({
  search: z.string().optional(),
  status: platformCampaignStatusSchema.optional(),
  trigger: campaignTriggerSchema.optional()
});
var paginatedCampaignsListSchema = z.object({
  data: z.array(platformCampaignSchema),
  pagination: paginationMetaSchema
});
var paginatedEnrollmentsListSchema = z.object({
  data: z.array(campaignEnrollmentSchema),
  pagination: paginationMetaSchema
});
var enrollTenantsInCampaignDtoSchema = z.object({
  tenantIds: z.array(z.string().uuid()).min(1)
});
var accountTagSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  color: z.string().default("#6366f1"),
  // Hex color
  description: z.string().optional().nullable(),
  accountCount: z.number().int().default(0),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createAccountTagDtoSchema = z.object({
  name: z.string().min(1),
  color: z.string().optional(),
  description: z.string().optional().nullable()
});
var paginatedTagsListSchema = z.object({
  data: z.array(accountTagSchema),
  pagination: paginationMetaSchema
});
var serviceHealthSchema = z.object({
  service: z.string(),
  status: healthStatusSchema,
  latencyMs: z.number().optional().nullable(),
  lastCheck: z.coerce.date(),
  errorMessage: z.string().optional().nullable(),
  metadata: z.record(z.unknown()).optional().nullable()
});
var platformHealthStatusSchema = z.object({
  services: z.array(serviceHealthSchema),
  overallStatus: healthStatusSchema,
  lastUpdated: z.coerce.date()
});
var queueStatsSchema = z.object({
  name: z.string(),
  displayName: z.string(),
  pending: z.number().int(),
  processing: z.number().int(),
  failed: z.number().int(),
  completed: z.number().int(),
  paused: z.boolean(),
  lastProcessedAt: z.coerce.date().optional().nullable()
});
var providerStatusSchema = z.object({
  name: z.string(),
  displayName: z.string(),
  status: healthStatusSchema,
  latencyMs: z.number().optional().nullable(),
  lastCheck: z.coerce.date(),
  errorCount24h: z.number().int().default(0),
  successRate24h: z.number().optional().nullable(),
  lastError: z.string().optional().nullable()
});
var maintenanceModeSchema = z.object({
  isActive: z.boolean(),
  message: z.string().optional().nullable(),
  scheduledEnd: z.coerce.date().optional().nullable(),
  startedAt: z.coerce.date().optional().nullable(),
  startedById: z.string().uuid().optional().nullable(),
  startedBy: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true, email: true }).optional().nullable()
});
var toggleMaintenanceModeDtoSchema = z.object({
  isActive: z.boolean(),
  message: z.string().optional(),
  scheduledEnd: z.coerce.date().optional()
});
var auditLogFiltersSchema = z.object({
  search: z.string().optional(),
  userId: z.string().uuid().optional(),
  action: superAdminAuditActionSchema.optional(),
  entityType: superAdminEntityTypeSchema.optional(),
  entityId: z.string().optional(),
  dateRange: z.enum(["all", "24h", "7days", "30days", "90days"]).optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional()
});
var paginatedAuditLogsSchema = z.object({
  data: z.array(superAdminAuditLogSchema),
  pagination: paginationMetaSchema
});
var auditExportRequestSchema = z.object({
  format: z.enum(["csv", "json"]),
  filters: auditLogFiltersSchema.optional()
});
var auditActivityDataPointSchema = z.object({
  date: z.string(),
  count: z.number().int()
});
var auditStatsSchema = z.object({
  totalLogs: z.number().int(),
  logsToday: z.number().int(),
  logsByAction: z.record(z.string(), z.number().int()),
  logsByEntityType: z.record(z.string(), z.number().int()),
  recentActivity: z.array(auditActivityDataPointSchema)
});
var superAdminUserListItemSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  role: superAdminRoleSchema,
  avatar: z.string().url().optional().nullable(),
  isActive: z.boolean(),
  lastLoginAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date()
});
var paginatedSuperAdminUserListSchema = z.object({
  data: z.array(superAdminUserListItemSchema),
  pagination: paginationMetaSchema
});
var adminUserFiltersSchema = z.object({
  search: z.string().optional(),
  role: superAdminRoleSchema.optional(),
  isActive: z.boolean().optional()
});
var resetPasswordDtoSchema = z.object({
  sendEmail: z.boolean().optional()
});
var featureFlagScopeSchema = z.enum(["global", "plan", "tenant"]);
var featureFlagSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  key: z.string().regex(/^[a-z0-9_]+$/),
  description: z.string().optional().nullable(),
  enabled: z.boolean(),
  scope: featureFlagScopeSchema,
  targetPlans: z.array(z.string().uuid()).optional().nullable(),
  targetTenants: z.array(z.string().uuid()).optional().nullable(),
  createdById: z.string().uuid().optional().nullable(),
  createdBy: superAdminUserSchema.pick({ id: true, firstName: true, lastName: true }).optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var featureFlagFiltersSchema = z.object({
  search: z.string().optional(),
  scope: featureFlagScopeSchema.optional(),
  enabled: z.boolean().optional()
});
var createFeatureFlagDtoSchema = z.object({
  name: z.string().min(1),
  key: z.string().regex(/^[a-z0-9_]+$/),
  description: z.string().optional().nullable(),
  enabled: z.boolean().optional(),
  scope: featureFlagScopeSchema,
  targetPlans: z.array(z.string().uuid()).optional(),
  targetTenants: z.array(z.string().uuid()).optional()
});
var updateFeatureFlagDtoSchema = createFeatureFlagDtoSchema.partial();
var toggleFeatureFlagDtoSchema = z.object({
  enabled: z.boolean()
});
var paginatedFeatureFlagsListSchema = z.object({
  data: z.array(featureFlagSchema),
  pagination: paginationMetaSchema
});
var emailProviderSchema = z.enum(["mailgun", "sendgrid", "smtp"]);
var emailConfigSchema = z.object({
  provider: emailProviderSchema,
  apiKey: z.string().optional(),
  domain: z.string().optional(),
  fromEmail: z.string().email(),
  fromName: z.string(),
  replyToEmail: z.string().email().optional().nullable(),
  smtpHost: z.string().optional().nullable(),
  smtpPort: z.number().int().optional().nullable(),
  smtpUsername: z.string().optional().nullable(),
  smtpPassword: z.string().optional().nullable(),
  smtpSecure: z.boolean().optional().nullable(),
  trackOpens: z.boolean().default(true),
  trackClicks: z.boolean().default(true),
  updatedAt: z.coerce.date().optional()
});
var updateEmailConfigDtoSchema = emailConfigSchema.omit({ updatedAt: true }).partial();
var smsProviderSchema = z.enum(["sinch", "twilio"]);
var smsConfigSchema = z.object({
  provider: smsProviderSchema,
  apiKey: z.string().optional(),
  apiSecret: z.string().optional(),
  senderId: z.string(),
  servicePlanId: z.string().optional().nullable(),
  accountSid: z.string().optional().nullable(),
  authToken: z.string().optional().nullable(),
  messagingServiceSid: z.string().optional().nullable(),
  deliveryReports: z.boolean().default(true),
  optOutHandling: z.boolean().default(true),
  updatedAt: z.coerce.date().optional()
});
var updateSMSConfigDtoSchema = smsConfigSchema.omit({ updatedAt: true }).partial();
var complianceSettingsSchema = z.object({
  dataRetentionDays: z.number().int().min(30).max(3650),
  auditLogRetentionDays: z.number().int().min(90).max(3650),
  gdprEnabled: z.boolean(),
  hipaaEnabled: z.boolean(),
  ccpaEnabled: z.boolean(),
  anonymizeDeletedUsers: z.boolean(),
  requireConsentForMarketing: z.boolean(),
  enforcePasswordPolicy: z.boolean(),
  passwordMinLength: z.number().int().min(8).max(32),
  passwordRequireUppercase: z.boolean(),
  passwordRequireNumber: z.boolean(),
  passwordRequireSpecial: z.boolean(),
  sessionTimeoutMinutes: z.number().int().min(15).max(1440),
  maxFailedLoginAttempts: z.number().int().min(3).max(10),
  lockoutDurationMinutes: z.number().int().min(5).max(60),
  logAllApiCalls: z.boolean(),
  logSensitiveDataAccess: z.boolean(),
  updatedAt: z.coerce.date().optional()
});
var updateComplianceSettingsDtoSchema = complianceSettingsSchema.omit({ updatedAt: true }).partial();
var discountTypeSchema = z.enum(["percentage", "fixed"]);
var discountScopeSchema = z.enum(["all_plans", "specific_plans", "account"]);
var discountSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().optional().nullable(),
  code: z.string().optional().nullable(),
  type: discountTypeSchema,
  value: z.coerce.number(),
  scope: discountScopeSchema,
  planIds: z.array(z.string().uuid()).optional().nullable(),
  tenantId: z.string().uuid().optional().nullable(),
  startsAt: z.coerce.date().optional().nullable(),
  expiresAt: z.coerce.date().optional().nullable(),
  maxUses: z.number().int().optional().nullable(),
  usedCount: z.number().int(),
  maxUsesPerAccount: z.number().int().optional().nullable(),
  isActive: z.boolean(),
  createdBy: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var discountWithStatsSchema = discountSchema.extend({
  assignmentCount: z.number().int(),
  tenantName: z.string().optional().nullable()
});
var tenantDiscountSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  discountId: z.string().uuid(),
  discount: discountSchema,
  limitOverrides: z.record(z.number()).optional().nullable(),
  assignedAt: z.coerce.date(),
  assignedBy: z.string(),
  notes: z.string().optional().nullable(),
  isActive: z.boolean()
});
var createDiscountDtoSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2e3).optional(),
  code: z.string().max(50).regex(/^[A-Z0-9_-]*$/, "Code must be uppercase alphanumeric").optional().nullable(),
  type: discountTypeSchema,
  value: z.coerce.number().positive(),
  scope: discountScopeSchema,
  planIds: z.array(z.string().uuid()).optional(),
  tenantId: z.string().uuid().optional().nullable(),
  startsAt: z.coerce.date().optional().nullable(),
  expiresAt: z.coerce.date().optional().nullable(),
  maxUses: z.number().int().positive().optional().nullable(),
  maxUsesPerAccount: z.number().int().positive().optional().nullable(),
  isActive: z.boolean().optional()
});
var updateDiscountDtoSchema = createDiscountDtoSchema.partial();
var assignDiscountDtoSchema = z.object({
  discountId: z.string().uuid(),
  limitOverrides: z.record(z.number()).optional(),
  notes: z.string().optional()
});
var discountListFiltersSchema = z.object({
  search: z.string().optional(),
  type: discountTypeSchema.optional(),
  scope: discountScopeSchema.optional(),
  isActive: z.boolean().optional(),
  hasCode: z.boolean().optional()
});
var paginatedDiscountsListSchema = z.object({
  data: z.array(discountWithStatsSchema),
  pagination: paginationMetaSchema
});
var messagePurposeSchema = z.enum(["MARKETING", "INFORMATIONAL"]);
var preChannelSchema = z.enum(["SMS", "EMAIL", "VOICE", "RCS", "SOCIAL_MESSAGE"]);
var consentStatusSchema = z.enum([
  "UNKNOWN",
  "NONE",
  "OPTOUT",
  "IMPLICIT_OPT_IN",
  "EXPRESS_OPTIN",
  "WRITTEN_EXPRESS_OPTIN"
]);
var ebrEventTypeSchema = z.enum([
  "PURCHASE",
  "INQUIRY",
  "CONSPICUOUS_PUBLICATION"
]);
var preDecisionSchema = z.enum(["ALLOW", "ALLOW_WITH_CONDITIONS", "BLOCK"]);
var eligibilityStatusSchema = z.enum(["ELIGIBLE", "NOT_ELIGIBLE"]);
var dncStatusSchema = z.enum(["UNKNOWN", "NOT_ON_LIST", "ON_LIST"]);
var b2bOrB2cSchema = z.enum(["B2B", "B2C"]);
var ruleLayerSchema = z.enum(["eligibility", "constraint", "consent_capture"]);
var ruleEffectSchema = z.enum(["ALLOW", "CONDITION", "BLOCK"]);
var subdivisionTypeSchema = z.enum(["GLOBAL", "FEDERAL", "STATE", "PROVINCE", "NATIONAL"]);
var registryTypeSchema = z.enum(["NATIONAL", "INTERNAL"]);
var PRE_CHECK_IDS = {
  CHK_INTERNAL_DNC_CLEAR: "CHK_INTERNAL_DNC_CLEAR",
  CHK_NATIONAL_REGISTRY_SCRUBBED: "CHK_NATIONAL_REGISTRY_SCRUBBED",
  CHK_NATIONAL_REGISTRY_CLEAR: "CHK_NATIONAL_REGISTRY_CLEAR",
  CHK_WITHIN_CALLING_HOURS: "CHK_WITHIN_CALLING_HOURS",
  CHK_FREQUENCY_CAP_24H_OK: "CHK_FREQUENCY_CAP_24H_OK",
  CHK_FREQUENCY_CAP_7D_OK: "CHK_FREQUENCY_CAP_7D_OK",
  CHK_CHANNEL_PREFERENCE_MATCH: "CHK_CHANNEL_PREFERENCE_MATCH",
  CHK_CONSENT_AT_LEAST: "CHK_CONSENT_AT_LEAST",
  CHK_CONTACT_ELIGIBLE: "CHK_CONTACT_ELIGIBLE",
  CHK_SENDER_ID_AND_UNSUB_PRESENT: "CHK_SENDER_ID_AND_UNSUB_PRESENT",
  CHK_BRAZIL_0303_PREFIX: "CHK_BRAZIL_0303_PREFIX",
  CHK_REVOCATION_HANDLING_ENABLED: "CHK_REVOCATION_HANDLING_ENABLED",
  CHK_WIRELESS_MARKETING_CONSENT_OK: "CHK_WIRELESS_MARKETING_CONSENT_OK",
  CHK_AUTOMATION_ALLOWED_FOR_SEND: "CHK_AUTOMATION_ALLOWED_FOR_SEND",
  CHK_END_CALL_WITHIN_10S_ON_REQUEST: "CHK_END_CALL_WITHIN_10S_ON_REQUEST",
  CHK_DNC_DISCLOSURE_AT_CALL_START: "CHK_DNC_DISCLOSURE_AT_CALL_START",
  CHK_CASL_CONSENT_VALID: "CHK_CASL_CONSENT_VALID",
  CHK_CASL_CONSPICUOUS_PUBLICATION_VALID: "CHK_CASL_CONSPICUOUS_PUBLICATION_VALID",
  CHK_LGPD_LAWFUL_BASIS_VALID: "CHK_LGPD_LAWFUL_BASIS_VALID",
  CHK_BRAZIL_NAO_ME_PERTURBE_CLEAR: "CHK_BRAZIL_NAO_ME_PERTURBE_CLEAR",
  CHK_RIGHT_TO_DISCONNECT_OK: "CHK_RIGHT_TO_DISCONNECT_OK"
};
var preCheckIdSchema = z.enum([
  "CHK_INTERNAL_DNC_CLEAR",
  "CHK_NATIONAL_REGISTRY_SCRUBBED",
  "CHK_NATIONAL_REGISTRY_CLEAR",
  "CHK_WITHIN_CALLING_HOURS",
  "CHK_FREQUENCY_CAP_24H_OK",
  "CHK_FREQUENCY_CAP_7D_OK",
  "CHK_CHANNEL_PREFERENCE_MATCH",
  "CHK_CONSENT_AT_LEAST",
  "CHK_CONTACT_ELIGIBLE",
  "CHK_SENDER_ID_AND_UNSUB_PRESENT",
  "CHK_BRAZIL_0303_PREFIX",
  "CHK_REVOCATION_HANDLING_ENABLED",
  "CHK_WIRELESS_MARKETING_CONSENT_OK",
  "CHK_AUTOMATION_ALLOWED_FOR_SEND",
  "CHK_END_CALL_WITHIN_10S_ON_REQUEST",
  "CHK_DNC_DISCLOSURE_AT_CALL_START",
  "CHK_CASL_CONSENT_VALID",
  "CHK_CASL_CONSPICUOUS_PUBLICATION_VALID",
  "CHK_LGPD_LAWFUL_BASIS_VALID",
  "CHK_BRAZIL_NAO_ME_PERTURBE_CLEAR",
  "CHK_RIGHT_TO_DISCONNECT_OK"
]);
var PRE_CONDITION_CODES = {
  COND_SCHEDULE_WITHIN_HOURS: "COND_SCHEDULE_WITHIN_HOURS",
  COND_REGISTRY_SCRUB_REQUIRED: "COND_REGISTRY_SCRUB_REQUIRED",
  COND_INCLUDE_SENDER_ID_AND_UNSUB: "COND_INCLUDE_SENDER_ID_AND_UNSUB",
  COND_RECORD_RETENTION: "COND_RECORD_RETENTION",
  COND_CAPTURE_EVIDENCE_SNAPSHOT: "COND_CAPTURE_EVIDENCE_SNAPSHOT",
  COND_VERIFY_RELEVANCE_RATIONALE: "COND_VERIFY_RELEVANCE_RATIONALE"
};
var preConditionCodeSchema = z.enum([
  "COND_SCHEDULE_WITHIN_HOURS",
  "COND_REGISTRY_SCRUB_REQUIRED",
  "COND_INCLUDE_SENDER_ID_AND_UNSUB",
  "COND_RECORD_RETENTION",
  "COND_CAPTURE_EVIDENCE_SNAPSHOT",
  "COND_VERIFY_RELEVANCE_RATIONALE"
]);
var PRE_REMEDIATION_CODES = {
  REMED_RETRY_AFTER_WINDOW_RESETS: "REMED_RETRY_AFTER_WINDOW_RESETS",
  REMED_REMOVE_FROM_CALL_LIST: "REMED_REMOVE_FROM_CALL_LIST",
  REMED_INTERNAL_DNC_CLEARANCE_REQUIRED: "REMED_INTERNAL_DNC_CLEARANCE_REQUIRED"
};
var preRemediationCodeSchema = z.enum([
  "REMED_RETRY_AFTER_WINDOW_RESETS",
  "REMED_REMOVE_FROM_CALL_LIST",
  "REMED_INTERNAL_DNC_CLEARANCE_REQUIRED"
]);
var PRE_RISK_FLAGS = {
  RISK_UNKNOWN_JURISDICTION: "RISK_UNKNOWN_JURISDICTION",
  RISK_UNKNOWN_LINE_TYPE: "RISK_UNKNOWN_LINE_TYPE",
  RISK_UNKNOWN_NATIONAL_REGISTRY_STATUS: "RISK_UNKNOWN_NATIONAL_REGISTRY_STATUS",
  RISK_UNKNOWN_INTERNAL_DNC_STATUS: "RISK_UNKNOWN_INTERNAL_DNC_STATUS",
  RISK_STATE_ASSUMED_BY_AREA_CODE: "RISK_STATE_ASSUMED_BY_AREA_CODE",
  RISK_CHANNEL_FALLBACK_TO_SMS: "RISK_CHANNEL_FALLBACK_TO_SMS",
  RISK_SMS_B2B_NORMALIZED_TO_B2C: "RISK_SMS_B2B_NORMALIZED_TO_B2C",
  /** Recipient timezone could not be determined from contact data, phone, or
   *  the calling-hour window's own timezone. When a rule requires
   *  CHK_WITHIN_CALLING_HOURS this drives a fail-closed BLOCK. */
  RISK_NO_TIMEZONE: "RISK_NO_TIMEZONE",
  /** Recipient timezone was derived from a non-explicit signal (phone area
   *  code, state, country, or window). Description carries the source. */
  RISK_TIMEZONE_DERIVED: "RISK_TIMEZONE_DERIVED"
};
var preRiskFlagSchema = z.enum([
  "RISK_UNKNOWN_JURISDICTION",
  "RISK_UNKNOWN_LINE_TYPE",
  "RISK_UNKNOWN_NATIONAL_REGISTRY_STATUS",
  "RISK_UNKNOWN_INTERNAL_DNC_STATUS",
  "RISK_STATE_ASSUMED_BY_AREA_CODE",
  "RISK_CHANNEL_FALLBACK_TO_SMS",
  "RISK_SMS_B2B_NORMALIZED_TO_B2C",
  "RISK_NO_TIMEZONE",
  "RISK_TIMEZONE_DERIVED"
]);
var PRE_RISK_FLAG_LABELS = {
  RISK_UNKNOWN_JURISDICTION: "Unknown Jurisdiction",
  RISK_UNKNOWN_LINE_TYPE: "Unknown Line Type",
  RISK_UNKNOWN_NATIONAL_REGISTRY_STATUS: "Unknown Registry Status",
  RISK_UNKNOWN_INTERNAL_DNC_STATUS: "Unknown DNC Status",
  RISK_STATE_ASSUMED_BY_AREA_CODE: "State Inferred from Area Code",
  RISK_CHANNEL_FALLBACK_TO_SMS: "Channel Fell Back to SMS",
  RISK_SMS_B2B_NORMALIZED_TO_B2C: "SMS B2B Treated as B2C (TCPA)",
  RISK_NO_TIMEZONE: "Recipient Timezone Unknown",
  RISK_TIMEZONE_DERIVED: "Recipient Timezone Inferred"
};
var PRE_CONDITION_LABELS = {
  COND_SCHEDULE_WITHIN_HOURS: "Schedule Within Calling Hours",
  COND_REGISTRY_SCRUB_REQUIRED: "National DNC Scrub Required",
  COND_INCLUDE_SENDER_ID_AND_UNSUB: "Include Sender ID, Postal Address, and Unsubscribe",
  COND_RECORD_RETENTION: "Retain Consent Evidence",
  COND_CAPTURE_EVIDENCE_SNAPSHOT: "Capture Evidence Snapshot",
  COND_VERIFY_RELEVANCE_RATIONALE: "Verify Message Relevance to Recipient"
};
var PRE_REMEDIATION_LABELS = {
  REMED_RETRY_AFTER_WINDOW_RESETS: "Retry After Window Resets",
  REMED_REMOVE_FROM_CALL_LIST: "Remove From Call List",
  REMED_INTERNAL_DNC_CLEARANCE_REQUIRED: "Internal DNC Clearance Required"
};
var PRE_DECISION_LABELS = {
  ALLOW: "Allow",
  ALLOW_WITH_CONDITIONS: "Conditional",
  BLOCK: "Blocked"
};
var PRE_DECISION_DESCRIPTIONS = {
  ALLOW: "This send is compliant and was delivered.",
  ALLOW_WITH_CONDITIONS: "This send was allowed but required additional conditions to stay compliant.",
  BLOCK: "This send was blocked because it would violate one or more policy rules."
};
var PRE_REMEDIATION_DESCRIPTIONS = {
  REMED_RETRY_AFTER_WINDOW_RESETS: "These sends will retry automatically when the calling-hours window resets.",
  REMED_REMOVE_FROM_CALL_LIST: "Remove these contacts from your call list \u2014 they cannot be contacted.",
  REMED_INTERNAL_DNC_CLEARANCE_REQUIRED: "These contacts are on your internal Do-Not-Contact list and need clearance before sending."
};
var consentRecordSchema = z.object({
  status: consentStatusSchema,
  capturedAt: z.coerce.date().optional().nullable(),
  source: z.string().optional().nullable(),
  scope: z.string().optional().nullable(),
  proof: z.string().optional().nullable(),
  agreementArtifactRef: z.string().optional().nullable(),
  disclosureTextSnapshot: z.string().optional().nullable(),
  captureContext: z.string().optional().nullable(),
  // EBR fields (issue #757 §1 + §6a). Required when status === 'IMPLICIT_OPT_IN'.
  ebrEventType: ebrEventTypeSchema.optional().nullable(),
  ebrEventAt: z.coerce.date().optional().nullable(),
  expiresAt: z.coerce.date().optional().nullable(),
  sourceUrl: z.string().optional().nullable()
});
var eligibilityFlagsSchema = z.object({
  informationalSms: z.boolean(),
  informationalEmail: z.boolean(),
  informationalVoice: z.boolean(),
  marketingSms: z.boolean(),
  marketingEmail: z.boolean(),
  marketingVoice: z.boolean()
});
var jurisdictionSchema = z.object({
  id: z.string().uuid(),
  code: z.string().min(1),
  name: z.string().min(1),
  country: z.string().min(1),
  subdivisionType: subdivisionTypeSchema,
  parentCode: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  isSystem: z.boolean().default(true),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createJurisdictionSchema = jurisdictionSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
var updateJurisdictionSchema = createJurisdictionSchema.partial();
var ruleRequiredCheckSchema = z.object({
  checkId: z.string().min(1),
  required: z.boolean(),
  params: z.record(z.unknown()).optional().nullable()
});
var ruleSchema = z.object({
  id: z.string().uuid(),
  ruleId: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  jurisdictionCode: z.string().min(1),
  layer: ruleLayerSchema,
  channel: preChannelSchema.optional().nullable(),
  messagePurpose: messagePurposeSchema.optional().nullable(),
  b2bOrB2c: b2bOrB2cSchema.optional().nullable(),
  tenantIndustry: z.string().optional().nullable(),
  requiredChecks: z.array(ruleRequiredCheckSchema),
  effect: ruleEffectSchema,
  remediation: z.string().optional().nullable(),
  sourceRef: z.string().optional().nullable(),
  /** Plain-language override of the per-check rendered bullets shown on the
   *  tenant Policies page. When null/empty, the renderer falls back to per-check
   *  userFacingTemplate values. */
  userFacingSummary: z.string().optional().nullable(),
  /** Short citation displayed under the rule's bullets on the tenant Policies
   *  page. Falls back to sourceRef when null. */
  userFacingCitation: z.string().optional().nullable(),
  priority: z.number().int().min(0),
  isActive: z.boolean().default(true),
  isOverridable: z.boolean().default(false),
  isSystem: z.boolean().default(true),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createRuleSchema = ruleSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
var updateRuleSchema = createRuleSchema.partial();
var callingHourWindowSchema = z.object({
  id: z.string().uuid(),
  windowId: z.string().min(1),
  jurisdictionCode: z.string().min(1),
  name: z.string().min(1),
  channel: preChannelSchema.optional().nullable(),
  messagePurpose: messagePurposeSchema.optional().nullable(),
  startHour: z.number().int().min(0).max(23),
  startMinute: z.number().int().min(0).max(59),
  endHour: z.number().int().min(0).max(23),
  endMinute: z.number().int().min(0).max(59),
  daysOfWeek: z.array(z.number().int().min(0).max(6)),
  timezone: z.string(),
  sourceRef: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createCallingHourWindowSchema = callingHourWindowSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
var updateCallingHourWindowSchema = createCallingHourWindowSchema.partial();
var dncRegistrySchema = z.object({
  id: z.string().uuid(),
  registryId: z.string().min(1),
  name: z.string().min(1),
  country: z.string().min(1),
  jurisdictionCode: z.string().min(1),
  registryType: registryTypeSchema,
  scrubCadenceDays: z.number().int().min(1),
  channel: preChannelSchema.optional().nullable(),
  description: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createDncRegistrySchema = dncRegistrySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
var updateDncRegistrySchema = createDncRegistrySchema.partial();
var frequencyCapConfigSchema = z.object({
  id: z.string().uuid(),
  jurisdictionCode: z.string().min(1),
  name: z.string().min(1),
  channel: preChannelSchema.optional().nullable(),
  messagePurpose: messagePurposeSchema.optional().nullable(),
  maxAttempts: z.number().int().min(1),
  periodHours: z.number().int().min(1),
  crossChannel: z.boolean().default(false),
  sourceRef: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createFrequencyCapConfigSchema = frequencyCapConfigSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
var updateFrequencyCapConfigSchema = createFrequencyCapConfigSchema.partial();
var preRecipientSchema = z.object({
  addressing: z.object({
    phone: z.string().optional().nullable(),
    email: z.string().optional().nullable()
  }),
  country: z.string().optional().nullable(),
  usAreaCode: z.string().optional().nullable(),
  b2bOrB2c: b2bOrB2cSchema.optional().nullable()
});
var preContactFrequencySchema = z.object({
  attemptsLast24hSameSubject: z.number().int().min(0),
  contactsLast7dSameSubjectAnyChannel: z.number().int().min(0),
  preferredChannel: preChannelSchema.optional().nullable()
});
var preInputSchema = z.object({
  recipient: preRecipientSchema,
  channel: preChannelSchema,
  messagePurpose: messagePurposeSchema,
  contactEligibility: eligibilityFlagsSchema,
  // Per-cell consent status keyed by `${channel}_${purpose}` (e.g. "SMS_MARKETING").
  // Sourced from ConsentRecord.status after IMPLICIT_OPT_IN expiry downgrade.
  // CHK_CONSENT_AT_LEAST consults this so that a rule declaring
  // `minConsent: 'WRITTEN_EXPRESS_OPTIN'` actually requires written consent
  // (the eligibility-flag proxy collapses WRITTEN and non-written into a single
  // boolean and cannot distinguish the two). Optional for back-compat; when
  // absent, the engine falls back to the eligibility-flag proxy.
  contactConsentStatuses: z.record(consentStatusSchema).optional(),
  internalDncStatus: dncStatusSchema,
  nationalRegistryStatus: dncStatusSchema,
  // Per-registry status map keyed by registryId (e.g. 'MX_REPEP', 'MX_REUS', 'CA_DNCL').
  // When present, registry-specific checks consult this; otherwise they fall back to
  // the aggregate `nationalRegistryStatus` above.
  nationalRegistryStatuses: z.record(dncStatusSchema).optional(),
  // Per-registry last-checked timestamp keyed by registryId (e.g. 'US_DNC').
  // Sourced from ContactDNCStatus.lastCheckedAt. When a rule declares
  // `maxAgeDays` (e.g. PRE-US-DNC-001 requires a scrub within 31 days),
  // CHK_NATIONAL_REGISTRY_SCRUBBED enforces freshness against this map and
  // fails-closed if the scrub is missing or stale.
  nationalRegistryCheckedAt: z.record(z.coerce.date().nullable()).optional(),
  // Tenant industry — used to gate sector-specific rules like Mexico REUS (financial sector).
  // Matches `Tenant.industry` (e.g. 'FINANCIAL_SERVICES').
  tenantIndustry: z.string().optional().nullable(),
  // Wall-clock time at the recipient (Date constructed so .getHours/.getDay etc.
  // return the recipient's local time). Nullable when the recipient's timezone
  // could not be resolved from contact data, phone, or window TZ — rules that
  // require CHK_WITHIN_CALLING_HOURS will fail-closed in that case.
  localTimeAtRecipient: z.coerce.date().nullable(),
  contactFrequency: preContactFrequencySchema,
  tenantId: z.string().uuid(),
  campaignId: z.string().uuid().optional().nullable(),
  // Tenant-level compliance attestations. Populated by the delivery worker
  // and the /api/pre/evaluate route. Used by CHK_REVOCATION_HANDLING_ENABLED
  // (the tenant has STOP/unsubscribe handlers wired live). Optional for
  // back-compat — absent → check fails closed.
  tenantConfig: z.object({
    smsStopHandlerEnabled: z.boolean(),
    emailUnsubHandlerEnabled: z.boolean()
  }).optional(),
  // Email-template compliance signals. Populated by the email delivery
  // worker after the template is rendered for the recipient. Used by
  // CHK_SENDER_ID_AND_UNSUB_PRESENT (CAN-SPAM). Optional — absent →
  // check fails closed.
  emailTemplate: z.object({
    hasUnsubscribeLink: z.boolean(),
    hasPhysicalPostalAddress: z.boolean(),
    hasIdentifiableFromAddress: z.boolean(),
    unsubscribeHonorWindowBusinessDays: z.number().int().min(1).max(30)
  }).optional(),
  // Recipient phone line type for TCPA wireless gating. Populated from
  // Contact.lineType (written by the line-type-lookup worker). Used by
  // CHK_WIRELESS_MARKETING_CONSENT_OK: MOBILE or UNKNOWN require the
  // rule's wireless min consent; LANDLINE/VOIP fall back to the channel's
  // normal consent gate.
  recipientLineType: z.enum(["MOBILE", "LANDLINE", "VOIP", "UNKNOWN"]).optional()
});
var preTraceEntrySchema = z.object({
  ruleId: z.string(),
  ruleName: z.string(),
  result: z.enum(["PASS", "FAIL", "SKIP"]),
  sourceRef: z.string().optional().nullable()
});
var preConditionOutputSchema = z.object({
  code: z.string(),
  params: z.record(z.unknown()).optional().nullable(),
  description: z.string().optional().nullable()
});
var preRemediationOutputSchema = z.object({
  code: z.string(),
  params: z.record(z.unknown()).optional().nullable(),
  description: z.string().optional().nullable()
});
var preRiskFlagOutputSchema = z.object({
  code: z.string(),
  description: z.string().optional().nullable()
});
var preConsentCaptureTraceEntrySchema = z.object({
  ruleId: z.string(),
  ruleName: z.string(),
  jurisdictionCode: z.string(),
  effect: z.enum(["ALLOW", "BLOCK"]),
  result: z.enum(["PASS", "SKIP"]),
  sourceRef: z.string().optional().nullable()
});
var preOutputSchema = z.object({
  decision: preDecisionSchema,
  eligibility: eligibilityStatusSchema,
  conditions: z.array(preConditionOutputSchema),
  riskFlags: z.array(preRiskFlagOutputSchema),
  remediation: z.array(preRemediationOutputSchema),
  trace: z.array(preTraceEntrySchema),
  // Layer-0 consent-capture rules evaluated for this (channel × purpose) cell.
  // Reflects what the Super Admin "Opt-in Requirements" section displays; the
  // winner's effect (ALLOW/BLOCK) gates initial informational auto-grant at
  // contact creation. Omitted on engines that haven't been updated.
  consentCapture: z.array(preConsentCaptureTraceEntrySchema).optional(),
  evaluatedAt: z.coerce.date()
});
var preDecisionLogSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  contactId: z.string().optional().nullable(),
  campaignId: z.string().optional().nullable(),
  channel: preChannelSchema,
  messagePurpose: messagePurposeSchema,
  jurisdictionCode: z.string(),
  decision: preDecisionSchema,
  eligibility: eligibilityStatusSchema,
  conditionCount: z.number().int().min(0),
  riskFlagCount: z.number().int().min(0),
  input: preInputSchema,
  output: preOutputSchema,
  internalDncSuppressed: z.boolean().optional(),
  internalDncReason: z.string().optional().nullable(),
  createdAt: z.coerce.date()
});
var preDecisionLogFiltersSchema = z.object({
  search: z.string().optional(),
  tenantId: z.string().uuid().optional(),
  channel: preChannelSchema.optional(),
  messagePurpose: messagePurposeSchema.optional(),
  decision: preDecisionSchema.optional(),
  dateRange: z.enum(["all", "24h", "7days", "30days", "90days"]).optional(),
  // True → only decisions where the SMS provider call was suppressed by Internal DNC.
  // Accepts string "true"/"false" from query params or boolean from JSON.
  internalDncOnly: z.union([z.boolean(), z.literal("true"), z.literal("false")]).transform((v) => v === true || v === "true").optional()
});
var preAiClassificationSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  messageContent: z.string(),
  classifiedPurpose: messagePurposeSchema,
  confidence: z.number().min(0).max(1),
  reasoning: z.string().optional().nullable(),
  isFlagged: z.boolean(),
  overriddenPurpose: messagePurposeSchema.optional().nullable(),
  overriddenBy: z.string().uuid().optional().nullable(),
  overriddenAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date()
});
var preAiClassificationFiltersSchema = z.object({
  search: z.string().optional(),
  tenantId: z.string().uuid().optional(),
  classifiedPurpose: messagePurposeSchema.optional(),
  isFlagged: z.boolean().optional(),
  dateRange: z.enum(["all", "24h", "7days", "30days", "90days"]).optional()
});
var ruleTypeSchema = z.enum(["OPT_IN", "OPT_OUT", "SENDING"]);
var contactOrSendSchema = ruleTypeSchema;
var preCheckDefinitionSchema = z.object({
  /** DB-generated UUID. Optional because the static catalogue arrays defined
   *  in app code don't carry an id; the API populates it. */
  id: z.string().uuid().optional(),
  checkId: z.string(),
  name: z.string(),
  description: z.string(),
  paramSchema: z.record(z.string()).optional().nullable(),
  /** Plain-language sentence rendered on the Policies page. Supports
   *  {paramName} placeholders substituted from per-check params at display
   *  time. */
  userFacingTemplate: z.string(),
  /** Which section the bullet renders in (Opt-in / Opt-out / Sending). */
  ruleType: ruleTypeSchema
});
var createPreCheckDefinitionSchema = preCheckDefinitionSchema.omit({ id: true });
var updatePreCheckDefinitionSchema = createPreCheckDefinitionSchema.partial();
var PRE_BUNDLE_SCHEMA_VERSION = 1;
var PRE_BUNDLE_CANONICALIZATION_VERSION = 1;
var bundleJurisdictionSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  country: z.string().min(1),
  subdivisionType: subdivisionTypeSchema,
  parentCode: z.string().nullable(),
  isActive: z.boolean(),
  isSystem: z.boolean()
}).strict();
var bundleCheckDefinitionSchema = z.object({
  checkId: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  paramSchema: z.record(z.string()).nullable(),
  userFacingTemplate: z.string(),
  ruleType: ruleTypeSchema,
  isSystem: z.boolean()
}).strict();
var bundleRuleSchema = z.object({
  ruleId: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  jurisdictionCode: z.string().min(1),
  layer: ruleLayerSchema,
  channel: preChannelSchema.nullable(),
  messagePurpose: messagePurposeSchema.nullable(),
  b2bOrB2c: b2bOrB2cSchema.nullable(),
  tenantIndustry: z.string().nullable(),
  requiredChecks: z.array(ruleRequiredCheckSchema),
  effect: ruleEffectSchema,
  remediation: z.string().nullable(),
  sourceRef: z.string().nullable(),
  userFacingSummary: z.string().nullable(),
  userFacingCitation: z.string().nullable(),
  priority: z.number().int().min(0),
  isActive: z.boolean(),
  isOverridable: z.boolean(),
  isSystem: z.boolean()
}).strict();
var bundleCallingHourWindowSchema = z.object({
  windowId: z.string().min(1),
  jurisdictionCode: z.string().min(1),
  name: z.string().min(1),
  channel: preChannelSchema.nullable(),
  messagePurpose: messagePurposeSchema.nullable(),
  startHour: z.number().int().min(0).max(23),
  startMinute: z.number().int().min(0).max(59),
  endHour: z.number().int().min(0).max(23),
  endMinute: z.number().int().min(0).max(59),
  daysOfWeek: z.array(z.number().int().min(0).max(6)),
  timezone: z.string().min(1),
  sourceRef: z.string().nullable(),
  isActive: z.boolean()
}).strict();
var bundleDncRegistrySchema = z.object({
  registryId: z.string().min(1),
  name: z.string().min(1),
  country: z.string().min(1),
  jurisdictionCode: z.string().min(1),
  registryType: registryTypeSchema,
  scrubCadenceDays: z.number().int().min(1),
  channel: preChannelSchema.nullable(),
  description: z.string().nullable(),
  isActive: z.boolean()
}).strict();
var bundleFrequencyCapSchema = z.object({
  jurisdictionCode: z.string().min(1),
  name: z.string().min(1),
  channel: preChannelSchema.nullable(),
  messagePurpose: messagePurposeSchema.nullable(),
  maxAttempts: z.number().int().min(1),
  periodHours: z.number().int().min(1),
  crossChannel: z.boolean(),
  sourceRef: z.string().nullable(),
  isActive: z.boolean()
}).strict();
var bundleFeatureFlagDefaultSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
  defaultEnabled: z.boolean()
}).strict();
var preBundleMetadataSchema = z.object({
  /** Stable id of the bundle generation event. */
  bundleId: z.string().uuid(),
  /** ISO-8601 UTC timestamp (with Z suffix). */
  generatedAt: z.string().datetime({ offset: false }),
  /** Super-admin id who triggered the export. */
  generatedBy: z.string().min(1),
  /** Optional human label (e.g. email) for the actor. */
  generatedByLabel: z.string().nullable(),
  /** Source environment of the export ('dev' | 'staging' | 'prod' | 'unknown'). */
  sourceEnv: z.enum(["dev", "staging", "prod", "unknown"]),
  /** Application version, if available (from package.json or env). */
  appVersion: z.string().nullable(),
  /** Git SHA, if available (from env CI_COMMIT_SHA / GIT_SHA). */
  gitSha: z.string().nullable(),
  /** Free-text description supplied by the operator at export time. */
  description: z.string().nullable(),
  /** Optional release tag (e.g. 'v2.4.0-rc1') for golden promotion. */
  releaseTag: z.string().nullable(),
  /** Parent bundle id this was derived from (for promotion lineage). */
  parentBundleId: z.string().uuid().nullable()
}).strict();
var preBundleIntegritySchema = z.object({
  algorithm: z.literal("sha256"),
  /** Hex-encoded SHA-256 of the canonical payload (bundle minus the
   * `integrity.checksum` field). Required. */
  checksum: z.string().regex(/^[a-f0-9]{64}$/),
  /** Optional reference to a detached signature file (e.g. '*.json.sig'). */
  signatureRef: z.string().nullable(),
  /** Canonicalization algorithm version used to compute `checksum`. */
  canonicalizationVersion: z.literal(PRE_BUNDLE_CANONICALIZATION_VERSION)
}).strict();
var preBundlePayloadSchema = z.object({
  jurisdictions: z.array(bundleJurisdictionSchema),
  checkDefinitions: z.array(bundleCheckDefinitionSchema),
  rules: z.array(bundleRuleSchema),
  callingHourWindows: z.array(bundleCallingHourWindowSchema),
  dncRegistries: z.array(bundleDncRegistrySchema),
  frequencyCaps: z.array(bundleFrequencyCapSchema),
  featureFlagDefaults: z.array(bundleFeatureFlagDefaultSchema)
}).strict();
var preBundleSchema = z.object({
  bundleSchemaVersion: z.literal(PRE_BUNDLE_SCHEMA_VERSION),
  metadata: preBundleMetadataSchema,
  integrity: preBundleIntegritySchema,
  payload: preBundlePayloadSchema
}).strict();
var ndfVersionSchema = z.literal("1.0");
var ndfKindSchema = z.enum(["document", "form", "hybrid", "sms", "email"]);
var textStyleSchema = z.object({
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  align: z.enum(["left", "center", "right"]).optional(),
  fontSize: z.number().positive().optional()
});
var normalizedPositionSchema = z.object({
  page: z.number().int().positive(),
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  width: z.number().min(0).max(1),
  height: z.number().min(0).max(1)
});
var ndfFieldValidationSchema = z.object({
  required: z.boolean().optional(),
  minLength: z.number().int().optional(),
  maxLength: z.number().int().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  pattern: z.string().optional(),
  customMessage: z.string().optional()
});
var ndfConditionalLogicSchema = z.object({
  fieldId: z.string(),
  operator: z.enum(["equals", "notEquals", "contains", "notContains"]),
  value: z.unknown()
});
var ndfSignerSchema = z.object({
  id: z.string(),
  name: z.string(),
  order: z.number().int().positive()
});
var ndfVariableDefinitionSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z.enum(["text", "date", "number", "email", "phone"]),
  defaultValue: z.string().optional()
});
var ndfDocumentMetaSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  category: z.string().optional(),
  variables: z.array(ndfVariableDefinitionSchema).optional(),
  layout: z.enum(["single", "two-column"]).optional()
});
var ndfPdfSourceSchema = z.object({
  fileUrl: z.string(),
  originalName: z.string(),
  fileSize: z.number().int().nonnegative(),
  pageCount: z.number().int().positive()
});
var ndfFormBrandingSchema = z.object({
  logoUrl: z.string().nullable().optional(),
  primaryColor: z.string().optional().default("#3b82f6"),
  logoPosition: z.enum(["left", "center", "right"]).optional().default("center"),
  inputBorderRadius: z.number().int().min(0).max(20).optional().default(8),
  inputBorderColor: z.string().nullable().optional(),
  inputBackgroundColor: z.string().nullable().optional(),
  inputTextColor: z.string().nullable().optional()
});
var ndfFormSettingsSchema = z.object({
  submitButtonText: z.string().optional(),
  successMessage: z.string().optional(),
  redirectUrl: z.string().optional(),
  notifyOnSubmission: z.boolean().optional(),
  notificationEmails: z.array(z.string().email()).optional(),
  webhookUrl: z.string().optional(),
  captureEmailConsent: z.boolean().optional(),
  captureSmsConsent: z.boolean().optional(),
  consentText: z.string().optional(),
  allowMultipleSubmissions: z.boolean().optional(),
  maxSubmissions: z.number().int().positive().optional(),
  requireAuth: z.boolean().optional(),
  // Branding settings
  branding: ndfFormBrandingSchema.optional()
});
var paragraphBlockSchema = z.object({
  type: z.literal("paragraph"),
  id: z.string(),
  content: z.string(),
  style: textStyleSchema.optional()
});
var headingBlockSchema = z.object({
  type: z.literal("heading"),
  id: z.string(),
  level: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  content: z.string()
});
var listBlockSchema = z.object({
  type: z.literal("list"),
  id: z.string(),
  ordered: z.boolean(),
  items: z.array(z.string())
});
var imageBlockSchema = z.object({
  type: z.literal("image"),
  id: z.string(),
  src: z.string(),
  alt: z.string().optional(),
  width: z.number().positive().optional(),
  height: z.number().positive().optional()
});
var tableBlockSchema = z.object({
  type: z.literal("table"),
  id: z.string(),
  columns: z.array(z.object({
    header: z.string(),
    width: z.number().positive().optional()
  })),
  rows: z.array(z.array(z.string()))
});
var dividerBlockSchema = z.object({
  type: z.literal("divider"),
  id: z.string()
});
var spacerBlockSchema = z.object({
  type: z.literal("spacer"),
  id: z.string(),
  height: z.number().positive().optional()
});
var sectionHeaderBlockSchema = z.object({
  type: z.literal("section_header"),
  id: z.string(),
  content: z.string()
});
var pageBreakBlockSchema = z.object({
  type: z.literal("page_break"),
  id: z.string()
});
var baseFieldBlockSchema = z.object({
  id: z.string(),
  label: z.string(),
  placeholder: z.string().optional(),
  helpText: z.string().optional(),
  defaultValue: z.unknown().optional(),
  validation: ndfFieldValidationSchema.optional(),
  conditionalLogic: ndfConditionalLogicSchema.optional(),
  signerId: z.string().optional()
});
var textFieldBlockSchema = baseFieldBlockSchema.extend({
  type: z.literal("field_text"),
  inputType: z.enum(["text", "textarea", "email", "phone", "number"])
});
var dateFieldBlockSchema = baseFieldBlockSchema.extend({
  type: z.literal("field_date")
});
var timeFieldBlockSchema = baseFieldBlockSchema.extend({
  type: z.literal("field_time")
});
var selectFieldBlockSchema = baseFieldBlockSchema.extend({
  type: z.literal("field_select"),
  selectType: z.enum(["dropdown", "radio", "checkbox"]),
  options: z.array(z.object({
    label: z.string(),
    value: z.string()
  }))
});
var fileFieldBlockSchema = baseFieldBlockSchema.extend({
  type: z.literal("field_file"),
  accept: z.array(z.string()).optional(),
  maxSize: z.number().int().positive().optional()
});
var signatureFieldBlockSchema = baseFieldBlockSchema.extend({
  type: z.literal("field_signature"),
  required: z.boolean(),
  position: normalizedPositionSchema.optional()
});
var pdfOverlayFieldBlockSchema = baseFieldBlockSchema.extend({
  type: z.literal("pdf_overlay"),
  overlayType: z.enum(["signature", "initials", "text", "date", "checkbox"]),
  position: normalizedPositionSchema,
  variableName: z.string().optional()
});
var ndfBlockSchema = z.discriminatedUnion("type", [
  // Content blocks
  paragraphBlockSchema,
  headingBlockSchema,
  listBlockSchema,
  imageBlockSchema,
  tableBlockSchema,
  dividerBlockSchema,
  spacerBlockSchema,
  sectionHeaderBlockSchema,
  pageBreakBlockSchema,
  // Field blocks
  textFieldBlockSchema,
  dateFieldBlockSchema,
  timeFieldBlockSchema,
  selectFieldBlockSchema,
  fileFieldBlockSchema,
  signatureFieldBlockSchema,
  // PDF overlay block
  pdfOverlayFieldBlockSchema
]);
var nexusDocumentSchema = z.object({
  version: ndfVersionSchema,
  kind: ndfKindSchema,
  meta: ndfDocumentMetaSchema,
  blocks: z.array(ndfBlockSchema),
  settings: ndfFormSettingsSchema.optional(),
  pdf: ndfPdfSourceSchema.optional(),
  signers: z.array(ndfSignerSchema).optional()
});
var NDF_CONTENT_BLOCK_TYPES = [
  "paragraph",
  "heading",
  "list",
  "image",
  "table",
  "divider",
  "spacer",
  "section_header",
  "page_break"
];
var NDF_FIELD_BLOCK_TYPES = [
  "field_text",
  "field_date",
  "field_time",
  "field_select",
  "field_file",
  "field_signature"
];
var NDF_BLOCK_TYPES = [
  ...NDF_CONTENT_BLOCK_TYPES,
  ...NDF_FIELD_BLOCK_TYPES,
  "pdf_overlay"
];
function isContentBlock(block) {
  return NDF_CONTENT_BLOCK_TYPES.includes(block.type);
}
function isFieldBlock(block) {
  return NDF_FIELD_BLOCK_TYPES.includes(block.type);
}
function isPdfOverlayBlock(block) {
  return block.type === "pdf_overlay";
}
function getFieldBlocks(doc) {
  return doc.blocks.filter(isFieldBlock);
}
function getSignatureBlocks(doc) {
  return doc.blocks.filter(
    (b) => b.type === "field_signature" || b.type === "pdf_overlay" && b.overlayType === "signature"
  );
}
function extractVariableReferences(doc) {
  const variablePattern = /\{\{([^}]+)\}\}/g;
  const variables = /* @__PURE__ */ new Set();
  for (const block of doc.blocks) {
    let text = "";
    if ("content" in block && typeof block.content === "string") {
      text = block.content;
    }
    if ("items" in block && Array.isArray(block.items)) {
      text += " " + block.items.join(" ");
    }
    if ("rows" in block && Array.isArray(block.rows)) {
      text += " " + block.rows.flat().join(" ");
    }
    if ("src" in block && typeof block.src === "string") {
      text += " " + block.src;
    }
    for (const match of text.matchAll(variablePattern)) {
      const varName = match[1]?.trim();
      if (varName) variables.add(varName);
    }
  }
  return Array.from(variables);
}

// src/ndf-converters.ts
function formSchemaToNdf(schema, settings, name, description, branding) {
  const blocks = schema.fields.map((field) => formFieldToNdfBlock(field));
  const ndfSettings = {
    submitButtonText: settings.submitButtonText,
    successMessage: settings.successMessage,
    redirectUrl: settings.redirectUrl,
    notifyOnSubmission: settings.notifyOnSubmission,
    notificationEmails: settings.notificationEmails,
    webhookUrl: settings.webhookUrl,
    captureEmailConsent: settings.captureEmailConsent,
    captureSmsConsent: settings.captureSmsConsent,
    consentText: settings.consentText,
    allowMultipleSubmissions: settings.allowMultipleSubmissions,
    maxSubmissions: settings.maxSubmissions,
    requireAuth: settings.requireAuth,
    branding
  };
  const cleanSettings = JSON.parse(JSON.stringify(ndfSettings));
  return {
    version: "1.0",
    kind: "form",
    meta: {
      title: name,
      description,
      layout: schema.layout || "single"
    },
    blocks,
    settings: cleanSettings
  };
}
function formFieldToNdfBlock(field) {
  const baseProps = {
    id: field.id,
    label: field.label,
    placeholder: field.placeholder,
    helpText: field.helpText,
    defaultValue: field.defaultValue,
    validation: field.validation ? {
      required: field.validation.required,
      minLength: field.validation.minLength,
      maxLength: field.validation.maxLength,
      min: field.validation.min,
      max: field.validation.max,
      pattern: field.validation.pattern,
      customMessage: field.validation.customMessage
    } : void 0,
    conditionalLogic: field.conditionalLogic ? {
      fieldId: field.conditionalLogic.fieldId,
      operator: field.conditionalLogic.operator,
      value: field.conditionalLogic.value
    } : void 0
  };
  const clean = (obj) => JSON.parse(JSON.stringify(obj));
  switch (field.type) {
    // Text-based fields → field_text
    case "text":
    case "textarea":
    case "email":
    case "phone":
    case "number":
      return clean({ ...baseProps, type: "field_text", inputType: field.type });
    // Date/Time → field_date / field_time
    case "date":
      return clean({ ...baseProps, type: "field_date" });
    case "time":
      return clean({ ...baseProps, type: "field_time" });
    // Selection fields → field_select
    case "dropdown":
    case "radio":
    case "checkbox":
      return clean({
        ...baseProps,
        type: "field_select",
        selectType: field.type,
        options: field.options || []
      });
    // File → field_file
    case "file":
      return clean({ ...baseProps, type: "field_file" });
    // Signature → field_signature
    case "signature":
      return clean({
        ...baseProps,
        type: "field_signature",
        required: field.validation?.required ?? false
      });
    // Layout/decorative fields → content blocks
    case "section_header":
      return { type: "section_header", id: field.id, content: field.label };
    case "divider":
      return { type: "divider", id: field.id };
    case "spacer":
      return { type: "spacer", id: field.id };
    default:
      return clean({ ...baseProps, type: "field_text", inputType: "text" });
  }
}
function ndfToFormSchema(doc) {
  const fields = doc.blocks.map((block) => ndfBlockToFormField(block)).filter((f) => f !== null);
  const schema = {
    fields,
    layout: doc.meta.layout || "single"
  };
  const s = doc.settings || {};
  const settings = {
    submitButtonText: s.submitButtonText ?? "Submit",
    successMessage: s.successMessage ?? "Thank you for your submission!",
    redirectUrl: s.redirectUrl,
    notifyOnSubmission: s.notifyOnSubmission ?? true,
    notificationEmails: s.notificationEmails,
    webhookUrl: s.webhookUrl,
    captureEmailConsent: s.captureEmailConsent ?? false,
    captureSmsConsent: s.captureSmsConsent ?? false,
    consentText: s.consentText,
    allowMultipleSubmissions: s.allowMultipleSubmissions ?? true,
    maxSubmissions: s.maxSubmissions,
    requireAuth: s.requireAuth ?? false
  };
  const branding = s.branding ? {
    logoUrl: s.branding.logoUrl ?? null,
    primaryColor: s.branding.primaryColor ?? "#3b82f6",
    logoPosition: s.branding.logoPosition ?? "center",
    inputBorderRadius: s.branding.inputBorderRadius ?? 8,
    inputBorderColor: s.branding.inputBorderColor ?? null,
    inputBackgroundColor: s.branding.inputBackgroundColor ?? null,
    inputTextColor: s.branding.inputTextColor ?? null
  } : void 0;
  return { schema, settings, branding };
}
function ndfBlockToFormField(block) {
  switch (block.type) {
    case "field_text":
      return {
        id: block.id,
        type: block.inputType,
        label: block.label,
        placeholder: block.placeholder,
        helpText: block.helpText,
        defaultValue: block.defaultValue,
        validation: block.validation ? { ...block.validation, required: block.validation.required ?? false } : void 0,
        conditionalLogic: block.conditionalLogic
      };
    case "field_date":
      return {
        id: block.id,
        type: "date",
        label: block.label,
        placeholder: block.placeholder,
        helpText: block.helpText,
        defaultValue: block.defaultValue,
        validation: block.validation ? { ...block.validation, required: block.validation.required ?? false } : void 0,
        conditionalLogic: block.conditionalLogic
      };
    case "field_time":
      return {
        id: block.id,
        type: "time",
        label: block.label,
        placeholder: block.placeholder,
        helpText: block.helpText,
        defaultValue: block.defaultValue,
        validation: block.validation ? { ...block.validation, required: block.validation.required ?? false } : void 0,
        conditionalLogic: block.conditionalLogic
      };
    case "field_select":
      return {
        id: block.id,
        type: block.selectType,
        label: block.label,
        placeholder: block.placeholder,
        helpText: block.helpText,
        defaultValue: block.defaultValue,
        options: block.options,
        validation: block.validation ? { ...block.validation, required: block.validation.required ?? false } : void 0,
        conditionalLogic: block.conditionalLogic
      };
    case "field_file":
      return {
        id: block.id,
        type: "file",
        label: block.label,
        placeholder: block.placeholder,
        helpText: block.helpText,
        defaultValue: block.defaultValue,
        validation: block.validation ? { ...block.validation, required: block.validation.required ?? false } : void 0,
        conditionalLogic: block.conditionalLogic
      };
    case "field_signature":
      return {
        id: block.id,
        type: "signature",
        label: block.label,
        validation: { required: block.required },
        conditionalLogic: block.conditionalLogic
      };
    case "section_header":
      return {
        id: block.id,
        type: "section_header",
        label: block.content
      };
    case "divider":
      return {
        id: block.id,
        type: "divider",
        label: ""
      };
    case "spacer":
      return {
        id: block.id,
        type: "spacer",
        label: ""
      };
    // Content blocks and pdf_overlay don't map to legacy form fields
    case "paragraph":
    case "heading":
    case "list":
    case "image":
    case "table":
    case "page_break":
    case "pdf_overlay":
      return null;
    default:
      return null;
  }
}
function templateContentToNdf(content, name, pdfSource) {
  const blocks = [];
  const variables = content.variables.map((v) => ({
    name: v,
    label: v.replace(/\./g, " ").replace(/([A-Z])/g, " $1").trim(),
    type: "text"
  }));
  const meta = {
    title: name,
    variables: variables.length > 0 ? variables : void 0
  };
  const signers = content.signers?.map((s) => ({
    id: s.id,
    name: s.name,
    order: s.order
  }));
  const isPdf = !!pdfSource;
  if (isPdf) {
    if (content.textFields) {
      for (const tf of content.textFields) {
        blocks.push({
          type: "pdf_overlay",
          id: tf.id,
          overlayType: "text",
          label: tf.variableName,
          variableName: tf.variableName,
          position: {
            page: tf.position.page,
            x: tf.position.x,
            y: tf.position.y,
            width: tf.position.width,
            height: tf.position.height
          },
          signerId: tf.signerId
        });
      }
    }
    for (const sf of content.signatureFields) {
      const sfExtra = sf;
      const fieldType = typeof sfExtra.fieldType === "string" ? sfExtra.fieldType : "signature";
      const overlayTypeMap = {
        signature: "signature",
        initials: "initials",
        text: "text",
        text_input: "text",
        checkbox: "checkbox",
        date: "date",
        date_signed: "date",
        image: "text"
      };
      const overlayType = overlayTypeMap[fieldType] ?? "signature";
      if (sf.position) {
        blocks.push({
          type: "pdf_overlay",
          id: sf.id,
          overlayType,
          label: sf.label,
          validation: { required: sf.required },
          position: {
            page: sf.position.page,
            x: sf.position.x,
            y: sf.position.y,
            width: sf.position.width,
            height: sf.position.height
          },
          signerId: sf.signerId
        });
      } else {
        blocks.push({
          type: "field_signature",
          id: sf.id,
          label: sf.label,
          required: sf.required,
          signerId: sf.signerId
        });
      }
    }
  } else {
    for (const sf of content.signatureFields) {
      blocks.push({
        type: "field_signature",
        id: sf.id,
        label: sf.label,
        required: sf.required,
        position: sf.position ? {
          page: sf.position.page,
          x: sf.position.x,
          y: sf.position.y,
          width: sf.position.width,
          height: sf.position.height
        } : void 0,
        signerId: sf.signerId
      });
    }
  }
  const pdf = pdfSource ? {
    fileUrl: pdfSource.fileUrl,
    originalName: pdfSource.originalName,
    fileSize: pdfSource.fileSize,
    pageCount: pdfSource.pageCount
  } : void 0;
  return {
    version: "1.0",
    kind: "document",
    meta,
    blocks,
    pdf,
    signers: signers && signers.length > 0 ? signers : void 0
  };
}
function ndfBlocksToHtml(doc) {
  const htmlParts = [];
  for (const block of doc.blocks) {
    switch (block.type) {
      case "heading": {
        const level = block.level || 2;
        const tag = `h${level}`;
        htmlParts.push(`<${tag}>${escapeHtml(block.content)}</${tag}>`);
        break;
      }
      case "paragraph":
        htmlParts.push(`<p>${escapeHtml(block.content)}</p>`);
        break;
      case "list": {
        const tag = block.ordered ? "ol" : "ul";
        const items = block.items.map((item) => `<li>${escapeHtml(item)}</li>`).join("");
        htmlParts.push(`<${tag}>${items}</${tag}>`);
        break;
      }
      case "divider":
        htmlParts.push("<hr />");
        break;
      case "section_header":
        htmlParts.push(`<h3>${escapeHtml(block.content)}</h3>`);
        break;
      case "field_signature":
        htmlParts.push(`<p><strong>[Signature: ${escapeHtml(block.label)}]</strong></p>`);
        break;
    }
  }
  return htmlParts.join("\n");
}
function escapeHtml(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}
function ndfToTemplateContent(doc) {
  const variables = doc.meta.variables?.map((v) => v.name) || [];
  const html = ndfBlocksToHtml(doc);
  const signatureFields = [];
  const textFields = [];
  for (const block of doc.blocks) {
    if (block.type === "field_signature") {
      signatureFields.push({
        id: block.id,
        label: block.label,
        required: block.required,
        position: block.position ? {
          page: block.position.page,
          x: block.position.x,
          y: block.position.y,
          width: block.position.width,
          height: block.position.height
        } : void 0,
        signerId: block.signerId
      });
    } else if (block.type === "pdf_overlay") {
      if (block.overlayType === "signature") {
        signatureFields.push({
          id: block.id,
          label: block.label,
          required: "required" in block ? block.required ?? true : true,
          position: {
            page: block.position.page,
            x: block.position.x,
            y: block.position.y,
            width: block.position.width,
            height: block.position.height
          },
          signerId: block.signerId
        });
      } else if (block.overlayType === "text" || block.overlayType === "date") {
        textFields.push({
          id: block.id,
          variableName: block.variableName || block.label,
          position: {
            page: block.position.page,
            x: block.position.x,
            y: block.position.y,
            width: block.position.width,
            height: block.position.height
          },
          signerId: block.signerId
        });
      }
    }
  }
  const signers = doc.signers?.map((s) => ({
    id: s.id,
    name: s.name,
    order: s.order
  }));
  return {
    html,
    variables,
    signatureFields,
    textFields: textFields.length > 0 ? textFields : void 0,
    pdfPageCount: doc.pdf?.pageCount,
    signers: signers && signers.length > 0 ? signers : void 0
  };
}
function globalTemplateContentToNdf(content, type, name, description) {
  switch (type) {
    case "form":
      return globalFormTemplateToNdf(content, name, description);
    case "document":
      return globalDocumentTemplateToNdf(content, name, description);
    case "sms":
      return globalSmsTemplateToNdf(content, name, description);
    case "email":
      return globalEmailTemplateToNdf(content, name, description);
  }
}
function globalFormTemplateToNdf(content, name, description) {
  const parsed = JSON.parse(content);
  if (parsed.version === "1.0" && parsed.kind) {
    return parsed;
  }
  if (parsed.fields && Array.isArray(parsed.fields)) {
    const blocks = parsed.fields.map((f) => {
      const field = {
        id: f.id || generateBlockId(),
        type: f.type || "text",
        label: f.label || "",
        placeholder: f.placeholder,
        helpText: f.helpText,
        options: f.options,
        validation: f.required ? { required: true } : void 0
      };
      return formFieldToNdfBlock(field);
    });
    return {
      version: "1.0",
      kind: "form",
      meta: {
        title: name || parsed.name || "Untitled Form",
        description: description || parsed.description,
        category: parsed.category
      },
      blocks
    };
  }
  return {
    version: "1.0",
    kind: "form",
    meta: { title: name },
    blocks: []
  };
}
function globalDocumentTemplateToNdf(content, name, description) {
  const parsed = JSON.parse(content);
  if (parsed.version === "1.0" && parsed.kind) {
    return parsed;
  }
  if (parsed.signatureFields || parsed.variables) {
    return templateContentToNdf(
      parsed,
      name,
      parsed.fileUrl ? {
        fileUrl: parsed.fileUrl,
        originalName: parsed.originalName || name,
        fileSize: parsed.fileSize || 0,
        pageCount: parsed.pdfPageCount || 1
      } : void 0
    );
  }
  return {
    version: "1.0",
    kind: "document",
    meta: { title: name, description },
    blocks: []
  };
}
function globalSmsTemplateToNdf(content, name, description) {
  let messageText;
  try {
    const parsed = JSON.parse(content);
    if (parsed.version === "1.0" && parsed.kind) {
      return parsed;
    }
    messageText = parsed.content || parsed.text || parsed.message || content;
  } catch {
    messageText = content;
  }
  const variablePattern = /\{\{([^}]+)\}\}/g;
  const variables = [];
  for (const match of messageText.matchAll(variablePattern)) {
    const varName = match[1]?.trim();
    if (varName && !variables.some((v) => v.name === varName)) {
      variables.push({
        name: varName,
        label: varName.replace(/\./g, " ").replace(/([A-Z])/g, " $1").trim(),
        type: "text"
      });
    }
  }
  return {
    version: "1.0",
    kind: "sms",
    meta: {
      title: name,
      description,
      variables: variables.length > 0 ? variables : void 0
    },
    blocks: [
      { type: "paragraph", id: generateBlockId(), content: messageText }
    ]
  };
}
function globalEmailTemplateToNdf(content, name, description) {
  let bodyText;
  try {
    const parsed = JSON.parse(content);
    if (parsed.version === "1.0" && parsed.kind) {
      return parsed;
    }
    bodyText = parsed.content || parsed.html || parsed.body || content;
  } catch {
    bodyText = content;
  }
  const variablePattern = /\{\{([^}]+)\}\}/g;
  const variables = [];
  for (const match of bodyText.matchAll(variablePattern)) {
    const varName = match[1]?.trim();
    if (varName && !variables.some((v) => v.name === varName)) {
      variables.push({
        name: varName,
        label: varName.replace(/\./g, " ").replace(/([A-Z])/g, " $1").trim(),
        type: "text"
      });
    }
  }
  return {
    version: "1.0",
    kind: "email",
    meta: {
      title: name,
      description,
      variables: variables.length > 0 ? variables : void 0
    },
    blocks: [
      { type: "paragraph", id: generateBlockId(), content: bodyText }
    ]
  };
}
var blockIdCounter = 0;
function generateBlockId() {
  blockIdCounter++;
  return `blk_${Date.now().toString(36)}_${blockIdCounter.toString(36)}`;
}
function resetBlockIdCounter() {
  blockIdCounter = 0;
}

// src/ndf-renderer.ts
function escapeHtml2(unsafe) {
  return unsafe.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}
function substituteVariables(text, variables) {
  return text.replace(/\{\{([^}]+)\}\}/g, (match, varName) => {
    const trimmed = (varName ?? "").trim();
    const resolved = trimmed && variables ? variables[trimmed] : void 0;
    if (resolved !== void 0) {
      return escapeHtml2(resolved);
    }
    return escapeHtml2(match);
  });
}
function textStyleToCss(style) {
  if (!style) return "";
  const parts = [];
  if (style.bold) parts.push("font-weight:700");
  if (style.italic) parts.push("font-style:italic");
  if (style.align) parts.push(`text-align:${style.align}`);
  if (style.fontSize) parts.push(`font-size:${style.fontSize}px`);
  return parts.length > 0 ? ` style="${parts.join(";")}"` : "";
}
function renderFieldLabel(label, required) {
  const escaped = escapeHtml2(label);
  const requiredMark = required ? ' <span class="ndf-required">*</span>' : "";
  return `<label class="ndf-label">${escaped}${requiredMark}</label>`;
}
function renderHelpText(helpText) {
  if (!helpText) return "";
  return `<p class="ndf-help-text">${escapeHtml2(helpText)}</p>`;
}
function renderBlock(block, variables) {
  switch (block.type) {
    // ── Content Blocks ──────────────────────────────
    case "paragraph": {
      const css = textStyleToCss(block.style);
      const content = substituteVariables(block.content, variables);
      return `<p class="ndf-paragraph"${css}>${content}</p>`;
    }
    case "heading": {
      const tag = `h${block.level}`;
      const content = substituteVariables(block.content, variables);
      return `<${tag} class="ndf-heading ndf-${tag}">${content}</${tag}>`;
    }
    case "list": {
      const tag = block.ordered ? "ol" : "ul";
      const items = block.items.map((item) => `<li>${substituteVariables(item, variables)}</li>`).join("\n");
      return `<${tag} class="ndf-list">
${items}
</${tag}>`;
    }
    case "image": {
      const alt = block.alt ? ` alt="${escapeHtml2(block.alt)}"` : ' alt=""';
      const width = block.width ? ` width="${block.width}"` : "";
      const height = block.height ? ` height="${block.height}"` : "";
      const src = substituteVariables(block.src, variables);
      return `<img class="ndf-image" src="${src}"${alt}${width}${height} />`;
    }
    case "table": {
      const thead = block.columns.map((col) => `<th>${escapeHtml2(col.header)}</th>`).join("");
      const tbody = block.rows.map(
        (row) => "<tr>" + row.map((cell) => `<td>${substituteVariables(cell, variables)}</td>`).join("") + "</tr>"
      ).join("\n");
      return `<table class="ndf-table">
<thead><tr>${thead}</tr></thead>
<tbody>
${tbody}
</tbody>
</table>`;
    }
    case "divider":
      return '<hr class="ndf-divider" />';
    case "spacer": {
      const height = block.height ?? 24;
      return `<div class="ndf-spacer" style="height:${height}px"></div>`;
    }
    case "section_header": {
      const content = substituteVariables(block.content, variables);
      return `<div class="ndf-section-header">${content}</div>`;
    }
    case "page_break":
      return '<div class="ndf-page-break"></div>';
    // ── Field Blocks ────────────────────────────────
    case "field_text": {
      const isRequired = block.validation?.required;
      const placeholder = block.placeholder ? ` placeholder="${escapeHtml2(block.placeholder)}"` : "";
      const name = ` name="${escapeHtml2(block.id)}"`;
      let input;
      if (block.inputType === "textarea") {
        input = `<textarea class="ndf-input ndf-textarea"${name}${placeholder}></textarea>`;
      } else {
        const typeMap = {
          text: "text",
          email: "email",
          phone: "tel",
          number: "number"
        };
        const inputType = typeMap[block.inputType] ?? "text";
        input = `<input class="ndf-input" type="${inputType}"${name}${placeholder} />`;
      }
      return `<div class="ndf-field" data-field-id="${escapeHtml2(block.id)}">
${renderFieldLabel(block.label, isRequired)}
${input}
${renderHelpText(block.helpText)}
</div>`;
    }
    case "field_date": {
      const isRequired = block.validation?.required;
      const name = ` name="${escapeHtml2(block.id)}"`;
      return `<div class="ndf-field" data-field-id="${escapeHtml2(block.id)}">
${renderFieldLabel(block.label, isRequired)}
<input class="ndf-input" type="date"${name} />
${renderHelpText(block.helpText)}
</div>`;
    }
    case "field_time": {
      const isRequired = block.validation?.required;
      const name = ` name="${escapeHtml2(block.id)}"`;
      return `<div class="ndf-field" data-field-id="${escapeHtml2(block.id)}">
${renderFieldLabel(block.label, isRequired)}
<input class="ndf-input" type="time"${name} />
${renderHelpText(block.helpText)}
</div>`;
    }
    case "field_select": {
      const isRequired = block.validation?.required;
      const name = ` name="${escapeHtml2(block.id)}"`;
      let input;
      if (block.selectType === "dropdown") {
        const options = block.options.map(
          (opt) => `<option value="${escapeHtml2(opt.value)}">${escapeHtml2(opt.label)}</option>`
        ).join("\n");
        input = `<select class="ndf-input ndf-select"${name}>
<option value="">Select...</option>
${options}
</select>`;
      } else {
        const type = block.selectType === "radio" ? "radio" : "checkbox";
        input = block.options.map(
          (opt) => `<label class="ndf-option"><input type="${type}"${name} value="${escapeHtml2(opt.value)}" /> ${escapeHtml2(opt.label)}</label>`
        ).join("\n");
        input = `<div class="ndf-option-group">
${input}
</div>`;
      }
      return `<div class="ndf-field" data-field-id="${escapeHtml2(block.id)}">
${renderFieldLabel(block.label, isRequired)}
${input}
${renderHelpText(block.helpText)}
</div>`;
    }
    case "field_file": {
      const isRequired = block.validation?.required;
      const name = ` name="${escapeHtml2(block.id)}"`;
      const accept = block.accept ? ` accept="${escapeHtml2(block.accept.join(","))}"` : "";
      return `<div class="ndf-field" data-field-id="${escapeHtml2(block.id)}">
${renderFieldLabel(block.label, isRequired)}
<input class="ndf-input ndf-file-input" type="file"${name}${accept} />
${renderHelpText(block.helpText)}
</div>`;
    }
    case "field_signature": {
      return `<div class="ndf-field ndf-signature-pad" data-field-id="${escapeHtml2(block.id)}">
${renderFieldLabel(block.label, block.required)}
<div class="ndf-signature-area">Sign here</div>
${renderHelpText(block.helpText)}
</div>`;
    }
    case "pdf_overlay":
      return "";
    default:
      return "";
  }
}
function renderNdfToHtml(doc, variables) {
  const blocks = doc.blocks.map((block) => renderBlock(block, variables)).filter((html) => html.length > 0).join("\n");
  const layoutClass = doc.meta.layout === "two-column" ? "ndf-layout-two-column" : "ndf-layout-single";
  return `<div class="ndf-document ${layoutClass}" data-ndf-kind="${escapeHtml2(doc.kind)}">
${blocks}
</div>`;
}
function getNdfStyles() {
  return `<style>
/* \u2500\u2500\u2500 NDF Document Styles \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
.ndf-document {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  line-height: 1.6;
  color: #1c1917;
  max-width: 800px;
  margin: 0 auto;
}
.ndf-layout-two-column {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}
.ndf-layout-single {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* \u2500\u2500\u2500 Content Blocks \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
.ndf-paragraph {
  margin: 0;
  padding: 0;
}
.ndf-heading {
  margin: 0;
  font-weight: 700;
  color: #0c0a09;
}
.ndf-h1 { font-size: 1.875rem; line-height: 2.25rem; }
.ndf-h2 { font-size: 1.5rem; line-height: 2rem; }
.ndf-h3 { font-size: 1.25rem; line-height: 1.75rem; }
.ndf-h4 { font-size: 1.125rem; line-height: 1.5rem; }
.ndf-list {
  margin: 0;
  padding-left: 1.5rem;
}
.ndf-list li {
  margin-bottom: 0.25rem;
}
.ndf-image {
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
}
.ndf-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.ndf-table th,
.ndf-table td {
  border: 1px solid #d6d3d1;
  padding: 0.5rem 0.75rem;
  text-align: left;
}
.ndf-table th {
  background-color: #f5f5f4;
  font-weight: 600;
}
.ndf-table tbody tr:nth-child(even) {
  background-color: #fafaf9;
}
.ndf-divider {
  border: none;
  border-top: 1px solid #d6d3d1;
  margin: 0.5rem 0;
}
.ndf-spacer {
  /* height set inline */
}
.ndf-section-header {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #57534e;
  border-left: 3px solid #6366f1;
  padding-left: 0.75rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}
.ndf-page-break {
  border-top: 2px dashed #a8a29e;
  margin: 1rem 0;
  position: relative;
}
.ndf-page-break::after {
  content: 'Page Break';
  position: absolute;
  top: -0.6em;
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  padding: 0 0.5rem;
  font-size: 0.6875rem;
  color: #a8a29e;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* \u2500\u2500\u2500 Field Blocks \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
.ndf-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.ndf-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #44403c;
}
.ndf-required {
  color: #ef4444;
}
.ndf-input {
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d6d3d1;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #1c1917;
  background-color: #fff;
  box-sizing: border-box;
}
.ndf-input:focus {
  outline: 2px solid #6366f1;
  outline-offset: -1px;
  border-color: #6366f1;
}
.ndf-textarea {
  min-height: 5rem;
  resize: vertical;
}
.ndf-select {
  appearance: auto;
}
.ndf-file-input {
  padding: 0.375rem;
}
.ndf-option-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.ndf-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #44403c;
  cursor: pointer;
}
.ndf-option input {
  width: 1rem;
  height: 1rem;
}
.ndf-help-text {
  font-size: 0.75rem;
  color: #78716c;
  margin: 0;
}
.ndf-signature-pad {
  /* extends .ndf-field */
}
.ndf-signature-area {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 6rem;
  border: 2px dashed #d6d3d1;
  border-radius: 0.5rem;
  color: #a8a29e;
  font-size: 0.875rem;
  background-color: #fafaf9;
}

/* \u2500\u2500\u2500 Print \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
@media print {
  .ndf-page-break {
    page-break-before: always;
    border: none;
    margin: 0;
    height: 0;
  }
  .ndf-page-break::after {
    display: none;
  }
}

/* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
   Legal / Professional Document Overrides
   Scoped to [data-ndf-kind="document"] so forms,
   emails, and SMS kinds are completely unaffected.
   \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */

/* Root \u2014 serif font, block flow (not flex gap) */
.ndf-document[data-ndf-kind="document"] {
  font-family: 'Times New Roman', Georgia, 'Palatino Linotype', serif;
  font-size: 12pt;
  line-height: 1.55;
  color: #1a1a1a;
  display: block;
}

/* Paragraphs \u2014 justified text, proper paragraph spacing */
.ndf-document[data-ndf-kind="document"] .ndf-paragraph {
  text-align: justify;
  margin: 0 0 0.9em 0;
  padding: 0;
  hyphens: auto;
}

/* H1 \u2014 Document title: bold, centred, ALL CAPS */
.ndf-document[data-ndf-kind="document"] .ndf-h1 {
  font-size: 14pt;
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 0.3em 0;
  line-height: 1.3;
}

/* H2 \u2014 Numbered section headings: bold, blue underline */
.ndf-document[data-ndf-kind="document"] .ndf-h2 {
  font-size: 11pt;
  font-weight: 700;
  text-align: left;
  text-decoration: underline;
  text-decoration-color: #1e40af;
  text-underline-offset: 3px;
  margin: 1.5em 0 0.55em 0;
  line-height: 1.35;
  text-transform: none;
}

/* H3 / H4 \u2014 Sub-section headings */
.ndf-document[data-ndf-kind="document"] .ndf-h3,
.ndf-document[data-ndf-kind="document"] .ndf-h4 {
  font-size: 11pt;
  font-weight: 700;
  margin: 1.1em 0 0.45em 0;
  line-height: 1.35;
}

/* Lists \u2014 alphabetic lettered clauses (a), (b), (c)\u2026 */
.ndf-document[data-ndf-kind="document"] .ndf-list {
  list-style-type: none;
  padding-left: 0;
  margin: 0 0 0.9em 0;
}
.ndf-document[data-ndf-kind="document"] .ndf-list li {
  text-align: justify;
  margin-bottom: 0.45em;
  padding-left: 1.5em;
  hyphens: auto;
}

/* Divider \u2014 accent rule before signature block */
.ndf-document[data-ndf-kind="document"] .ndf-divider {
  border: none;
  border-top: 1.5px solid #1e40af;
  margin: 1.8em 0;
}

/* Section header \u2014 plain bold text (no left-border widget) */
.ndf-document[data-ndf-kind="document"] .ndf-section-header {
  font-family: 'Times New Roman', Georgia, 'Palatino Linotype', serif;
  font-size: 11pt;
  font-weight: 700;
  text-transform: none;
  letter-spacing: normal;
  border-left: none;
  padding-left: 0;
  padding-top: 0;
  padding-bottom: 0;
  color: inherit;
  margin: 1.1em 0 0.45em 0;
}

/* Spacer \u2014 honoured as-is */
.ndf-document[data-ndf-kind="document"] .ndf-spacer {
  display: block;
}

/* Table \u2014 formal table styling for legal docs */
.ndf-document[data-ndf-kind="document"] .ndf-table {
  font-family: 'Times New Roman', Georgia, 'Palatino Linotype', serif;
  font-size: 11pt;
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 0.9em;
}
.ndf-document[data-ndf-kind="document"] .ndf-table th,
.ndf-document[data-ndf-kind="document"] .ndf-table td {
  border: 1px solid #555;
  padding: 0.4em 0.65em;
  text-align: left;
  vertical-align: top;
}
.ndf-document[data-ndf-kind="document"] .ndf-table th {
  background-color: #f0f0f0;
  font-weight: 700;
}
</style>`;
}
var contactCollectionFormSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1).max(200),
  slug: z.string().min(1).max(100),
  isEnabled: z.boolean().default(true),
  // Consent customization
  smsConsentText: z.string().max(1e3).optional().nullable(),
  emailConsentText: z.string().max(1e3).optional().nullable(),
  voiceConsentText: z.string().max(1e3).optional().nullable(),
  showSmsConsent: z.boolean().default(true),
  showEmailConsent: z.boolean().default(true),
  showVoiceConsent: z.boolean().default(false),
  // Purpose toggles per channel
  smsShowInformational: z.boolean().default(true),
  smsShowMarketing: z.boolean().default(true),
  emailShowInformational: z.boolean().default(true),
  emailShowMarketing: z.boolean().default(true),
  voiceShowInformational: z.boolean().default(true),
  voiceShowMarketing: z.boolean().default(false),
  // Form customization
  headerText: z.string().max(200).optional().nullable(),
  welcomeMessage: z.string().max(2e3).optional().nullable(),
  successMessage: z.string().max(1e3).optional().nullable(),
  logoUrl: z.string().url().optional().nullable(),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional().nullable(),
  // Include custom fields toggle
  includeCustomFields: z.boolean().default(true),
  // QR Code
  qrCodeUrl: z.string().optional().nullable(),
  submissionCount: z.number().int().default(0),
  createdAt: z.date(),
  updatedAt: z.date()
});
var createContactCollectionFormSchema = contactCollectionFormSchema.omit({
  id: true,
  tenantId: true,
  qrCodeUrl: true,
  submissionCount: true,
  createdAt: true,
  updatedAt: true
});
var updateContactCollectionFormSchema = createContactCollectionFormSchema.partial();
var contactCollectionSubmissionSchema = z.object({
  id: z.string().uuid(),
  formId: z.string().uuid(),
  contactId: z.string().uuid().optional().nullable(),
  data: z.record(z.unknown()),
  // Captured consent
  smsInformational: z.boolean().default(false),
  smsMarketing: z.boolean().default(false),
  emailInformational: z.boolean().default(false),
  emailMarketing: z.boolean().default(false),
  voiceInformational: z.boolean().default(false),
  voiceMarketing: z.boolean().default(false),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  submittedAt: z.date()
});
var contactCollectionFormSubmissionSchema = z.object({
  // Contact fields
  firstName: z.string().min(1, "First name is required").max(100),
  lastName: z.string().max(100).optional(),
  email: z.string().email("Invalid email").optional(),
  phone: z.string().min(10, "Phone must be at least 10 digits").optional(),
  address: z.string().max(200).optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(100).optional(),
  zipCode: z.string().max(15).optional(),
  country: z.string().max(100).optional(),
  // Custom fields (dynamic)
  customFields: z.record(z.unknown()).optional(),
  // Consent
  consent: z.object({
    sms: channelConsentSchema.optional(),
    email: channelConsentSchema.optional(),
    voice: channelConsentSchema.optional()
  }).optional(),
  // Honeypot field (should be empty)
  website: z.string().max(0, "Bot detected").optional()
}).refine(
  (data) => data.email || data.phone,
  { message: "Either email or phone is required", path: ["email"] }
);
var publicFormConfigSchema = z.object({
  name: z.string(),
  headerText: z.string().optional().nullable(),
  welcomeMessage: z.string().optional().nullable(),
  successMessage: z.string().optional().nullable(),
  logoUrl: z.string().optional().nullable(),
  primaryColor: z.string().optional().nullable(),
  // Which consent options to show
  showSmsConsent: z.boolean(),
  showEmailConsent: z.boolean(),
  showVoiceConsent: z.boolean(),
  // Purpose toggles
  smsShowInformational: z.boolean(),
  smsShowMarketing: z.boolean(),
  emailShowInformational: z.boolean(),
  emailShowMarketing: z.boolean(),
  voiceShowInformational: z.boolean(),
  voiceShowMarketing: z.boolean(),
  // Consent text
  smsConsentText: z.string().optional().nullable(),
  emailConsentText: z.string().optional().nullable(),
  voiceConsentText: z.string().optional().nullable(),
  // Custom fields definitions
  customFields: z.array(z.object({
    fieldKey: z.string(),
    name: z.string(),
    fieldType: z.string(),
    isRequired: z.boolean(),
    options: z.array(z.string()).optional().nullable(),
    placeholder: z.string().optional().nullable(),
    helpText: z.string().optional().nullable()
  })).optional(),
  // Branding
  companyName: z.string().optional()
});
var smsTemplateTypeSchema = z.enum(["document", "form"]);
var smsTemplateSchema = z.object({
  id: z.string().uuid(),
  type: smsTemplateTypeSchema,
  name: z.string().min(1),
  body: z.string().min(1),
  variables: z.array(z.string()),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
var createSmsTemplateSchema = z.object({
  type: smsTemplateTypeSchema,
  name: z.string().min(1).max(200),
  body: z.string().min(1).max(1600),
  isDefault: z.boolean().optional().default(false),
  isActive: z.boolean().optional().default(true)
});
var updateSmsTemplateSchema = createSmsTemplateSchema.partial().omit({ type: true });
var CONTACT_SMS_VARIABLES = [
  {
    key: "firstName",
    label: "First Name",
    placeholder: "{{firstName}}",
    group: "contact",
    description: "Recipient's first name"
  },
  {
    key: "lastName",
    label: "Last Name",
    placeholder: "{{lastName}}",
    group: "contact",
    description: "Recipient's last name"
  },
  {
    key: "fullName",
    label: "Full Name",
    placeholder: "{{fullName}}",
    group: "contact",
    description: "Recipient's full name"
  }
];
var ORGANISATION_SMS_VARIABLES = [
  {
    key: "orgName",
    label: "Organization Name",
    placeholder: "{{orgName}}",
    group: "organisation",
    description: "Your organization name"
  },
  {
    key: "orgCity",
    label: "City",
    placeholder: "{{orgCity}}",
    group: "organisation",
    description: "Your organization city"
  },
  {
    key: "orgState",
    label: "State / Province",
    placeholder: "{{orgState}}",
    group: "organisation",
    description: "Your organization state or province"
  },
  {
    key: "orgCountry",
    label: "Country",
    placeholder: "{{orgCountry}}",
    group: "organisation",
    description: "Your organization country (ISO-2)"
  }
];
var DOCUMENT_SPECIFIC_VARIABLES = [
  {
    key: "documentName",
    label: "Document Name",
    placeholder: "{{documentName}}",
    group: "document",
    description: "Name of the document to be signed"
  },
  {
    key: "signingUrl",
    label: "Signing URL",
    placeholder: "{{signingUrl}}",
    group: "document",
    description: "Link for the recipient to sign the document"
  },
  {
    key: "expiresAt",
    label: "Expiry Date",
    placeholder: "{{expiresAt}}",
    group: "document",
    description: "Date the signing link expires"
  }
];
var FORM_SPECIFIC_VARIABLES = [
  {
    key: "formName",
    label: "Form Name",
    placeholder: "{{formName}}",
    group: "form",
    description: "Name of the form to be filled"
  },
  {
    key: "formUrl",
    label: "Form URL",
    placeholder: "{{formUrl}}",
    group: "form",
    description: "Link for the recipient to fill the form"
  }
];
var DOCUMENT_SMS_VARIABLES = [
  ...CONTACT_SMS_VARIABLES,
  ...DOCUMENT_SPECIFIC_VARIABLES,
  ...ORGANISATION_SMS_VARIABLES
];
var FORM_SMS_VARIABLES = [
  ...CONTACT_SMS_VARIABLES,
  ...FORM_SPECIFIC_VARIABLES,
  ...ORGANISATION_SMS_VARIABLES
];
var EMAIL_TEMPLATE_KEYS = [
  "verification",
  "welcome",
  "forgot-password",
  "account-linked",
  "payment-success",
  "payment-failed",
  "support-ticket-created",
  "support-reply-received",
  "team-invitation",
  "account-suspended",
  "esignature-request",
  "signature-completed",
  "consent-confirmation"
];
var EMAIL_TEMPLATE_DEFINITIONS = {
  "verification": {
    key: "verification",
    name: "Email Verification",
    description: "Sent when a new user signs up and needs to verify their email address.",
    defaultSubject: "Verify your email address \u2014 Respectly",
    category: "auth",
    senderType: "respectly",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "verifyUrl", label: "Verification URL", placeholder: "{{verifyUrl}}", group: "system", description: "Link for the user to verify their email", sampleValue: "https://app.respectly.ai/verify-email?token=sample123", required: true }
    ]
  },
  "welcome": {
    key: "welcome",
    name: "Welcome Email",
    description: "Sent after a user successfully verifies their email address.",
    defaultSubject: "Welcome to Respectly!",
    category: "auth",
    senderType: "respectly",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "loginUrl", label: "Login URL", placeholder: "{{loginUrl}}", group: "system", description: "Link to the login page", sampleValue: "https://app.respectly.ai/login", required: true }
    ]
  },
  "forgot-password": {
    key: "forgot-password",
    name: "Password Reset",
    description: "Sent when a user requests a password reset.",
    defaultSubject: "Reset your password \u2014 Respectly",
    category: "auth",
    senderType: "respectly",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "resetUrl", label: "Reset URL", placeholder: "{{resetUrl}}", group: "system", description: "Link to reset the password", sampleValue: "https://app.respectly.ai/reset-password?token=sample123", required: true }
    ]
  },
  "account-linked": {
    key: "account-linked",
    name: "Account Linked",
    description: "Sent when a user links an OAuth provider (Google, Microsoft, Apple).",
    defaultSubject: "Your account has been linked \u2014 Respectly",
    category: "auth",
    senderType: "respectly",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "provider", label: "Provider", placeholder: "{{provider}}", group: "system", description: "OAuth provider name (Google, Microsoft, Apple)", sampleValue: "Google", required: true },
      { key: "linkedAt", label: "Linked Date", placeholder: "{{linkedAt}}", group: "system", description: "Date and time the account was linked", sampleValue: "January 20, 2026", required: true }
    ]
  },
  "payment-success": {
    key: "payment-success",
    name: "Payment Confirmation",
    description: "Sent to the account owner after a successful payment.",
    defaultSubject: "Payment Received \u2014 Respectly",
    category: "billing",
    senderType: "respectly",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "tenantName", label: "Organization Name", placeholder: "{{tenantName}}", group: "billing", description: "Name of the organization", sampleValue: "Acme Corp", required: true },
      { key: "planName", label: "Plan Name", placeholder: "{{planName}}", group: "billing", description: "Subscription plan purchased", sampleValue: "Professional", required: true },
      { key: "amount", label: "Amount", placeholder: "{{amount}}", group: "billing", description: "Payment amount (e.g. $99.00 CAD)", sampleValue: "$99.00", required: true },
      { key: "invoiceNumber", label: "Invoice Number", placeholder: "{{invoiceNumber}}", group: "billing", description: "Invoice reference number", sampleValue: "INV-0001", required: true },
      { key: "transactionId", label: "Transaction ID", placeholder: "{{transactionId}}", group: "billing", description: "Helcim transaction ID", sampleValue: "TXN-123456", required: true },
      { key: "invoiceUrl", label: "Invoice URL", placeholder: "{{invoiceUrl}}", group: "billing", description: "Link to view the invoice", sampleValue: "https://app.respectly.ai/billing", required: true }
    ]
  },
  "payment-failed": {
    key: "payment-failed",
    name: "Payment Failed",
    description: "Sent to the account owner when a payment fails.",
    defaultSubject: "Payment Failed - Action Required - Respectly",
    category: "billing",
    senderType: "respectly",
    variables: [
      { key: "recipientName", label: "Recipient Name", placeholder: "{{recipientName}}", group: "contact", description: "Recipient's name", sampleValue: "Jane", required: true },
      { key: "tenantName", label: "Organization Name", placeholder: "{{tenantName}}", group: "billing", description: "Name of the organization", sampleValue: "Acme Corp", required: true },
      { key: "amount", label: "Amount", placeholder: "{{amount}}", group: "billing", description: "Payment amount that failed", sampleValue: "$99.00", required: true },
      { key: "failureReason", label: "Failure Reason", placeholder: "{{failureReason}}", group: "billing", description: "Reason the payment failed", sampleValue: "Insufficient funds", required: true },
      { key: "gracePeriodDays", label: "Grace Period (Days)", placeholder: "{{gracePeriodDays}}", group: "billing", description: "Days until account is suspended", sampleValue: "7", required: true },
      { key: "billingUrl", label: "Billing URL", placeholder: "{{billingUrl}}", group: "billing", description: "Link to update payment method", sampleValue: "https://app.respectly.ai/billing", required: true }
    ]
  },
  "support-ticket-created": {
    key: "support-ticket-created",
    name: "Support Ticket Created",
    description: "Sent to the user who opens a new support ticket.",
    defaultSubject: "Support ticket received \u2014 Respectly",
    category: "support",
    senderType: "respectly",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "ticketSubject", label: "Ticket Subject", placeholder: "{{ticketSubject}}", group: "support", description: "Subject of the support ticket", sampleValue: "Unable to send documents", required: true },
      { key: "ticketId", label: "Ticket ID", placeholder: "{{ticketId}}", group: "support", description: "Unique ticket identifier", sampleValue: "TKT-001", required: true },
      { key: "viewUrl", label: "View URL", placeholder: "{{viewUrl}}", group: "support", description: "Link to view the ticket", sampleValue: "https://app.respectly.ai/support/TKT-001", required: true }
    ]
  },
  "support-reply-received": {
    key: "support-reply-received",
    name: "Support Reply Received",
    description: "Sent to the user when support replies to their ticket.",
    defaultSubject: "New reply on your support ticket \u2014 Respectly",
    category: "support",
    senderType: "respectly",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "ticketSubject", label: "Ticket Subject", placeholder: "{{ticketSubject}}", group: "support", description: "Subject of the support ticket", sampleValue: "Unable to send documents", required: true },
      { key: "viewUrl", label: "View URL", placeholder: "{{viewUrl}}", group: "support", description: "Link to view the reply", sampleValue: "https://app.respectly.ai/support/TKT-001", required: true }
    ]
  },
  "team-invitation": {
    key: "team-invitation",
    name: "Team Invitation",
    description: "Sent when a user is invited to join an organization.",
    defaultSubject: "You have been invited to join a team \u2014 Respectly",
    category: "team",
    senderType: "respectly",
    variables: [
      { key: "inviterName", label: "Inviter Name", placeholder: "{{inviterName}}", group: "team", description: "Name of the person sending the invite", sampleValue: "John Smith", required: true },
      { key: "organizationName", label: "Organization Name", placeholder: "{{organizationName}}", group: "team", description: "Name of the organization", sampleValue: "Acme Corp", required: true },
      { key: "roleName", label: "Role", placeholder: "{{roleName}}", group: "team", description: "Role the invitee is being assigned", sampleValue: "Member", required: true },
      { key: "acceptUrl", label: "Accept URL", placeholder: "{{acceptUrl}}", group: "team", description: "Link to accept the invitation", sampleValue: "https://app.respectly.ai/invite?token=sample123", required: true }
    ]
  },
  "account-suspended": {
    key: "account-suspended",
    name: "Account Suspended",
    description: "Sent to all active users when their account is suspended by a Super Admin.",
    defaultSubject: "Account Suspended \u2014 Respectly",
    category: "billing",
    senderType: "respectly",
    variables: [
      { key: "recipientName", label: "Recipient Name", placeholder: "{{recipientName}}", group: "contact", description: "Recipient's name", sampleValue: "Jane", required: true },
      { key: "tenantName", label: "Organization Name", placeholder: "{{tenantName}}", group: "billing", description: "Name of the suspended organization", sampleValue: "Acme Corp", required: true },
      { key: "reason", label: "Suspension Reason", placeholder: "{{reason}}", group: "billing", description: "Reason for the suspension", sampleValue: "Overdue payment", required: true },
      { key: "supportEmail", label: "Support Email", placeholder: "{{supportEmail}}", group: "support", description: "Support contact email address", sampleValue: "support@respectly.ai", required: true }
    ]
  },
  "esignature-request": {
    key: "esignature-request",
    name: "eSignature Request",
    description: "Sent to a contact when they are requested to sign a document.",
    defaultSubject: "Document Signature Request \u2014 Respectly",
    category: "documents",
    senderType: "customer",
    variables: [
      { key: "recipientName", label: "Recipient Name", placeholder: "{{recipientName}}", group: "contact", description: "Name of the person being asked to sign", sampleValue: "Jane Doe", required: true },
      { key: "senderName", label: "Sender / Organization Name", placeholder: "{{senderName}}", group: "team", description: "Name of the organization or person requesting the signature", sampleValue: "Acme Corp", required: true },
      { key: "documentName", label: "Document Name", placeholder: "{{documentName}}", group: "document", description: "Name of the document to be signed", sampleValue: "Service Agreement 2026", required: true },
      { key: "message", label: "Custom Message", placeholder: "{{message}}", group: "document", description: "Optional personal message from the sender", sampleValue: "Please review and sign at your earliest convenience.", required: false },
      { key: "signingUrl", label: "Signing URL", placeholder: "{{signingUrl}}", group: "document", description: "Link for the recipient to sign the document", sampleValue: "https://sign.respectly.ai/d/sample123", required: true },
      { key: "expiresAt", label: "Expiry Date", placeholder: "{{expiresAt}}", group: "document", description: "Date the signing link expires (blank if no expiry)", sampleValue: "February 28, 2026", required: false },
      { key: "tenantLogoUrl", label: "Organization Logo URL", placeholder: "{{tenantLogoUrl}}", group: "team", description: "URL to the organization's logo image shown in the email header", sampleValue: "https://example.com/logo.png", required: false },
      { key: "tenantAddress", label: "Organization Address", placeholder: "{{tenantAddress}}", group: "team", description: "Organization's physical postal address shown in the email footer", sampleValue: "123 Main St \xB7 Toronto, ON \xB7 M5V 1A1 \xB7 CA", required: false },
      { key: "tenantWebsite", label: "Organization Website", placeholder: "{{tenantWebsite}}", group: "team", description: "Organization's website URL shown in the email footer", sampleValue: "https://www.acmecorp.com", required: false }
    ]
  },
  "signature-completed": {
    key: "signature-completed",
    name: "Signature Completed",
    description: "Sent to the signer after they successfully sign a document.",
    defaultSubject: "Document signed \u2014 Respectly",
    category: "documents",
    senderType: "customer",
    variables: [
      { key: "recipientName", label: "Recipient Name", placeholder: "{{recipientName}}", group: "contact", description: "Name of the person who signed", sampleValue: "Jane Doe", required: true },
      { key: "documentName", label: "Document Name", placeholder: "{{documentName}}", group: "document", description: "Name of the signed document", sampleValue: "Service Agreement 2026", required: true },
      { key: "signedAt", label: "Signed At", placeholder: "{{signedAt}}", group: "document", description: "Date and time the document was signed", sampleValue: "January 20, 2026 at 2:30 PM", required: true },
      { key: "downloadUrl", label: "Download URL", placeholder: "{{downloadUrl}}", group: "document", description: "Link to download the signed document", sampleValue: "https://sign.respectly.ai/download/sample123", required: true },
      { key: "tenantName", label: "Organization Name", placeholder: "{{tenantName}}", group: "team", description: "Organization's name shown in the email header and footer", sampleValue: "Acme Corp", required: false },
      { key: "tenantLogoUrl", label: "Organization Logo URL", placeholder: "{{tenantLogoUrl}}", group: "team", description: "URL to the organization's logo image shown in the email header", sampleValue: "https://example.com/logo.png", required: false },
      { key: "tenantAddress", label: "Organization Address", placeholder: "{{tenantAddress}}", group: "team", description: "Organization's physical postal address shown in the email footer", sampleValue: "123 Main St \xB7 Toronto, ON \xB7 M5V 1A1 \xB7 CA", required: false },
      { key: "tenantWebsite", label: "Organization Website", placeholder: "{{tenantWebsite}}", group: "team", description: "Organization's website URL shown in the email footer", sampleValue: "https://www.acmecorp.com", required: false }
    ]
  },
  "consent-confirmation": {
    key: "consent-confirmation",
    name: "Consent Confirmation",
    description: "Sent to contacts who must confirm their opt-in when double opt-in is enabled.",
    defaultSubject: "Please confirm your opt-in \u2014 Respectly",
    category: "documents",
    senderType: "customer",
    variables: [
      { key: "firstName", label: "First Name", placeholder: "{{firstName}}", group: "contact", description: "Recipient's first name", sampleValue: "Jane", required: true },
      { key: "tenantName", label: "Organization Name", placeholder: "{{tenantName}}", group: "team", description: "Name of the organization requesting opt-in", sampleValue: "Acme Corp", required: true },
      { key: "confirmationUrl", label: "Confirmation URL", placeholder: "{{confirmationUrl}}", group: "system", description: "Link to confirm the opt-in", sampleValue: "https://app.respectly.ai/consent/confirm?token=sample123", required: true },
      { key: "expiryHours", label: "Expiry (Hours)", placeholder: "{{expiryHours}}", group: "system", description: "Hours until the confirmation link expires", sampleValue: "48", required: true },
      { key: "tenantLogoUrl", label: "Organization Logo URL", placeholder: "{{tenantLogoUrl}}", group: "team", description: "URL to the organization's logo image shown in the email header", sampleValue: "https://example.com/logo.png", required: false },
      { key: "tenantAddress", label: "Organization Address", placeholder: "{{tenantAddress}}", group: "team", description: "Organization's physical postal address shown in the email footer", sampleValue: "123 Main St \xB7 Toronto, ON \xB7 M5V 1A1 \xB7 CA", required: false },
      { key: "tenantWebsite", label: "Organization Website", placeholder: "{{tenantWebsite}}", group: "team", description: "Organization's website URL shown in the email footer", sampleValue: "https://www.acmecorp.com", required: false }
    ]
  }
};
var ALL_EMAIL_TEMPLATE_VARIABLES = (() => {
  const seen = /* @__PURE__ */ new Set();
  const result = [];
  for (const def of Object.values(EMAIL_TEMPLATE_DEFINITIONS)) {
    for (const v of def.variables) {
      if (!seen.has(v.key)) {
        seen.add(v.key);
        result.push(v);
      }
    }
  }
  return result;
})();
var updateEmailTemplateSchema = z.object({
  subject: z.string().min(1, "Subject is required").max(300, "Subject must be under 300 characters"),
  htmlContent: z.string().min(1, "HTML content is required"),
  preheader: z.string().max(200, "Preheader must be under 200 characters").optional()
});
var testSendEmailTemplateSchema = z.object({
  to: z.string().email("A valid email address is required")
});
var setupStepIdSchema = z.enum([
  "welcome",
  "business-details",
  "brand-assets",
  "setup-goals",
  "contact-import",
  "feature-discovery",
  "communications",
  "subscription",
  // Legacy step IDs preserved for backwards-compatibility with stored progress
  "document-template",
  "campaign-starter"
]);
var setupIntentSchema = z.enum(["campaigns", "documents", "explore"]);
var setupProgressSchema = z.object({
  setupCompletedAt: z.string().datetime().nullable(),
  setupSkippedAt: z.string().datetime().nullable(),
  setupStepsCompleted: z.array(setupStepIdSchema),
  subscriptionPromptTiming: z.enum(["early", "late"]),
  setupIntent: z.array(setupIntentSchema).default([])
});
var updateSetupProgressSchema = z.object({
  completedSteps: z.array(setupStepIdSchema).optional(),
  skippedAt: z.string().datetime().nullable().optional(),
  completedAt: z.string().datetime().nullable().optional(),
  setupIntent: z.array(setupIntentSchema).optional()
});

// src/short-link.types.ts
var SHORT_LINK_PURPOSES = ["intake_prefill", "signing_link"];

// src/dashboard.types.ts
var DASHBOARD_WIDGET_REGISTRY = {
  // Zone H — page-top KPI strip + onboarding checklist
  "kpi-strip": {
    id: "kpi-strip",
    title: "At a Glance",
    zone: "H",
    defaultVisible: true,
    requiredPermission: null,
    refreshSeconds: 120,
    cacheTtlSeconds: 60,
    realtime: false,
    desktopColSpan: 12
  },
  "getting-started": {
    id: "getting-started",
    title: "Getting Started",
    zone: "H",
    defaultVisible: true,
    requiredPermission: null,
    refreshSeconds: 60,
    cacheTtlSeconds: 30,
    realtime: false,
    desktopColSpan: 12
  },
  // Zone A — live, short cache
  "outstanding-signatures": {
    id: "outstanding-signatures",
    title: "Outstanding Signatures",
    zone: "A",
    defaultVisible: true,
    requiredPermission: "documents:read",
    refreshSeconds: 30,
    cacheTtlSeconds: 30,
    realtime: true,
    desktopColSpan: 3
  },
  "unread-inbox": {
    id: "unread-inbox",
    title: "Unread Inbox",
    zone: "A",
    defaultVisible: true,
    requiredPermission: "inbox:read",
    refreshSeconds: 30,
    cacheTtlSeconds: 30,
    realtime: true,
    desktopColSpan: 3
  },
  "failed-deliveries": {
    id: "failed-deliveries",
    title: "Failed Deliveries (24h)",
    zone: "A",
    defaultVisible: true,
    requiredPermission: "campaigns:read",
    refreshSeconds: 60,
    cacheTtlSeconds: 30,
    realtime: true,
    desktopColSpan: 3
  },
  "compliance-attention": {
    id: "compliance-attention",
    title: "Compliance Attention",
    zone: "A",
    defaultVisible: true,
    requiredPermission: null,
    refreshSeconds: 60,
    cacheTtlSeconds: 60,
    realtime: true,
    desktopColSpan: 3
  },
  // Zone B — analytical
  "intake-form-submission-trend": {
    id: "intake-form-submission-trend",
    title: "Form Submission Trend",
    zone: "B",
    defaultVisible: true,
    requiredPermission: "forms:read",
    refreshSeconds: 120,
    cacheTtlSeconds: 120,
    realtime: false,
    defaultWindow: "30d",
    desktopColSpan: 6
  },
  "average-turnaround-time": {
    id: "average-turnaround-time",
    title: "Average Turnaround Time",
    zone: "B",
    defaultVisible: true,
    requiredPermission: "documents:read",
    refreshSeconds: 300,
    cacheTtlSeconds: 300,
    realtime: false,
    defaultWindow: "30d",
    desktopColSpan: 3
  },
  "document-signature-funnel": {
    id: "document-signature-funnel",
    title: "Document Signature Funnel",
    zone: "B",
    defaultVisible: true,
    requiredPermission: "documents:read",
    refreshSeconds: 120,
    cacheTtlSeconds: 120,
    realtime: false,
    defaultWindow: "30d",
    desktopColSpan: 6
  },
  "multichannel-communication-summary": {
    id: "multichannel-communication-summary",
    title: "Multichannel Communication Summary",
    zone: "B",
    defaultVisible: true,
    requiredPermission: "campaigns:read",
    refreshSeconds: 120,
    cacheTtlSeconds: 120,
    realtime: false,
    defaultWindow: "7d",
    desktopColSpan: 6
  },
  "total-contacts": {
    id: "total-contacts",
    title: "Total Contacts",
    zone: "B",
    defaultVisible: true,
    requiredPermission: "contacts:read",
    refreshSeconds: 300,
    cacheTtlSeconds: 300,
    realtime: false,
    desktopColSpan: 3
  },
  "intake-forms-completed": {
    id: "intake-forms-completed",
    title: "Intake Forms Completed",
    zone: "B",
    defaultVisible: true,
    requiredPermission: "forms:read",
    refreshSeconds: 120,
    cacheTtlSeconds: 120,
    realtime: false,
    desktopColSpan: 3
  },
  // Zone B′ — per-entity tables
  "campaign-activity": {
    id: "campaign-activity",
    title: "Campaign Activity",
    zone: "B-prime",
    defaultVisible: true,
    requiredPermission: "campaigns:read",
    refreshSeconds: 60,
    cacheTtlSeconds: 60,
    realtime: false,
    defaultWindow: "30d",
    desktopColSpan: 12
  },
  "document-status": {
    id: "document-status",
    title: "Document Status",
    zone: "B-prime",
    defaultVisible: true,
    requiredPermission: "documents:read",
    refreshSeconds: 60,
    cacheTtlSeconds: 60,
    realtime: false,
    desktopColSpan: 6
  },
  "form-activity": {
    id: "form-activity",
    title: "Form Activity",
    zone: "B-prime",
    defaultVisible: true,
    requiredPermission: "forms:read",
    refreshSeconds: 60,
    cacheTtlSeconds: 60,
    realtime: false,
    desktopColSpan: 6
  },
  // Zone C
  "consent-status": {
    id: "consent-status",
    title: "Consent Status",
    zone: "C",
    defaultVisible: true,
    requiredPermission: "contacts:read",
    refreshSeconds: 300,
    cacheTtlSeconds: 300,
    realtime: false,
    desktopColSpan: 4
  },
  "recent-opt-outs": {
    id: "recent-opt-outs",
    title: "Recent Opt-Outs",
    zone: "C",
    defaultVisible: true,
    requiredPermission: null,
    refreshSeconds: 120,
    cacheTtlSeconds: 120,
    realtime: false,
    desktopColSpan: 4
  },
  "recent-org-activity": {
    id: "recent-org-activity",
    title: "Recent Activity",
    zone: "C",
    defaultVisible: true,
    requiredPermission: null,
    refreshSeconds: 60,
    cacheTtlSeconds: 60,
    realtime: true,
    desktopColSpan: 4
  },
  "plan-usage": {
    id: "plan-usage",
    title: "Plan & Usage",
    zone: "C",
    defaultVisible: true,
    requiredPermission: null,
    refreshSeconds: 300,
    cacheTtlSeconds: 300,
    realtime: false,
    desktopColSpan: 6
  },
  "quick-actions": {
    id: "quick-actions",
    title: "Quick Actions",
    zone: "C",
    defaultVisible: true,
    requiredPermission: null,
    refreshSeconds: 0,
    cacheTtlSeconds: 0,
    realtime: false,
    desktopColSpan: 6
  }
};
var DASHBOARD_WIDGET_IDS = Object.keys(
  DASHBOARD_WIDGET_REGISTRY
);
var DEFAULT_DASHBOARD_LAYOUT = {
  widgetOrder: DASHBOARD_WIDGET_IDS,
  hiddenWidgets: [],
  density: "comfortable",
  timeWindowDefaults: {},
  updatedAt: (/* @__PURE__ */ new Date(0)).toISOString()
};
var aiToneSchema = z.enum(["professional", "friendly", "urgent", "casual"]);
var replyComplianceSchema = z.object({
  messageType: z.enum(["informational", "marketing", "transactional"]),
  passed: z.boolean(),
  risks: z.array(
    z.object({
      type: z.string(),
      severity: z.enum(["low", "medium", "high"]),
      description: z.string(),
      suggestion: z.string().optional()
    })
  )
});
var suggestReplyRequestSchema = z.object({
  conversationId: z.string().uuid(),
  tone: aiToneSchema.optional(),
  /** Optional steering, e.g. "offer a 10% discount" or "ask for their order number". */
  instruction: z.string().max(500).optional()
});
var suggestReplyResponseSchema = z.object({
  content: z.string(),
  /** Email replies only. */
  subject: z.string().optional(),
  /** True when the contact has NOT opted in for the conversation's channel. */
  optedOut: z.boolean(),
  /** Present when the draft was run through inline compliance analysis. */
  compliance: replyComplianceSchema.optional()
});

// src/industries.ts
var INDUSTRIES = [
  { value: "consulting", label: "Consulting" },
  { value: "education", label: "Education" },
  { value: "financial", label: "Financial Services" },
  { value: "healthcare", label: "Healthcare" },
  { value: "home-services", label: "Home Services" },
  { value: "hospitality", label: "Hospitality" },
  { value: "insurance", label: "Insurance" },
  { value: "legal", label: "Legal Services" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "real-estate", label: "Real Estate" },
  { value: "recreational", label: "Recreational" },
  { value: "retail", label: "Retail & E-Commerce" },
  { value: "technology", label: "Technology" },
  { value: "other", label: "Other" }
];
var INDUSTRY_SAMPLE_MESSAGES = {
  technology: "Invite trial users to book a free onboarding call before their trial expires next week",
  healthcare: "Notify patients that flu shots are now available \u2014 walk-ins welcome starting Monday",
  financial: "Inform clients that they need to complete their annual compliance documentation",
  finance: "Inform clients that they need to complete their annual compliance documentation",
  insurance: "Notify clients that open enrollment begins next Monday \u2014 schedule a call to review your plan",
  retail: "Notify customers that our store will be closing early, 3pm on Monday and Tuesday this week",
  "real-estate": "Invite prospective buyers to an open house this Saturday from 1pm to 4pm at 123 Oak Street",
  education: "Remind parents that the spring parent-teacher conference sign-up closes this Friday",
  legal: "Let clients know about an upcoming deadline for filing and schedule a call to discuss next steps",
  consulting: "Send a follow-up to prospects who attended last week's webinar with a link to book a discovery call",
  manufacturing: "Let customers know about updated lead times for custom orders placed after June 1st",
  hospitality: "Send a thank-you to recent guests and invite them to leave a review for a discount on their next stay",
  "home-services": "Notify customers that our seasonal lawn care packages are now available \u2014 book before spots fill up",
  recreational: "Remind members that summer league registration closes this Friday \u2014 sign up before spots fill"
};
var DEFAULT_SAMPLE_MESSAGE = "Send a follow-up to recent clients with a thank-you and a link to book their next visit";
function getIndustrySampleMessage(industry) {
  if (!industry) return DEFAULT_SAMPLE_MESSAGE;
  return INDUSTRY_SAMPLE_MESSAGES[industry] ?? DEFAULT_SAMPLE_MESSAGE;
}

export { ACTION_COLORS, ACTION_LABELS, ALL_EMAIL_TEMPLATE_VARIABLES, CAMPAIGN_FIELD_LABELS, CONTACT_FIELD_LABELS, CONTACT_SMS_VARIABLES, DASHBOARD_WIDGET_IDS, DASHBOARD_WIDGET_REGISTRY, DEFAULT_DASHBOARD_LAYOUT, DELIVERY_METHOD_CONFIG, DOCUMENT_FIELD_LABELS, DOCUMENT_SMS_VARIABLES, EMAIL_TEMPLATE_DEFINITIONS, EMAIL_TEMPLATE_KEYS, ENTITY_ICONS, FIELD_LABELS, FORM_FIELD_LABELS, FORM_SMS_VARIABLES, INDUSTRIES, LAYOUT_FIELD_TYPES, NDF_BLOCK_TYPES, NDF_CONTENT_BLOCK_TYPES, NDF_FIELD_BLOCK_TYPES, OPT_IN_CONFIRMATION_SMS, OPT_OUT_CONFIRMATION_SMS, ORGANISATION_SMS_VARIABLES, ORG_CUSTOM_FIELD_RESERVED, OrgCustomFieldSchema, OrgCustomFieldsSchema, PRE_BUNDLE_CANONICALIZATION_VERSION, PRE_BUNDLE_SCHEMA_VERSION, PRE_CHECK_IDS, PRE_CONDITION_CODES, PRE_CONDITION_LABELS, PRE_DECISION_DESCRIPTIONS, PRE_DECISION_LABELS, PRE_REMEDIATION_CODES, PRE_REMEDIATION_DESCRIPTIONS, PRE_REMEDIATION_LABELS, PRE_RISK_FLAGS, PRE_RISK_FLAG_LABELS, ROLE_PERMISSIONS, SEND_TYPE_CONFIG, SHORT_LINK_PURPOSES, SMS_OPT_IN_KEYWORDS, SMS_OPT_OUT_KEYWORDS, STATUS_BADGE_CONFIG, VOICE_SCRIPT_FIELD_LABELS, accountDetailSchema, accountListFiltersSchema, accountListItemSchema, accountNoteSchema, accountSortFieldSchema, accountTagSchema, accountUsageStatsSchema, activityItemSchema, activityListResponseSchema, activityPaginationSchema, activityQueryParamsSchema, activityStatsSchema, activityStatusSchema, activitySummarySchema, activityTabSchema, activityTagSchema, addOnCatalogFiltersSchema, addOnCatalogSchema, addOnCategorySchema, addOnPurchaseSchema, addOnTypeSchema, addSupportMessageDtoSchema, adminOptOutEventsQuerySchema, adminUserFiltersSchema, aggregationPeriodSchema, aiFlaggedContentSchema, aiToneSchema, analyticsQuerySchema, apiErrorResponseSchema, apiErrorSchema, apiSuccessSchema, assignAddOnToPlanDtoSchema, assignDiscountDtoSchema, assignSupportRequestDtoSchema, auditActionSchema, auditActivityDataPointSchema, auditEntityTypeSchema, auditExportRequestSchema, auditLogFiltersSchema, auditLogSchema, auditStatsSchema, authUserSchema, authorTypeSchema, availableAddOnSchema, b2bOrB2cSchema, billingCycleSchema, billingDashboardSchema, billingStatusSchema, bulkActionRequestSchema, bulkActionResponseSchema, bundleCallingHourWindowSchema, bundleCheckDefinitionSchema, bundleDncRegistrySchema, bundleFeatureFlagDefaultSchema, bundleFrequencyCapSchema, bundleJurisdictionSchema, bundleRuleSchema, callingHourWindowSchema, campaignAudienceTypeSchema, campaignDetailResponseSchema, campaignDetailSchema, campaignEnrollmentSchema, campaignSchema, campaignStatsSchema, campaignStatusSchema, campaignStepSchema, campaignStepStatsSchema, campaignStepTypeSchema, campaignTriggerSchema, campaignTypeSchema, cancelAccountDtoSchema, changeActionSchema, changeDetailSchema, changeEntrySchema, changeListResponseSchema, changeUserSchema, changesQuerySchema, channelConsentSchema, channelSchema, chartDataSchema, clicksendInboundSmsMessageSchema, clicksendInboundSmsSchema, complianceCheckRequestSchema, complianceCheckSchema, complianceSettingsSchema, consentFormValuesSchema, consentLevelSchema, consentRecordSchema, consentStatusSchema, contactCollectionFormSchema, contactCollectionFormSubmissionSchema, contactCollectionSubmissionSchema, contactFilterSchema, contactGroupInfoSchema, contactGroupMembershipSchema, contactGroupSchema, contactGroupStageSchema, contactImportRowSchema, contactOrSendSchema, contactSchema, conversationActionResponseSchema, conversationDetailResponseSchema, conversationDetailSchema, conversationListItemSchema, conversationMessageSchema, conversationSchema, conversationStatusSchema, conversationsListResponseSchema, costEstimateSchema, createAccountDtoSchema, createAccountNoteSchema, createAccountTagDtoSchema, createAddOnCatalogDtoSchema, createCallingHourWindowSchema, createCampaignSchema, createCampaignStepDtoSchema, createContactCollectionFormSchema, createContactGroupSchema, createContactGroupStageSchema, createContactSchema, createContactWithConsentSchema, createCustomFieldSchema, createDiscountDtoSchema, createDncRegistrySchema, createDocumentSchema, createFeatureFlagDtoSchema, createFormSchema, createFrequencyCapConfigSchema, createJurisdictionSchema, createPlanDtoSchema, createPlatformCampaignDtoSchema, createPreCheckDefinitionSchema, createRuleSchema, createSignatureRequestSchema, createSmsTemplateSchema, createSuperAdminUserSchema, createSupportRequestDtoSchema, createTagSchema, createTemplateCategoryDtoSchema, createTemplateDtoSchema, createTenantSchema, createUserSchema, createVoiceScriptSchema, createWebhookSchema, customFieldDefinitionListResponseSchema, customFieldDefinitionSchema, customFieldFilterSchema, customFieldTypeSchema, customFieldValueSchema, customFieldValuesSchema, dashboardStatsSchema, dateFieldBlockSchema, dateRangeSchema, declineDocumentSchema, deliveryChannelSchema, deliveryConfigSchema, deliveryMethodSchema, discountListFiltersSchema, discountSchema, discountScopeSchema, discountTypeSchema, discountWithStatsSchema, disputeStatusSchema, dividerBlockSchema, dncRegistrySchema, dncStatusSchema, documentSchema, documentStatusSchema, documentTypeSchema, ebrEventTypeSchema, eligibilityFlagsSchema, eligibilityStatusSchema, emailConfigSchema, emailProviderSchema, enrollTenantsInCampaignDtoSchema, enrollmentStatusSchema, exportFormatSchema, exportRequestSchema, exportResponseSchema, extractVariableReferences, featureFlagFiltersSchema, featureFlagSchema, featureFlagScopeSchema, fieldValidationSchema, fileFieldBlockSchema, flagSeveritySchema, flagStatusSchema, flaggedContentTypeSchema, formBrandingSchema, formFieldSchema, formFieldTypeSchema, formResponseFieldSchema, formSchema, formSchemaDefinition, formSchemaToNdf, formSettingsSchema, formSubmissionResponseSchema, formSubmissionSchema, formatFieldName, freePlanAccountSchema, frequencyCapConfigSchema, generateBlockId, getFieldBlocks, getFieldLabel, getIndustrySampleMessage, getNdfStyles, getSignatureBlocks, globalTemplateContentToNdf, globalTemplateSchema, groupListItemSchema, headingBlockSchema, healthStatusSchema, imageBlockSchema, impersonationResponseSchema, impersonationSessionSchema, initiateVoiceCallSchema, invoiceLineItemSchema, invoiceListFiltersSchema, invoiceSchema, invoiceStatusSchema, isContentBlock, isFieldBlock, isLayoutFieldType, isPdfOverlayBlock, jurisdictionSchema, kanbanContactSchema, kanbanGroupSchema, kanbanStageSchema, lifecycleEventTypeSchema, limitIncreaseTypeSchema, listBlockSchema, listConversationsQuerySchema, maintenanceModeSchema, manualOptChangeRequestSchema, mapUltravoxStatus, markAsReadRequestSchema, messageDirectionSchema, messagePurposeSchema, messageSchema, messageStatusSchema, messageTypeSchema, moveContactSchema, mrrBreakdownSchema, multiSignerStatusSchema, ndfBlockSchema, ndfConditionalLogicSchema, ndfDocumentMetaSchema, ndfFieldValidationSchema, ndfFormBrandingSchema, ndfFormSettingsSchema, ndfKindSchema, ndfPdfSourceSchema, ndfSignerSchema, ndfToFormSchema, ndfToTemplateContent, ndfVariableDefinitionSchema, ndfVersionSchema, nexusDocumentSchema, normalizedPositionSchema, optInSourceSchema, optOutActionSchema, optOutChannelSchema, optOutEventSchema, optOutHistoryQuerySchema, optOutHistoryResponseSchema, optOutSourceSchema, overageTypeSchema, pageBreakBlockSchema, paginatedAccountListSchema, paginatedAddOnCatalogSchema, paginatedAiFlagsListSchema, paginatedAuditLogsSchema, paginatedCampaignsListSchema, paginatedDiscountsListSchema, paginatedEnrollmentsListSchema, paginatedFeatureFlagsListSchema, paginatedInvoicesListSchema, paginatedPlansListSchema, paginatedResponseSchema, paginatedSuperAdminUserListSchema, paginatedSupportRequestListSchema, paginatedTagsListSchema, paginatedTemplatesListSchema, paginationMetaSchema, paginationSchema, paragraphBlockSchema, pastDueAccountSchema, paymentDisputeSchema, paymentSummarySchema, pdfOverlayFieldBlockSchema, planAddOnAssignmentSchema, planDistributionSchema, planListFiltersSchema, planSchema, planStatusSchema, planWithStatsSchema, platformCampaignListFiltersSchema, platformCampaignSchema, platformCampaignStatusSchema, platformHealthCheckSchema, platformHealthStatusSchema, platformServiceSchema, preAiClassificationFiltersSchema, preAiClassificationSchema, preBundleIntegritySchema, preBundleMetadataSchema, preBundlePayloadSchema, preBundleSchema, preChannelSchema, preCheckDefinitionSchema, preCheckIdSchema, preConditionCodeSchema, preConditionOutputSchema, preConsentCaptureTraceEntrySchema, preContactFrequencySchema, preDecisionLogFiltersSchema, preDecisionLogSchema, preDecisionSchema, preInputSchema, preOutputSchema, preRecipientSchema, preRemediationCodeSchema, preRemediationOutputSchema, preRiskFlagOutputSchema, preRiskFlagSchema, preSummarySchema, preTraceEntrySchema, previewVoiceScriptSchema, prioritySchema, providerStatusSchema, publicDocumentSchema, publicFileUploadResponseSchema, publicFormConfigSchema, publicFormSchema, publicFormSubmissionSchema, publicSignDocumentSchema, queueStatsSchema, reactivateAccountDtoSchema, recipientScheduleOverrideSchema, recipientSelectionSchema, recipientStatusSchema, recipientWithContactSchema, recipientsListResponseSchema, registryTypeSchema, renderNdfToHtml, reorderCategoriesDtoSchema, reorderCustomFieldsSchema, replyComplianceSchema, reportRequestSchema, resetBlockIdCounter, resetPasswordDtoSchema, resolveSupportRequestDtoSchema, revenueMetricsSchema, revenueTrendDataPointSchema, reviewAiFlagDtoSchema, ruleEffectSchema, ruleLayerSchema, ruleRequiredCheckSchema, ruleSchema, ruleTypeSchema, scheduleOptionsSchema, sectionHeaderBlockSchema, selectFieldBlockSchema, sendIntentSchema, sendMessageSchema, sendReminderRequestSchema, sendReminderResponseSchema, sendReplyRequestSchema, sendReplyResponseSchema, sendTypeSchema, serviceHealthSchema, setupIntentSchema, setupProgressSchema, setupStepIdSchema, signDocumentSchema, signatureCompletionResponseSchema, signatureDataSchema, signatureFieldBlockSchema, signatureFieldPositionSchema, signatureInstanceSchema, signatureInstanceStatusSchema, signatureSchema, signatureStatusSchema, signatureWithRelationsSchema, signerContextSchema, signerMappingInstanceSchema, signerMappingSchema, sinchInboundSmsSchema, smsConfigSchema, smsProviderSchema, smsTemplateSchema, smsTemplateTypeSchema, spacerBlockSchema, startImpersonationDtoSchema, subdivisionTypeSchema, subscriptionInfoSchema, suggestReplyRequestSchema, suggestReplyResponseSchema, superAdminAuditActionSchema, superAdminAuditLogSchema, superAdminAuthUserSchema, superAdminEntityTypeSchema, superAdminLoginRequestSchema, superAdminLoginResponseSchema, superAdminPlanSchema, superAdminRefreshRequestSchema, superAdminRefreshResponseSchema, superAdminRoleSchema, superAdminUserListItemSchema, superAdminUserSchema, supportCategorySchema, supportListFiltersSchema, supportMessageSchema, supportPrioritySchema, supportQueueSummarySchema, supportRequestSchema, supportRequestStatusSchema, suspendAccountDtoSchema, tableBlockSchema, tagSchema, templateCategoryEntitySchema, templateCategorySchema, templateCategoryTypeSchema, templateContentSchema, templateContentToNdf, templateListFiltersSchema, templateSignerSchema, templateTypeSchema, templateVisibilitySchema, tenantDiscountSchema, tenantLifecycleEventSchema, tenantSchema, tenantSettingsSchema, tenantSourceSchema, tenantStatusSchema, tenantUsageSummarySchema, testSendEmailTemplateSchema, textFieldBlockSchema, textFieldSchema, textStyleSchema, timeFieldBlockSchema, timeSeriesPointSchema, timelineEventSchema, timelineEventTypeSchema, timelineResponseSchema, toggleFeatureFlagDtoSchema, toggleMaintenanceModeDtoSchema, tokenStatusSchema, tokenValidationResponseSchema, ultravoxStatusSchema, ultravoxWebhookPayloadSchema, unassignAddOnFromPlanDtoSchema, unifiedInboxChannelSchema, unifiedInboxContactSchema, unifiedInboxCountsSchema, unifiedInboxDetailResponseSchema, unifiedInboxFilterSchema, unifiedInboxFiltersSchema, unifiedInboxItemDetailSchema, unifiedInboxItemSchema, unifiedInboxItemTypeSchema, unifiedInboxListResponseSchema, unifiedInboxMessageSchema, unifiedInboxPaginationSchema, unifiedInboxQueryParamsSchema, unreadCountResponseSchema, unsubscribeInfoSchema, unsubscribeTokenSchema, updateAccountDtoSchema, updateAccountNoteSchema, updateAddOnCatalogDtoSchema, updateCallingHourWindowSchema, updateCampaignSchema, updateComplianceSettingsDtoSchema, updateContactCollectionFormSchema, updateContactGroupSchema, updateContactGroupStageSchema, updateContactSchema, updateContactWithConsentSchema, updateConversationRequestSchema, updateConversationResponseSchema, updateCustomFieldSchema, updateDiscountDtoSchema, updateDncRegistrySchema, updateDocumentSchema, updateEmailConfigDtoSchema, updateEmailTemplateSchema, updateFeatureFlagDtoSchema, updateFormSchema, updateFrequencyCapConfigSchema, updateJurisdictionSchema, updatePlanDtoSchema, updatePlatformCampaignDtoSchema, updatePreCheckDefinitionSchema, updateRuleSchema, updateSMSConfigDtoSchema, updateSetupProgressSchema, updateSmsTemplateSchema, updateSuperAdminUserSchema, updateSupportRequestDtoSchema, updateTemplateCategoryDtoSchema, updateTemplateDtoSchema, updateTemplateVisibilityDtoSchema, updateTenantSchema, updateUserSchema, updateVoiceScriptSchema, usageOverageSchema, usageStatusSchema, usageTypeSchema, userRoleSchema, userSchema, userSettingsSchema, userWithFullNameSchema, viewModeSchema, voiceCallResultSchema, voiceCallSchema, voiceCallStatusSchema, voiceCallTypeSchema, voiceConsentTypeSchema, voiceScriptCallTypeSchema, voiceScriptReferenceSchema, voiceScriptSchema, voiceScriptSettingsSchema, voiceScriptUsageStatsSchema, voiceScriptWithTagsSchema, webhookEventSchema, webhookEventTypeSchema, webhookSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map